/**
 * Executive Dashboard Component - v3.1 UPDATED
 * FIXES:
 * 1. Shift filter (ALL/DAY/NIGHT) now properly filters products
 * 2. Loose Bags shows product-wise breakdown
 * 3. Stock Aging in Combined View shows product-wise breakdown (expandable)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import './ExecutiveDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

// Helper functions
const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('en-IN');
};

// Format LBS - always round to whole number
const formatLbs = (num) => {
  if (num === null || num === undefined) return '0';
  return Math.round(Number(num)).toLocaleString('en-IN');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }).toUpperCase();
};

// Main Component
const ExecutiveDashboard = ({ user, showToast }) => {
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('exec_dashboard_tab') || 'production';
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [modal, setModal] = useState({ show: false, title: '', data: null });
  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    return localStorage.getItem('exec_dashboard_theme') === 'dark';
  });
  const refreshTimerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('exec_dashboard_tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('exec_dashboard_theme', isDarkTheme ? 'dark' : 'light');
    document.body.classList.remove('light-theme', 'dark-theme');
    document.body.classList.add(isDarkTheme ? 'dark-theme' : 'light-theme');
  }, [isDarkTheme]);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = `${API_BASE_URL}/coldstore_dashboard_api.php`;
      
      const response = await axios.get(
        apiUrl,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setData(response.data.data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError(response.data.error || 'Failed to load data');
      }
    } catch (err) {
      console.error('Error fetching executive dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    refreshTimerRef.current = setInterval(fetchData, 300000);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [fetchData]);

  const handleRefresh = () => {
    setLoading(true);
    fetchData();
    if (showToast) showToast('🔄 Dashboard refreshed', 'info', 2000);
  };

  const openModal = (title, modalData) => {
    setModal({ show: true, title, data: modalData });
  };

  const closeModal = () => {
    setModal({ show: false, title: '', data: null });
  };

  if (loading && !data) {
    return (
      <div className="exec-loading">
        <div className="exec-spinner"></div>
        <p>Loading Executive Dashboard...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="exec-error">
        <div className="exec-error-icon">⚠️</div>
        <h3>Access Denied</h3>
        <p>{error}</p>
        <button onClick={handleRefresh}>Retry</button>
      </div>
    );
  }

  const currentDate = new Date().toLocaleDateString('en-IN', { 
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
  });

  return (
    <div className={`exec-dashboard ${isDarkTheme ? 'dark-theme' : 'light-theme'}`}>
      <header className="exec-header">
        <div className="exec-header-left">
          <h1>EXECUTIVE DASHBOARD</h1>
          <p className="exec-subtitle">REAL-TIME OVERVIEW • CHOICE CANNING CO.</p>
        </div>
        <div className="exec-header-right">
          <div className="theme-toggle">
            <button 
              className={`theme-btn ${!isDarkTheme ? 'active' : ''}`}
              onClick={() => setIsDarkTheme(false)}
              title="Light Theme"
            >
              ☀️ LIGHT
            </button>
            <button 
              className={`theme-btn ${isDarkTheme ? 'active' : ''}`}
              onClick={() => setIsDarkTheme(true)}
              title="Dark Theme"
            >
              🌙 DARK
            </button>
          </div>
          <div className="exec-date-badge">📅 {currentDate.toUpperCase()}</div>
          {lastUpdated && (
            <div className="exec-time-badge">🕐 {lastUpdated.toLocaleTimeString('en-IN').toUpperCase()}</div>
          )}
          <button className="exec-refresh-btn" onClick={handleRefresh} disabled={loading}>
            🔄 REFRESH
          </button>
        </div>
      </header>

      <div className="exec-tabs">
        <button 
          className={`exec-tab ${activeTab === 'production' ? 'active' : ''}`} 
          onClick={() => setActiveTab('production')}
        >
          🏭 PRODUCTION SUMMARY
        </button>
        <button 
          className={`exec-tab ${activeTab === 'coldstore' ? 'active' : ''}`} 
          onClick={() => setActiveTab('coldstore')}
        >
          ❄️ COLD STORE SUMMARY
        </button>
        <button 
          className={`exec-tab ${activeTab === 'shipment' ? 'active' : ''}`} 
          onClick={() => setActiveTab('shipment')}
        >
          🚚 SHIPMENT ANALYSIS
        </button>
        <button 
          className={`exec-tab ${activeTab === 'combined' ? 'active' : ''}`} 
          onClick={() => setActiveTab('combined')}
        >
          📊 COMBINED VIEW
        </button>
      </div>

      <div className="exec-content">
        {activeTab === 'production' && <ProductionTab data={data} openModal={openModal} />}
        {activeTab === 'coldstore' && <ColdStoreTab data={data} />}
        {activeTab === 'shipment' && <ShipmentTab data={data} />}
        {activeTab === 'combined' && <CombinedTab data={data} />}
      </div>

      <footer className="exec-footer">
        Choice Canning Co. - Cold Store Management System | Version 3.1
      </footer>

      {modal.show && (
        <BrandModal 
          title={modal.title} 
          data={modal.data} 
          onClose={closeModal} 
        />
      )}
    </div>
  );
};

// ===================== BRAND MODAL =====================
const BrandModal = ({ title, data, onClose }) => {
  if (!data) return null;
  
  const totalCartons = data.brands?.reduce((sum, b) => sum + (b.cartons || 0), 0) || 0;
  const totalLbs = data.brands?.reduce((sum, b) => sum + (b.lbs || 0), 0) || 0;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="brand-modal">
        <div className="modal-header">
          <div>
            <h2>{data.code} - {data.name}</h2>
            <span>{data.status} - BRAND-WISE BREAKUP</span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-summary">
          <div className="modal-summary-item">
            <div className="modal-summary-label">TOTAL CARTONS</div>
            <div className="modal-summary-value">{formatNumber(totalCartons)}</div>
          </div>
          <div className="modal-summary-item">
            <div className="modal-summary-label">TOTAL LBS</div>
            <div className="modal-summary-value lbs">{formatLbs(totalLbs)}</div>
          </div>
          <div className="modal-summary-item">
            <div className="modal-summary-label">BRANDS</div>
            <div className="modal-summary-value">{data.brands?.length || 0}</div>
          </div>
        </div>

        <div className="modal-body">
          <table className="brand-table">
            <thead>
              <tr>
                <th>CODE</th>
                <th>BRAND NAME</th>
                <th>CARTONS</th>
                <th>LBS</th>
              </tr>
            </thead>
            <tbody>
              {data.brands?.map((brand, idx) => (
                <tr key={idx}>
                  <td className="code">{brand.code}</td>
                  <td>{brand.name}</td>
                  <td className="num">{formatNumber(brand.cartons)}</td>
                  <td className="num lbs">{formatLbs(brand.lbs)} LBS</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ===================== PRODUCT DETAIL MODAL =====================
const ProductDetailModal = ({ data, onClose }) => {
  const [activeTab, setActiveTab] = React.useState('brand_grade');
  
  if (!data) return null;
  
  const totalCartons = data.product?.cartons || 0;
  const totalLbs = data.product?.lbs || 0;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="product-detail-modal">
        <div className="modal-header purple-gradient">
          <div>
            <h2>{data.title}</h2>
            <span>BRAND / GRADE / PACK SIZE BREAKUP</span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-summary">
          <div className="modal-summary-item">
            <div className="modal-summary-label">TOTAL CARTONS</div>
            <div className="modal-summary-value">{formatNumber(totalCartons)}</div>
          </div>
          <div className="modal-summary-item">
            <div className="modal-summary-label">TOTAL LBS</div>
            <div className="modal-summary-value lbs">{formatLbs(totalLbs)}</div>
          </div>
          <div className="modal-summary-item">
            <div className="modal-summary-label">BRANDS</div>
            <div className="modal-summary-value">{data.brands?.length || 0}</div>
          </div>
          <div className="modal-summary-item">
            <div className="modal-summary-label">GRADES</div>
            <div className="modal-summary-value">{data.grades?.length || data.pack_sizes?.length || 0}</div>
          </div>
        </div>

        <div className="modal-tabs">
          <button className={`modal-tab ${activeTab === 'brand_grade' ? 'active' : ''}`} onClick={() => setActiveTab('brand_grade')}>
            🏷️ BRAND → GRADE
          </button>
          <button className={`modal-tab ${activeTab === 'grade_packsize' ? 'active' : ''}`} onClick={() => setActiveTab('grade_packsize')}>
            📦 GRADE → PACK SIZE
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'brand_grade' && (
            <table className="detail-table hierarchical-table">
              <thead>
                <tr>
                  <th>BRAND / GRADE</th>
                  <th style={{textAlign:'right'}}>CARTONS</th>
                  <th style={{textAlign:'right'}}>LBS</th>
                </tr>
              </thead>
              <tbody>
                {data.brands?.length > 0 ? data.brands.map((brand, bIdx) => (
                  <React.Fragment key={bIdx}>
                    <tr className="brand-header-row">
                      <td className="brand-name">{brand.name}</td>
                      <td className="num brand-total">{formatNumber(brand.cartons)}</td>
                      <td className="num lbs brand-total">{formatLbs(brand.lbs)}</td>
                    </tr>
                    {brand.grades && brand.grades.length > 0 ? (
                      brand.grades.map((grade, gIdx) => (
                        <tr key={`${bIdx}-${gIdx}`} className="pack-size-row">
                          <td className="pack-size-indent">↳ {grade.name}</td>
                          <td className="num">{formatNumber(grade.cartons)}</td>
                          <td className="num lbs">{formatLbs(grade.lbs)}</td>
                        </tr>
                      ))
                    ) : brand.pack_sizes && brand.pack_sizes.length > 0 ? (
                      brand.pack_sizes.map((ps, psIdx) => (
                        <tr key={`${bIdx}-${psIdx}`} className="pack-size-row">
                          <td className="pack-size-indent">↳ {ps.name}</td>
                          <td className="num">{formatNumber(ps.cartons)}</td>
                          <td className="num lbs">{formatLbs(ps.lbs)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="pack-size-row">
                        <td className="pack-size-indent no-data-small">↳ No grade breakdown</td>
                        <td></td>
                        <td></td>
                      </tr>
                    )}
                  </React.Fragment>
                )) : (
                  <tr><td colSpan="3" className="no-data">No brand data available</td></tr>
                )}
              </tbody>
            </table>
          )}
          {activeTab === 'grade_packsize' && (
            <table className="detail-table hierarchical-table">
              <thead>
                <tr>
                  <th>GRADE / PACK SIZE</th>
                  <th style={{textAlign:'right'}}>CARTONS</th>
                  <th style={{textAlign:'right'}}>LBS</th>
                </tr>
              </thead>
              <tbody>
                {data.grades?.length > 0 ? data.grades.map((grade, gIdx) => (
                  <React.Fragment key={gIdx}>
                    <tr className="brand-header-row">
                      <td className="brand-name">{grade.name}</td>
                      <td className="num brand-total">{formatNumber(grade.cartons)}</td>
                      <td className="num lbs brand-total">{formatLbs(grade.lbs)}</td>
                    </tr>
                    {grade.pack_sizes && grade.pack_sizes.length > 0 ? (
                      grade.pack_sizes.map((ps, psIdx) => (
                        <tr key={`${gIdx}-${psIdx}`} className="pack-size-row">
                          <td className="pack-size-indent">↳ {ps.name}</td>
                          <td className="num">{formatNumber(ps.cartons)}</td>
                          <td className="num lbs">{formatLbs(ps.lbs)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="pack-size-row">
                        <td className="pack-size-indent no-data-small">↳ No pack size breakdown</td>
                        <td></td>
                        <td></td>
                      </tr>
                    )}
                  </React.Fragment>
                )) : data.pack_sizes?.length > 0 ? data.pack_sizes.map((p, idx) => (
                  <tr key={idx}>
                    <td>{p.name}</td>
                    <td className="num">{formatNumber(p.cartons)}</td>
                    <td className="num lbs">{formatLbs(p.lbs)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="3" className="no-data">No grade/pack size data available</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ===================== SUMMARY POPUP MODAL - For PRODUCED/RE-PACK/LOOSE BAGS clicks =====================
const SummaryPopupModal = ({ data, onClose }) => {
  if (!data) return null;
  
  const { title, type, summary, processData, dateLabel } = data;
  
  // Get products, brands, grades for the process
  const products = processData?.products || [];
  const brands = processData?.brands || [];
  const grades = processData?.grades || [];
  
  const totalCartons = type === 'loose_bags' 
    ? (summary?.total || 0) 
    : (processData?.total || summary?.total || 0);
  const totalLbs = processData?.lbs || summary?.lbs || 0;
  
  // Count unique grades across all brands
  const uniqueGrades = [...new Set(grades.map(g => g.name))];

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="excel-popup-modal">
        <div className={`modal-header ${type === 'produced' ? 'green-gradient' : type === 'repack' ? 'blue-gradient' : 'orange-gradient'}`}>
          <div>
            <h2>{title} - {dateLabel}</h2>
            <span>PRODUCT / BRAND / GRADE / PACK SIZE BREAKUP</span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        {/* Summary Header Row - Like Excel */}
        <div className="excel-summary-header">
          <div className="excel-summary-cell title">
            <span className="label">Product {products.length}</span>
          </div>
          <div className="excel-summary-cell highlight">
            <span className="label">Total CARTONS</span>
            <span className="value">{formatNumber(totalCartons)}</span>
          </div>
          <div className="excel-summary-cell">
            <span className="label">LBS</span>
            <span className="value lbs">{formatLbs(totalLbs)} LBS</span>
          </div>
        </div>

        <div className="modal-body excel-popup-body">
          <div className="excel-columns-container">
            {/* Left Section: Products Table */}
            <div className="excel-section products-section">
              <table className="excel-table">
                <thead>
                  <tr className="excel-header-row">
                    <th>PRODUCT</th>
                    <th className="num">CTNS</th>
                    <th className="num">LBS</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, idx) => (
                    <tr key={idx} className="excel-data-row">
                      <td>{p.name}</td>
                      <td className="num">{formatNumber(p.cartons)}</td>
                      <td className="num lbs">{formatLbs(p.lbs)}</td>
                    </tr>
                  ))}
                  <tr className="excel-total-row">
                    <td><strong>TOTAL</strong></td>
                    <td className="num"><strong>{formatNumber(totalCartons)}</strong></td>
                    <td className="num lbs"><strong>{formatLbs(totalLbs)}</strong></td>
                  </tr>
                </tbody>
              </table>
              
              {/* Brand Section Below Products */}
              {type !== 'loose_bags' && brands.length > 0 && (
                <table className="excel-table brand-table">
                  <thead>
                    <tr className="excel-header-row brand-header">
                      <th>BRAND</th>
                      <th className="num">GRADE (No of Grades)</th>
                      <th className="num">Total Weight</th>
                    </tr>
                    <tr className="excel-subheader-row">
                      <td><em>Brand</em></td>
                      <td className="num"><em>GRADE / PACK SIZE</em></td>
                      <td className="num"><em>LBS</em></td>
                    </tr>
                  </thead>
                  <tbody>
                    {brands.map((b, idx) => (
                      <React.Fragment key={idx}>
                        <tr className="excel-brand-row">
                          <td><strong>{b.name}</strong></td>
                          <td className="num">{b.grades?.length || 0}</td>
                          <td className="num lbs">{formatLbs(b.lbs)}</td>
                        </tr>
                        {b.grades && b.grades.map((g, gIdx) => (
                          <tr key={gIdx} className="excel-grade-sub-row">
                            <td className="sub-item">↳ {g.name}</td>
                            <td className="num">{formatNumber(g.cartons)}</td>
                            <td className="num lbs">{formatLbs(g.lbs)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
              
              {/* Grade Summary Table */}
              {type !== 'loose_bags' && grades.length > 0 && (
                <table className="excel-table grade-summary-table">
                  <thead>
                    <tr className="excel-header-row">
                      <th>GRADE</th>
                      <th className="num">CARTONS</th>
                      <th className="num">LBS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grades.map((g, idx) => (
                      <tr key={idx} className="excel-data-row">
                        <td>{g.name}</td>
                        <td className="num">{formatNumber(g.cartons)}</td>
                        <td className="num lbs">{formatLbs(g.lbs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Right Section: Grade / Pack Size Table */}
            {type !== 'loose_bags' && (
              <div className="excel-section grade-packsize-section">
                <table className="excel-table">
                  <thead>
                    <tr className="excel-header-row">
                      <th>GRADE / PACK SIZE</th>
                      <th className="num">CARTONS</th>
                      <th className="num">LBS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grades.map((g, gIdx) => (
                      <React.Fragment key={gIdx}>
                        <tr className="excel-grade-row">
                          <td><strong>{g.name}</strong></td>
                          <td className="num"><strong>{formatNumber(g.cartons)}</strong></td>
                          <td className="num lbs"><strong>{formatLbs(g.lbs)}</strong></td>
                        </tr>
                        {g.pack_sizes && g.pack_sizes.length > 0 ? (
                          g.pack_sizes.map((ps, psIdx) => (
                            <tr key={psIdx} className="excel-packsize-row">
                              <td className="sub-item">↳ {ps.name}</td>
                              <td className="num">{formatNumber(ps.cartons)}</td>
                              <td className="num lbs">{formatLbs(ps.lbs)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr className="excel-packsize-row no-data">
                            <td className="sub-item" colSpan="3">↳ No pack size breakdown</td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== EXPORT TO EXCEL UTILITY - CSV Format for Reliability =====================
const exportToExcel = (data, filename) => {
  // Use CSV format for maximum compatibility
  const escapeCSV = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };
  
  const formatNum = (num) => {
    if (num === null || num === undefined) return '0';
    return parseFloat(num).toLocaleString('en-US', { maximumFractionDigits: 2 });
  };
  
  const createCSVContent = () => {
    let csv = '';
    
    if (data.type === 'production') {
      const { today, yesterday, dates, today_shipment, yesterday_shipment, weekly_trend, current_month, previous_month, ytd } = data;
      
      // Helper to build section for a day
      const buildDaySection = (dayData, shipmentData, dayLabel, dateStr) => {
        let section = '';
        const summary = dayData?.summary || {};
        const produced = summary?.produced || {};
        const repack = summary?.repack || {};
        const reprint = summary?.reprint || {};
        const reprocess = summary?.reprocess || {};
        
        section += `\n${dayLabel} PRODUCTION REPORT\n`;
        section += `Date: ${dateStr}\n\n`;
        
        // Summary with CTNS and LBS
        section += 'PROCESS,TOTAL CTNS,TOTAL LBS,DAY CTNS,DAY LBS,NIGHT CTNS,NIGHT LBS\n';
        section += `PRODUCED,${produced.total || 0},${produced.lbs || 0},${produced.day?.cartons || 0},${produced.day?.lbs || 0},${produced.night?.cartons || 0},${produced.night?.lbs || 0}\n`;
        
        const repackTotal = (repack.total || 0) + (reprint.total || 0) + (reprocess.total || 0);
        const repackLbs = (repack.lbs || 0) + (reprint.lbs || 0) + (reprocess.lbs || 0);
        const repackDayCtns = (repack.day?.cartons || 0) + (reprint.day?.cartons || 0) + (reprocess.day?.cartons || 0);
        const repackDayLbs = (repack.day?.lbs || 0) + (reprint.day?.lbs || 0) + (reprocess.day?.lbs || 0);
        const repackNightCtns = (repack.night?.cartons || 0) + (reprint.night?.cartons || 0) + (reprocess.night?.cartons || 0);
        const repackNightLbs = (repack.night?.lbs || 0) + (reprint.night?.lbs || 0) + (reprocess.night?.lbs || 0);
        
        section += `RE-PACK,${repackTotal},${repackLbs},${repackDayCtns},${repackDayLbs},${repackNightCtns},${repackNightLbs}\n`;
        section += `LOOSE BAGS,${summary?.total_loose_bags || 0},,${summary?.day_loose_bags || 0},,${summary?.night_loose_bags || 0},\n`;
        
        // Shipped data
        if (shipmentData) {
          section += `SHIPPED,${shipmentData.total_cartons || 0},${shipmentData.total_lbs || 0},${shipmentData.day?.cartons || 0},${shipmentData.day?.lbs || 0},${shipmentData.night?.cartons || 0},${shipmentData.night?.lbs || 0}\n`;
        }
        section += '\n';
        
        // Product Breakdown
        section += 'PRODUCT BREAKUP\n';
        section += 'PRODUCT,CTNS,LBS\n';
        const products = produced.products || [];
        let prodTotal = 0, prodLbsTotal = 0;
        products.forEach(p => {
          section += `${escapeCSV(p.name)},${p.cartons},${p.lbs}\n`;
          prodTotal += p.cartons;
          prodLbsTotal += p.lbs;
        });
        section += `TOTAL,${prodTotal},${prodLbsTotal}\n\n`;
        
        // Grade Breakdown with Pack Sizes
        section += 'GRADE BREAKUP\n';
        section += 'GRADE,CTNS,LBS\n';
        const grades = produced.grades || [];
        grades.forEach(g => {
          section += `${escapeCSV(g.name)},${g.cartons},${g.lbs}\n`;
          if (g.pack_sizes && g.pack_sizes.length > 0) {
            g.pack_sizes.forEach(ps => {
              section += `  ↳ ${escapeCSV(ps.name)},${ps.cartons},${ps.lbs}\n`;
            });
          }
        });
        section += '\n';
        
        // Brand Breakdown
        section += 'BRAND BREAKUP\n';
        section += 'BRAND,CTNS,LBS\n';
        const brands = produced.brands || [];
        brands.forEach(b => {
          section += `${escapeCSV(b.name)},${b.cartons},${b.lbs}\n`;
        });
        
        return section;
      };
      
      // Add header
      csv += 'PRODUCTION SUMMARY EXPORT\n';
      csv += `Generated: ${new Date().toLocaleString()}\n`;
      csv += '='.repeat(60) + '\n';
      
      // Today section
      csv += buildDaySection(today, today_shipment, 'TODAY', dates?.today || new Date().toISOString().split('T')[0]);
      
      csv += '\n' + '='.repeat(60) + '\n';
      
      // Yesterday section
      csv += buildDaySection(yesterday, yesterday_shipment, 'YESTERDAY', dates?.yesterday || '');
      
      // Weekly Trend
      if (weekly_trend && weekly_trend.length > 0) {
        csv += '\n' + '='.repeat(60) + '\n';
        csv += '\nWEEKLY PRODUCTION TREND\n';
        csv += 'DATE,DAY,PRODUCED CTNS,PRODUCED LBS,SHIPPED CTNS,SHIPPED LBS,DAY SHIFT,NIGHT SHIFT\n';
        weekly_trend.forEach(d => {
          csv += `${d.date},${d.day},${d.inward || 0},${d.inward_lbs || 0},${d.shipped || 0},${d.shipped_lbs || 0},${d.shift?.day?.cartons || 0},${d.shift?.night?.cartons || 0}\n`;
        });
        
        // Weekly Product Summary
        csv += '\nWEEKLY TOP PRODUCTS\n';
        csv += 'DATE,PRODUCT,CTNS,LBS\n';
        weekly_trend.forEach(d => {
          if (d.products && d.products.length > 0) {
            d.products.forEach(p => {
              csv += `${d.date},${escapeCSV(p.name)},${p.cartons},${p.lbs}\n`;
            });
          }
        });
      }
      
      // Monthly Comparison
      if (current_month) {
        csv += '\n' + '='.repeat(60) + '\n';
        csv += '\nMONTHLY COMPARISON\n\n';
        
        csv += `CURRENT MONTH: ${current_month.month_name} ${current_month.year}\n`;
        csv += 'METRIC,CTNS,LBS\n';
        csv += `Total Production,${current_month.total_cartons || 0},${current_month.total_lbs || 0}\n`;
        csv += `Produced,${current_month.produced?.cartons || 0},${current_month.produced?.lbs || 0}\n`;
        csv += `Re-Pack,${current_month.repack?.cartons || 0},${current_month.repack?.lbs || 0}\n`;
        csv += `Shipped,${current_month.shipped?.cartons || 0},${current_month.shipped?.lbs || 0}\n`;
        csv += `Working Days,${current_month.working_days || 0},\n\n`;
        
        csv += `TOP PRODUCTS - ${current_month.month_name}\n`;
        csv += 'RANK,PRODUCT,CTNS,LBS\n';
        (current_month.products || []).forEach((p, i) => {
          csv += `${i+1},${escapeCSV(p.name)},${p.cartons},${p.lbs}\n`;
        });
        
        csv += `\nTOP GRADES - ${current_month.month_name}\n`;
        csv += 'RANK,GRADE,CTNS,LBS\n';
        (current_month.grades || []).forEach((g, i) => {
          csv += `${i+1},${escapeCSV(g.name)},${g.cartons},${g.lbs}\n`;
        });
        
        if (previous_month) {
          csv += `\n\nPREVIOUS MONTH: ${previous_month.month_name} ${previous_month.year}\n`;
          csv += 'METRIC,CTNS,LBS\n';
          csv += `Total Production,${previous_month.total_cartons || 0},${previous_month.total_lbs || 0}\n`;
          csv += `Produced,${previous_month.produced?.cartons || 0},${previous_month.produced?.lbs || 0}\n`;
          csv += `Re-Pack,${previous_month.repack?.cartons || 0},${previous_month.repack?.lbs || 0}\n`;
          csv += `Shipped,${previous_month.shipped?.cartons || 0},${previous_month.shipped?.lbs || 0}\n`;
          csv += `Working Days,${previous_month.working_days || 0},\n`;
        }
      }
      
      // YTD Summary
      if (ytd) {
        csv += '\n' + '='.repeat(60) + '\n';
        csv += `\nYEAR TO DATE (${ytd.year})\n\n`;
        
        csv += 'YTD SUMMARY\n';
        csv += 'METRIC,CTNS,LBS\n';
        csv += `Total Production,${ytd.total_cartons || 0},${ytd.total_lbs || 0}\n`;
        csv += `Produced,${ytd.produced?.cartons || 0},${ytd.produced?.lbs || 0}\n`;
        csv += `Re-Pack,${ytd.repack?.cartons || 0},${ytd.repack?.lbs || 0}\n`;
        csv += `Shipped,${ytd.shipped?.cartons || 0},${ytd.shipped?.lbs || 0}\n\n`;
        
        csv += 'YTD MONTHLY BREAKUP\n';
        csv += 'MONTH,PRODUCED CTNS,RE-PACK CTNS,TOTAL CTNS,LBS,SHIPPED,WORKING DAYS\n';
        (ytd.monthly_breakdown || []).forEach(m => {
          csv += `${m.month},${m.produced || 0},${m.repack || 0},${m.cartons || 0},${m.lbs || 0},${m.shipped || 0},${m.working_days || 0}\n`;
        });
        
        csv += '\nYTD TOP PRODUCTS\n';
        csv += 'RANK,PRODUCT,CTNS,LBS\n';
        (ytd.products || []).forEach((p, i) => {
          csv += `${i+1},${escapeCSV(p.name)},${p.cartons},${p.lbs}\n`;
        });
        
        csv += '\nYTD TOP GRADES\n';
        csv += 'RANK,GRADE,CTNS,LBS\n';
        (ytd.grades || []).forEach((g, i) => {
          csv += `${i+1},${escapeCSV(g.name)},${g.cartons},${g.lbs}\n`;
        });
      }
      
    } else if (data.type === 'coldstore') {
      const { cs1, cs2, current_stock, today_inward, yesterday_inward } = data;
      
      csv += '═'.repeat(70) + '\n';
      csv += 'COLD STORE INVENTORY REPORT\n';
      csv += '═'.repeat(70) + '\n';
      csv += `Generated: ${new Date().toLocaleString()}\n\n`;
      
      // Section 1: Cold Store Location Summary
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 1: COLD STORE LOCATION SUMMARY\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'LOCATION,CARTONS,LBS,BINS USED\n';
      csv += `Cold Store 1 (CS1),${cs1?.total_cartons || 0},${cs1?.total_lbs || 0},-\n`;
      csv += `Cold Store 2 (CS2),${cs2?.total_cartons || 0},${cs2?.total_lbs || 0},-\n`;
      csv += `TOTAL,${(cs1?.total_cartons || 0) + (cs2?.total_cartons || 0)},${(cs1?.total_lbs || 0) + (cs2?.total_lbs || 0)},-\n\n`;
      
      // Section 2: Current Stock Summary
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 2: CURRENT STOCK SUMMARY\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'METRIC,CARTONS,LBS\n';
      csv += `Total Stock,${current_stock?.cartons || 0},${current_stock?.lbs || 0}\n`;
      csv += `Today Inward,${today_inward?.total || 0},${today_inward?.lbs || 0}\n`;
      csv += `Yesterday Inward,${yesterday_inward?.total || 0},${yesterday_inward?.lbs || 0}\n\n`;
      
      // Section 3: Product Distribution
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 3: PRODUCT DISTRIBUTION\n';
      csv += '═'.repeat(50) + '\n\n';
      
      const totalCartons = current_stock?.cartons || 0;
      csv += 'RANK,PRODUCT,CARTONS,LBS,%\n';
      (current_stock?.products || []).forEach((p, i) => {
        const pct = totalCartons > 0 ? ((p.cartons / totalCartons) * 100).toFixed(1) : 0;
        csv += `${i+1},${escapeCSV(p.name)},${p.cartons},${p.lbs},${pct}%\n`;
      });
      csv += `TOTAL,-,${totalCartons},${current_stock?.lbs || 0},100%\n\n`;
      
      // Section 4: Grade Distribution
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 4: GRADE DISTRIBUTION\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'RANK,GRADE,CARTONS,LBS,%\n';
      (current_stock?.grades || []).forEach((g, i) => {
        const pct = totalCartons > 0 ? ((g.cartons / totalCartons) * 100).toFixed(1) : 0;
        csv += `${i+1},${escapeCSV(g.name)},${g.cartons},${g.lbs},${pct}%\n`;
      });
      csv += `TOTAL,-,${totalCartons},${current_stock?.lbs || 0},100%\n\n`;
      
      // Section 5: Brand Distribution
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 5: BRAND DISTRIBUTION\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'RANK,BRAND,CARTONS,LBS,%\n';
      (current_stock?.brands || []).forEach((b, i) => {
        const pct = totalCartons > 0 ? ((b.cartons / totalCartons) * 100).toFixed(1) : 0;
        csv += `${i+1},${escapeCSV(b.name)},${b.cartons},${b.lbs},${pct}%\n`;
      });
      csv += `TOTAL,-,${totalCartons},${current_stock?.lbs || 0},100%\n\n`;
      
      // Section 6: Pack Size Distribution
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 6: PACK SIZE DISTRIBUTION\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'RANK,PACK SIZE,CARTONS,LBS,%\n';
      (current_stock?.pack_sizes || []).forEach((ps, i) => {
        const pct = totalCartons > 0 ? ((ps.cartons / totalCartons) * 100).toFixed(1) : 0;
        csv += `${i+1},${escapeCSV(ps.name)},${ps.cartons},${ps.lbs},${pct}%\n`;
      });
      csv += `TOTAL,-,${totalCartons},${current_stock?.lbs || 0},100%\n`;
      
    } else if (data.type === 'combined') {
      const { combined, current_stock, stock_aging } = data;
      
      csv += '═'.repeat(70) + '\n';
      csv += 'COMBINED EXECUTIVE SUMMARY REPORT\n';
      csv += '═'.repeat(70) + '\n';
      csv += `Generated: ${new Date().toLocaleString()}\n\n`;
      
      // Sheet 1: Executive Summary
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 1: EXECUTIVE SUMMARY\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'KEY METRICS,CARTONS,LBS\n';
      csv += `Current Stock,${combined?.total_cartons || 0},${combined?.total_lbs || 0}\n`;
      csv += `Today Production,${combined?.today_production || 0},-\n`;
      csv += `Today Inward,${combined?.today_inward || 0},-\n`;
      csv += `Today Shipped,${combined?.today_shipped || 0},-\n`;
      csv += `MTD Shipped,${combined?.mtd_shipped || 0},-\n\n`;
      
      // Sheet 2: Product Distribution
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 2: PRODUCT DISTRIBUTION\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'RANK,PRODUCT,CARTONS,LBS,%\n';
      const totalCartons = current_stock?.cartons || 0;
      (current_stock?.products || []).forEach((p, i) => {
        const pct = totalCartons > 0 ? ((p.cartons / totalCartons) * 100).toFixed(1) : 0;
        csv += `${i+1},${escapeCSV(p.name)},${p.cartons},${p.lbs},${pct}%\n`;
      });
      csv += `TOTAL,-,${totalCartons},${current_stock?.lbs || 0},100%\n\n`;
      
      // Sheet 3: Grade Distribution
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 3: GRADE DISTRIBUTION\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'RANK,GRADE,CARTONS,LBS,%\n';
      (current_stock?.grades || []).forEach((g, i) => {
        const pct = totalCartons > 0 ? ((g.cartons / totalCartons) * 100).toFixed(1) : 0;
        csv += `${i+1},${escapeCSV(g.name)},${g.cartons},${g.lbs},${pct}%\n`;
      });
      csv += `TOTAL,-,${totalCartons},${current_stock?.lbs || 0},100%\n\n`;
      
      // Sheet 4: Brand Distribution
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 4: BRAND DISTRIBUTION\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'RANK,BRAND,CARTONS,LBS,%\n';
      (current_stock?.brands || []).forEach((b, i) => {
        const pct = totalCartons > 0 ? ((b.cartons / totalCartons) * 100).toFixed(1) : 0;
        csv += `${i+1},${escapeCSV(b.name)},${b.cartons},${b.lbs},${pct}%\n`;
      });
      csv += `TOTAL,-,${totalCartons},${current_stock?.lbs || 0},100%\n\n`;
      
      // Sheet 5: Pack Size Distribution
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 5: PACK SIZE DISTRIBUTION\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'RANK,PACK SIZE,CARTONS,LBS,%\n';
      (current_stock?.pack_sizes || []).forEach((ps, i) => {
        const pct = totalCartons > 0 ? ((ps.cartons / totalCartons) * 100).toFixed(1) : 0;
        csv += `${i+1},${escapeCSV(ps.name)},${ps.cartons},${ps.lbs},${pct}%\n`;
      });
      csv += `TOTAL,-,${totalCartons},${current_stock?.lbs || 0},100%\n\n`;
      
      // Sheet 6: Stock Aging with Product Breakdown
      csv += '═'.repeat(50) + '\n';
      csv += 'SECTION 6: STOCK AGING ANALYSIS\n';
      csv += '═'.repeat(50) + '\n\n';
      
      csv += 'AGE GROUP,CARTONS,LBS,%\n';
      const agingTotal = (stock_aging || []).reduce((sum, a) => sum + a.cartons, 0);
      (stock_aging || []).forEach(a => {
        const pct = agingTotal > 0 ? ((a.cartons / agingTotal) * 100).toFixed(1) : 0;
        csv += `${a.age_group},${a.cartons},${a.lbs},${pct}%\n`;
      });
      csv += `TOTAL,${agingTotal},${(stock_aging || []).reduce((sum, a) => sum + a.lbs, 0)},100%\n\n`;
      
      // Detailed Aging by Product
      csv += 'STOCK AGING - PRODUCT BREAKUP\n';
      csv += 'AGE GROUP,PRODUCT,GRADE,CARTONS,LBS\n';
      (stock_aging || []).forEach(ageGroup => {
        (ageGroup.products || []).forEach(prod => {
          csv += `${ageGroup.age_group},${escapeCSV(prod.name)},-,${prod.cartons},${prod.lbs}\n`;
          (prod.grades || []).forEach(grade => {
            csv += `${ageGroup.age_group},${escapeCSV(prod.name)},${escapeCSV(grade.name)},${grade.cartons},${grade.lbs}\n`;
          });
        });
      });
    }
    
    return csv;
  };
  
  // Generate and download
  const csv = createCSVContent();
  const BOM = '\uFEFF'; // UTF-8 BOM for Excel
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};
// ===================== EXPORT BUTTON COMPONENT =====================
const ExportButton = ({ onClick, label = 'Export' }) => (
  <button className="export-btn excel-export" onClick={onClick} title="Export to Excel">
    <svg className="excel-icon" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12.9,14.5L15.8,19H14L12,15.6L10,19H8.2L11.1,14.5L8.2,10H10L12,13.4L14,10H15.8L12.9,14.5Z"/>
    </svg>
    <span>{label}</span>
  </button>
);

// ===================== PRODUCTION TAB - UPDATED with Shift Filter Fix =====================
const ProductionTab = ({ data, openModal }) => {
  const [activeTab, setActiveTab] = React.useState('today');
  const [shiftFilter, setShiftFilter] = React.useState('all');
  const [productModal, setProductModal] = React.useState(null);
  const [summaryModal, setSummaryModal] = React.useState(null);
  const [showMoreBreakdowns, setShowMoreBreakdowns] = React.useState(false);
  
  if (!data) return null;
  const { today, yesterday, dates, today_shipment, yesterday_shipment } = data;
  
  // API returns data under 'summary' property: today.summary.produced, today.summary.total_cartons, etc.
  const currentData = activeTab === 'today' ? today?.summary : yesterday?.summary;
  const currentFullHierarchy = activeTab === 'today' ? today?.full_hierarchy : yesterday?.full_hierarchy;
  const currentShipment = activeTab === 'today' ? today_shipment : yesterday_shipment;
  const currentLabel = activeTab === 'today' ? 'TODAY' : 'YESTERDAY';

  // FIXED: Helper function to get shift-filtered data for a process
  const getShiftFilteredProcessData = (processData) => {
    if (!processData) return processData;
    if (shiftFilter === 'all') {
      return {
        ...processData,
        total: processData.total || 0,
        lbs: processData.lbs || 0,
        products: processData.products || [],
        pack_sizes: processData.pack_sizes || [],
        brands: processData.brands || []
      };
    }
    // Use shift-specific data from API
    const shiftData = processData[shiftFilter];
    return {
      ...processData,
      total: shiftData?.cartons || 0,
      lbs: shiftData?.lbs || 0,
      products: shiftData?.products || [],
      pack_sizes: shiftData?.pack_sizes || [],
      brands: shiftData?.brands || []
    };
  };

  // FIXED: Get combined RE-PACK data with shift filter
  const getCombinedRepackData = () => {
    const processes = ['repack', 'reprint', 'reprocess'];
    let combinedData = {
      total: 0,
      lbs: 0,
      products: [],
      pack_sizes: [],
      brands: []
    };

    processes.forEach(processKey => {
      const processData = currentData?.[processKey];
      if (!processData) return;

      if (shiftFilter === 'all') {
        combinedData.total += processData.total || 0;
        combinedData.lbs += processData.lbs || 0;
        
        (processData.products || []).forEach(prod => {
          const existing = combinedData.products.find(p => p.name === prod.name);
          if (existing) {
            existing.cartons += prod.cartons;
            existing.lbs += prod.lbs;
          } else {
            combinedData.products.push({ ...prod });
          }
        });
        
        (processData.pack_sizes || []).forEach(ps => {
          const existing = combinedData.pack_sizes.find(p => p.name === ps.name);
          if (existing) {
            existing.cartons += ps.cartons;
            existing.lbs += ps.lbs;
          } else {
            combinedData.pack_sizes.push({ ...ps });
          }
        });
        
        (processData.brands || []).forEach(br => {
          const existing = combinedData.brands.find(b => b.name === br.name);
          if (existing) {
            existing.cartons += br.cartons;
            existing.lbs += br.lbs;
          } else {
            combinedData.brands.push({ ...br });
          }
        });
      } else {
        const shiftData = processData[shiftFilter];
        if (shiftData) {
          combinedData.total += shiftData.cartons || 0;
          combinedData.lbs += shiftData.lbs || 0;
          
          (shiftData.products || []).forEach(prod => {
            const existing = combinedData.products.find(p => p.name === prod.name);
            if (existing) {
              existing.cartons += prod.cartons;
              existing.lbs += prod.lbs;
            } else {
              combinedData.products.push({ ...prod });
            }
          });
          
          (shiftData.pack_sizes || []).forEach(ps => {
            const existing = combinedData.pack_sizes.find(p => p.name === ps.name);
            if (existing) {
              existing.cartons += ps.cartons;
              existing.lbs += ps.lbs;
            } else {
              combinedData.pack_sizes.push({ ...ps });
            }
          });
          
          (shiftData.brands || []).forEach(br => {
            const existing = combinedData.brands.find(b => b.name === br.name);
            if (existing) {
              existing.cartons += br.cartons;
              existing.lbs += br.lbs;
            } else {
              combinedData.brands.push({ ...br });
            }
          });
        }
      }
    });

    combinedData.products.sort((a, b) => b.cartons - a.cartons);
    combinedData.pack_sizes.sort((a, b) => b.cartons - a.cartons);
    combinedData.brands.sort((a, b) => b.cartons - a.cartons);

    return combinedData;
  };

  // FIXED: Get loose bags data based on shift filter
  const getLooseBagsData = () => {
    const looseBagsBreakdown = currentData?.loose_bags_breakdown;
    if (!looseBagsBreakdown) {
      return {
        total: currentData?.total_loose_bags || 0,
        products: []
      };
    }

    if (shiftFilter === 'all') {
      return {
        total: looseBagsBreakdown.total || 0,
        products: looseBagsBreakdown.products || []
      };
    } else {
      const shiftData = looseBagsBreakdown[shiftFilter];
      return {
        total: shiftData?.total || 0,
        products: shiftData?.products || []
      };
    }
  };

  const handleProductClick = (processTitle, product, processData) => {
    // Get brands data specific to this product (not the entire process)
    // The product object now contains its own brands with grades
    const productBrands = product?.brands || [];
    const productGrades = product?.grades || [];
    const productPackSizes = processData?.pack_sizes || [];
    
    // Use the product's own brands with grades
    const brandsWithGrades = productBrands.map(b => ({
      ...b,
      grades: b.grades || []
    }));
    
    // Build grades with pack_sizes (use product's grades - preserve existing pack_sizes)
    const gradesWithPackSizes = productGrades.map(g => ({
      ...g,
      pack_sizes: g.pack_sizes || []
    }));
    
    setProductModal({
      title: `${product.name} - ${processTitle}`,
      product: product,
      brands: brandsWithGrades,
      grades: gradesWithPackSizes,
      pack_sizes: productPackSizes
    });
  };

  return (
    <div className="production-tab">
      {productModal && (
        <ProductDetailModal 
          data={productModal} 
          onClose={() => setProductModal(null)} 
        />
      )}
      
      {summaryModal && (
        <SummaryPopupModal 
          data={summaryModal} 
          onClose={() => setSummaryModal(null)} 
        />
      )}

      <div className="section-title">
        <div className="section-icon purple">📊</div>
        <span>SHIFT-WISE SUMMARY</span>
        <ExportButton 
          onClick={() => exportToExcel({ 
            type: 'production', 
            today, 
            yesterday, 
            dates,
            today_shipment,
            yesterday_shipment,
            weekly_trend: data.weekly_trend,
            current_month: data.current_month,
            previous_month: data.previous_month,
            ytd: data.ytd
          }, 'Production_Summary')}
          label="Export"
        />
      </div>

      <div className="summary-row-2col">
        <TodayCard data={today} shipped={today_shipment} dates={data.dates} onSummaryClick={setSummaryModal} />
        <YesterdayCard data={yesterday} shipped={yesterday_shipment} dates={data.dates} onSummaryClick={setSummaryModal} />
      </div>

      <div className="section-title">
        <div className="section-icon orange">📋</div>
        <span>PROCESS-WISE PRODUCT BREAKUP</span>
        <div className="section-tabs">
          <button 
            className={`section-tab ${activeTab === 'today' ? 'active' : ''}`}
            onClick={() => setActiveTab('today')}
          >
            📅 Today ({formatDate(dates?.today)})
          </button>
          <button 
            className={`section-tab ${activeTab === 'yesterday' ? 'active' : ''}`}
            onClick={() => setActiveTab('yesterday')}
          >
            📅 Yesterday ({formatDate(dates?.yesterday)})
          </button>
        </div>
      </div>

      <div className="shift-filter-row">
        <button 
          className={`shift-filter-btn ${shiftFilter === 'all' ? 'active' : ''}`}
          onClick={() => setShiftFilter('all')}
        >
          📊 ALL
        </button>
        <button 
          className={`shift-filter-btn ${shiftFilter === 'day' ? 'active' : ''}`}
          onClick={() => setShiftFilter('day')}
        >
          ☀️ DAY
        </button>
        <button 
          className={`shift-filter-btn ${shiftFilter === 'night' ? 'active' : ''}`}
          onClick={() => setShiftFilter('night')}
        >
          🌙 NIGHT
        </button>
      </div>

      <div className="breakup-grid-3col">
        <ProcessBrandTable 
          title="PRODUCED" 
          data={getShiftFilteredProcessData(currentData?.produced)}
          color="green"
          dateLabel={currentLabel}
          onProductClick={handleProductClick}
        />
        <ProcessBrandTable 
          title="RE-PACK" 
          data={getCombinedRepackData()}
          color="blue"
          dateLabel={currentLabel}
          onProductClick={handleProductClick}
        />
        <LooseBagsCard 
          data={getLooseBagsData()}
          dateLabel={currentLabel}
          shiftLabel={shiftFilter === 'all' ? 'ALL' : shiftFilter === 'day' ? 'DAY' : 'NIGHT'}
          onProductClick={handleProductClick}
        />
      </div>

      <div className="section-title">
        <div className="section-icon blue">📊</div>
        <span>DAY VS NIGHT SHIFT COMPARISON</span>
        <div className="section-tabs">
          <button 
            className={`section-tab ${activeTab === 'today' ? 'active' : ''}`}
            onClick={() => setActiveTab('today')}
          >
            Today
          </button>
          <button 
            className={`section-tab ${activeTab === 'yesterday' ? 'active' : ''}`}
            onClick={() => setActiveTab('yesterday')}
          >
            Yesterday
          </button>
        </div>
      </div>

      <div className="chart-card-full">
        <div className="chart-subtitle">🌙 ☀️ {currentLabel} SHIFT-WISE PRODUCTION</div>
        <ShiftComparisonChart data={currentData} />
      </div>

      {/* COMPREHENSIVE BREAKUP - PRODUCT → BRAND → GRADE → PACK SIZE */}
      <div className="section-title">
        <span className="section-icon-emoji">📊</span>
        <span>PRODUCT → BRAND → GRADE → PACK SIZE BREAKUP</span>
        <div className="section-tabs">
          <button 
            className={`section-tab ${activeTab === 'today' ? 'active' : ''}`}
            onClick={() => setActiveTab('today')}
          >
            Today
          </button>
          <button 
            className={`section-tab ${activeTab === 'yesterday' ? 'active' : ''}`}
            onClick={() => setActiveTab('yesterday')}
          >
            Yesterday
          </button>
        </div>
      </div>

      <div className="chart-card-full">
        <ComprehensiveBreakdownChart fullHierarchy={currentFullHierarchy} dateLabel={currentLabel} />
      </div>

      {/* ADDITIONAL BREAKUPS - Collapsible */}
      <div className="collapsible-section">
        <button 
          className="collapsible-toggle"
          onClick={() => setShowMoreBreakdowns(!showMoreBreakdowns)}
        >
          {showMoreBreakdowns ? '▼ Hide' : '▶ Show'} Additional Breakups (Product→Grade, Grade→PackSize, Brand→Grade)
        </button>
        
        {showMoreBreakdowns && (
          <div className="collapsible-content">
            {/* PRODUCT → GRADE BREAKUP */}
            <div className="section-title compact">
              <div className="section-icon green">🏷️</div>
              <span>PRODUCT → GRADE BREAKUP</span>
              <div className="section-tabs">
                <button className={`section-tab ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>Today</button>
                <button className={`section-tab ${activeTab === 'yesterday' ? 'active' : ''}`} onClick={() => setActiveTab('yesterday')}>Yesterday</button>
              </div>
            </div>
            <div className="chart-card-full">
              <ProductGradeBreakdownChart data={currentData} dateLabel={currentLabel} />
            </div>

            {/* GRADE → PACK SIZE BREAKUP */}
            <div className="section-title compact">
              <div className="section-icon purple">📏</div>
              <span>GRADE → PACK SIZE BREAKUP</span>
              <div className="section-tabs">
                <button className={`section-tab ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>Today</button>
                <button className={`section-tab ${activeTab === 'yesterday' ? 'active' : ''}`} onClick={() => setActiveTab('yesterday')}>Yesterday</button>
              </div>
            </div>
            <div className="chart-card-full">
              <GradePackSizeBreakdownChart data={currentData} dateLabel={currentLabel} />
            </div>

            {/* BRAND → GRADE BREAKUP */}
            <div className="section-title compact">
              <div className="section-icon amber">🏷️</div>
              <span>BRAND → GRADE BREAKUP</span>
              <div className="section-tabs">
                <button className={`section-tab ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>Today</button>
                <button className={`section-tab ${activeTab === 'yesterday' ? 'active' : ''}`} onClick={() => setActiveTab('yesterday')}>Yesterday</button>
              </div>
            </div>
            <div className="chart-card-full">
              <BrandGradeBreakdownChart data={currentData} dateLabel={currentLabel} />
            </div>
          </div>
        )}
      </div>

      <div className="section-title">
        <div className="section-icon orange">📈</div>
        <span>WEEKLY PRODUCTION TREND</span>
      </div>

      <div className="chart-card-full">
        <WeeklyProductionTrendChart data={data.weekly_trend} />
      </div>

      {/* Monthly Comparison Section */}
      {data.current_month && (
        <>
          <div className="section-title" style={{ marginTop: '24px' }}>
            <div className="section-icon purple">📅</div>
            <span>MONTHLY COMPARISON</span>
          </div>
          <MonthlyComparisonCard 
            currentMonth={data.current_month} 
            previousMonth={data.previous_month} 
          />
        </>
      )}

      {/* YTD Section */}
      {data.ytd && (
        <>
          <div className="section-title" style={{ marginTop: '24px' }}>
            <div className="section-icon cyan">📊</div>
            <span>YEAR TO DATE (YTD)</span>
          </div>
          <YTDSummaryCard 
            ytd={data.ytd} 
            lastYearYtd={data.last_year_ytd} 
          />
        </>
      )}
    </div>
  );
};

// ===================== PROCESS BRAND TABLE =====================
const ProcessBrandTable = ({ title, data, color, dateLabel, onProductClick }) => {
  const total = data?.total || 0;
  const totalLbs = data?.lbs || 0;
  const products = data?.products || [];
  
  const getHeaderColor = () => {
    switch(color) {
      case 'green': return { bg: 'linear-gradient(135deg, #22c55e, #16a34a)', text: '#fff' };
      case 'blue': return { bg: 'linear-gradient(135deg, #3b82f6, #2563eb)', text: '#fff' };
      case 'orange': return { bg: 'linear-gradient(135deg, #f97316, #ea580c)', text: '#fff' };
      default: return { bg: 'linear-gradient(135deg, #d32f2f, #1565c0)', text: '#fff' };
    }
  };
  
  const headerStyle = getHeaderColor();
  
  return (
    <div className={`breakup-table-card ${color}`}>
      <div className="breakup-table-header" style={{ background: headerStyle.bg }}>
        <div className="table-title" style={{ color: headerStyle.text, fontWeight: 800 }}>{title}</div>
        <div className="header-stats-row">
          <div className="header-stat-item">
            <span className="header-stat-value" style={{ color: headerStyle.text }}>{formatNumber(total)}</span>
            <span className="header-stat-label">CARTONS</span>
          </div>
          <div className="header-stat-item">
            <span className="header-stat-value" style={{ color: headerStyle.text }}>{formatLbs(totalLbs)}</span>
            <span className="header-stat-label">LBS</span>
          </div>
        </div>
      </div>
      <div className="process-brand-table">
        <table>
          <thead>
            <tr>
              <th>PRODUCT</th>
              <th style={{textAlign:'right'}}>CTNS</th>
              <th style={{textAlign:'right'}}>LBS</th>
            </tr>
          </thead>
          <tbody>
            {products.length > 0 ? products.map((p, idx) => (
              <tr key={idx} className="clickable-row" onClick={() => onProductClick && onProductClick(title, p, data)}>
                <td>{p.name}</td>
                <td className="num">{formatNumber(p.cartons)}</td>
                <td className="num lbs">{formatLbs(p.lbs)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="3" className="no-data">No {title.toLowerCase()} {dateLabel.toLowerCase()}</td>
              </tr>
            )}
          </tbody>
          {products.length > 0 && (
            <tfoot>
              <tr className="total-row">
                <td><strong>TOTAL</strong></td>
                <td className="num"><strong>{formatNumber(total)}</strong></td>
                <td className="num lbs"><strong>{formatLbs(totalLbs)}</strong></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ===================== LOOSE BAGS CARD - UPDATED with Product Breakdown and Popup =====================
const LooseBagsCard = ({ data, dateLabel, shiftLabel, onProductClick }) => {
  const total = data?.total || 0;
  const products = data?.products || [];
  
  return (
    <div className="breakup-table-card orange">
      <div className="breakup-table-header" style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}>
        <div className="table-title" style={{ color: '#fff', fontWeight: 800 }}>LOOSE BAGS</div>
        <div className="header-stats-row">
          <div className="header-stat-item">
            <span className="header-stat-value" style={{ color: '#fff' }}>{formatNumber(total)}</span>
            <span className="header-stat-label">TOTAL</span>
          </div>
        </div>
      </div>
      <div className="process-brand-table">
        <table>
          <thead>
            <tr>
              <th>PRODUCT</th>
              <th style={{textAlign:'right'}}>BAGS</th>
            </tr>
          </thead>
          <tbody>
            {products.length > 0 ? products.map((p, idx) => (
              <tr key={idx} className="clickable-row" onClick={() => onProductClick && onProductClick('LOOSE BAGS', p, data)}>
                <td>{p.name}</td>
                <td className="num">{formatNumber(p.bags || p.cartons || 0)}</td>
              </tr>
            )) : total > 0 ? (
              <tr>
                <td>ALL PRODUCTS</td>
                <td className="num">{formatNumber(total)}</td>
              </tr>
            ) : (
              <tr>
                <td colSpan="2" className="no-data">No loose bags {dateLabel?.toLowerCase()}</td>
              </tr>
            )}
          </tbody>
          {(products.length > 0 || total > 0) && products.length > 0 && (
            <tfoot>
              <tr className="total-row">
                <td><strong>TOTAL</strong></td>
                <td className="num"><strong>{formatNumber(total)}</strong></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ===================== PRODUCT GRADE CHART V2 - Hierarchical Product → Grade =====================
const ProductPackSizeChartV2 = ({ data, dateLabel }) => {
  const [tooltip, setTooltip] = React.useState({ show: false, x: 0, y: 0, data: null });
  const chartRef = React.useRef(null);
  
  const handleMouseMove = (e, itemData) => {
    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      setTooltip({
        show: true,
        x: e.clientX - rect.left + 15,
        y: e.clientY - rect.top - 10,
        data: itemData
      });
    }
  };
  
  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, data: null });
  };
  
  // Build Product → Grade hierarchy
  const productGradeData = {};
  
  ['produced', 'repack', 'reprint', 'reprocess'].forEach(process => {
    const products = data?.[process]?.products || [];
    products.forEach(p => {
      if (!productGradeData[p.name]) {
        productGradeData[p.name] = { cartons: 0, lbs: 0, grades: {} };
      }
      productGradeData[p.name].cartons += p.cartons;
      productGradeData[p.name].lbs += p.lbs;
      
      // Add grade breakdown if available
      if (p.grades) {
        p.grades.forEach(g => {
          if (!productGradeData[p.name].grades[g.name]) {
            productGradeData[p.name].grades[g.name] = { cartons: 0, lbs: 0 };
          }
          productGradeData[p.name].grades[g.name].cartons += g.cartons;
          productGradeData[p.name].grades[g.name].lbs += g.lbs;
        });
      }
    });
  });
  
  const sortedProducts = Object.entries(productGradeData)
    .map(([name, values]) => ({
      name,
      cartons: values.cartons,
      lbs: values.lbs,
      grades: Object.entries(values.grades)
        .map(([gName, gValues]) => ({ name: gName, ...gValues }))
        .sort((a, b) => b.cartons - a.cartons)
    }))
    .sort((a, b) => b.cartons - a.cartons)
    .slice(0, 6);

  if (sortedProducts.length === 0) {
    return <div className="no-chart-data">No product data for {dateLabel}</div>;
  }

  const maxCartons = sortedProducts[0]?.cartons || 1;
  const productColors = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#06b6d4', '#ef4444'];

  return (
    <div className="product-grade-chart" ref={chartRef} style={{ position: 'relative' }}>
      <div className="chart-subtitle-bold">📊 PRODUCT → GRADE BREAKUP ({dateLabel})</div>
      <div className="product-grade-bars">
        {sortedProducts.map((product, pIdx) => (
          <div key={pIdx} className="product-grade-group">
            <div 
              className="product-bar-row main-product"
              onMouseMove={(e) => handleMouseMove(e, { type: 'product', ...product })}
              onMouseLeave={handleMouseLeave}
            >
              <div className="bar-label product-name-bold">{product.name}</div>
              <div className="bar-container">
                <div 
                  className="bar-fill" 
                  style={{ 
                    width: `${(product.cartons / maxCartons) * 100}%`,
                    background: productColors[pIdx % productColors.length]
                  }}
                >
                  <span className="bar-value-large">{formatNumber(product.cartons)}</span>
                </div>
              </div>
            </div>
            {product.grades.slice(0, 4).map((grade, gIdx) => (
              <div 
                key={gIdx} 
                className="product-bar-row grade-row"
                onMouseMove={(e) => handleMouseMove(e, { type: 'grade', ...grade, product: product.name })}
                onMouseLeave={handleMouseLeave}
              >
                <div className="bar-label grade-name">↳ {grade.name}</div>
                <div className="bar-container">
                  <div 
                    className="bar-fill grade-bar" 
                    style={{ 
                      width: `${(grade.cartons / maxCartons) * 100}%`,
                      background: productColors[pIdx % productColors.length] + '80'
                    }}
                  >
                    <span className="bar-value-medium">{formatNumber(grade.cartons)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* Mouse-following tooltip */}
      {tooltip.show && tooltip.data && (
        <div className="floating-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="tooltip-title">{tooltip.data.type === 'product' ? tooltip.data.name : `${tooltip.data.product} → ${tooltip.data.name}`}</div>
          <div className="tooltip-row"><strong>Cartons:</strong> {formatNumber(tooltip.data.cartons)}</div>
          <div className="tooltip-row"><strong>{formatLbs(tooltip.data.lbs)} LBS</strong></div>
          {tooltip.data.type === 'product' && tooltip.data.grades?.length > 0 && (
            <>
              <div className="tooltip-divider"></div>
              <div className="tooltip-subtitle">Grade Breakup:</div>
              {tooltip.data.grades.slice(0, 5).map((g, i) => (
                <div key={i} className="tooltip-row small">↳ {g.name}: {formatNumber(g.cartons)} ctns / {formatLbs(g.lbs)} lbs</div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== PRODUCT BRAND CHART V2 - Hierarchical Product → Grade =====================
const ProductBrandChartV2 = ({ data, dateLabel }) => {
  const [tooltip, setTooltip] = React.useState({ show: false, x: 0, y: 0, data: null });
  const chartRef = React.useRef(null);
  
  const handleMouseMove = (e, itemData) => {
    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      setTooltip({
        show: true,
        x: e.clientX - rect.left + 15,
        y: e.clientY - rect.top - 10,
        data: itemData
      });
    }
  };
  
  const handleMouseLeave = () => {
    setTooltip({ show: false, x: 0, y: 0, data: null });
  };
  
  // Build Product → Grade hierarchy (similar to Product Pack Size)
  const productGradeData = {};
  
  ['produced', 'repack', 'reprint', 'reprocess'].forEach(process => {
    const products = data?.[process]?.products || [];
    products.forEach(p => {
      if (!productGradeData[p.name]) {
        productGradeData[p.name] = { cartons: 0, lbs: 0, grades: {} };
      }
      productGradeData[p.name].cartons += p.cartons;
      productGradeData[p.name].lbs += p.lbs;
      
      // Add grade/pack_size breakdown
      if (p.grades) {
        p.grades.forEach(g => {
          if (!productGradeData[p.name].grades[g.name]) {
            productGradeData[p.name].grades[g.name] = { cartons: 0, lbs: 0 };
          }
          productGradeData[p.name].grades[g.name].cartons += g.cartons;
          productGradeData[p.name].grades[g.name].lbs += g.lbs;
        });
      }
      if (p.pack_sizes) {
        p.pack_sizes.forEach(ps => {
          if (!productGradeData[p.name].grades[ps.name]) {
            productGradeData[p.name].grades[ps.name] = { cartons: 0, lbs: 0 };
          }
          productGradeData[p.name].grades[ps.name].cartons += ps.cartons;
          productGradeData[p.name].grades[ps.name].lbs += ps.lbs;
        });
      }
    });
  });
  
  const sortedProducts = Object.entries(productGradeData)
    .map(([name, values]) => ({
      name,
      cartons: values.cartons,
      lbs: values.lbs,
      grades: Object.entries(values.grades)
        .map(([gName, gValues]) => ({ name: gName, ...gValues }))
        .sort((a, b) => b.cartons - a.cartons)
    }))
    .sort((a, b) => b.cartons - a.cartons)
    .slice(0, 6);

  if (sortedProducts.length === 0) {
    return <div className="no-chart-data">No product data for {dateLabel}</div>;
  }
  
  const productColors = ['#1565c0', '#22c55e', '#f9a825', '#c62828', '#0097a7', '#6a1b9a'];
  const maxCartons = sortedProducts[0]?.cartons || 1;
  
  return (
    <div className="product-grade-chart brand-variant" ref={chartRef} style={{ position: 'relative' }}>
      <div className="chart-subtitle-bold">🏷️ PRODUCT → GRADE BREAKUP ({dateLabel})</div>
      <div className="product-grade-bars">
        {sortedProducts.map((product, pIdx) => (
          <div key={pIdx} className="product-grade-group">
            <div 
              className="product-bar-row main-product"
              onMouseMove={(e) => handleMouseMove(e, { type: 'product', ...product })}
              onMouseLeave={handleMouseLeave}
            >
              <div className="bar-label product-name-bold">{product.name}</div>
              <div className="bar-container">
                <div 
                  className="bar-fill" 
                  style={{ 
                    width: `${(product.cartons / maxCartons) * 100}%`,
                    background: productColors[pIdx % productColors.length]
                  }}
                >
                  <span className="bar-value-large">{formatNumber(product.cartons)}</span>
                </div>
              </div>
            </div>
            {product.grades.slice(0, 4).map((grade, gIdx) => (
              <div 
                key={gIdx} 
                className="product-bar-row grade-row"
                onMouseMove={(e) => handleMouseMove(e, { type: 'grade', ...grade, product: product.name })}
                onMouseLeave={handleMouseLeave}
              >
                <div className="bar-label grade-name">↳ {grade.name}</div>
                <div className="bar-container">
                  <div 
                    className="bar-fill grade-bar" 
                    style={{ 
                      width: `${(grade.cartons / maxCartons) * 100}%`,
                      background: productColors[pIdx % productColors.length] + '80'
                    }}
                  >
                    <span className="bar-value-medium">{formatNumber(grade.cartons)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
      
      {/* Mouse-following tooltip */}
      {tooltip.show && tooltip.data && (
        <div className="floating-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="tooltip-title">{tooltip.data.type === 'product' ? tooltip.data.name : `${tooltip.data.product} → ${tooltip.data.name}`}</div>
          <div className="tooltip-row"><strong>Cartons:</strong> {formatNumber(tooltip.data.cartons)}</div>
          <div className="tooltip-row"><strong>{formatLbs(tooltip.data.lbs)} LBS</strong></div>
          {tooltip.data.type === 'product' && tooltip.data.grades?.length > 0 && (
            <>
              <div className="tooltip-divider"></div>
              <div className="tooltip-subtitle">Grade Breakup:</div>
              {tooltip.data.grades.slice(0, 5).map((g, i) => (
                <div key={i} className="tooltip-row small">↳ {g.name}: {formatNumber(g.cartons)} ctns / {formatLbs(g.lbs)} lbs</div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== TODAY CARD =====================
const TodayCard = ({ data, shipped, dates, onSummaryClick }) => {
  // API returns data under 'summary' property: data.summary.total_cartons, data.summary.produced, etc.
  const summary = data?.summary || {};
  
  const repackConsolidated = {
    total: (summary.repack?.total || 0) + (summary.reprint?.total || 0) + (summary.reprocess?.total || 0),
    lbs: (summary.repack?.lbs || 0) + (summary.reprint?.lbs || 0) + (summary.reprocess?.lbs || 0),
    day: {
      cartons: (summary.repack?.day?.cartons || 0) + (summary.reprint?.day?.cartons || 0) + (summary.reprocess?.day?.cartons || 0),
      lbs: (summary.repack?.day?.lbs || 0) + (summary.reprint?.day?.lbs || 0) + (summary.reprocess?.day?.lbs || 0)
    },
    night: {
      cartons: (summary.repack?.night?.cartons || 0) + (summary.reprint?.night?.cartons || 0) + (summary.reprocess?.night?.cartons || 0),
      lbs: (summary.repack?.night?.lbs || 0) + (summary.reprint?.night?.lbs || 0) + (summary.reprocess?.night?.lbs || 0)
    }
  };
  
  // Combine repack data - get products/brands/grades from each process
  const repackData = {
    products: [...(summary.repack?.products || []), ...(summary.reprint?.products || []), ...(summary.reprocess?.products || [])],
    brands: [...(summary.repack?.brands || []), ...(summary.reprint?.brands || []), ...(summary.reprocess?.brands || [])],
    grades: [...(summary.repack?.grades || []), ...(summary.reprint?.grades || []), ...(summary.reprocess?.grades || [])],
    total: repackConsolidated.total,
    lbs: repackConsolidated.lbs
  };
  
  // Loose bags data
  const looseBagsData = {
    products: summary.loose_bags_breakdown?.products || [],
    total: summary.total_loose_bags || 0
  };

  return (
    <div className="summary-card-main today">
      <div className="card-header-row">
        <div className="date-label">
          <span>📅 TODAY</span>
          <small>({formatDate(dates?.today)})</small>
        </div>
        <span className="badge-live">🔴 LIVE</span>
      </div>
      
      <div className="total-box green-bg">
        <div className="total-label">TOTAL CARTONS</div>
        <div className="total-value green">{formatNumber(summary.total_cartons || 0)}</div>
        <div className="total-lbs">{formatLbs(summary.total_lbs || 0)} LBS</div>
      </div>

      <div className="process-grid-with-shifts">
        <ProcessBoxWithShift 
          label="PRODUCED" 
          data={summary.produced} 
          color="green"
          onClick={() => onSummaryClick && onSummaryClick({
            title: 'PRODUCED',
            type: 'produced',
            summary: summary.produced,
            processData: summary.produced,
            dateLabel: 'TODAY'
          })}
        />
        <ProcessBoxWithShift 
          label="RE-PACK" 
          data={repackConsolidated} 
          color="blue"
          onClick={() => onSummaryClick && onSummaryClick({
            title: 'RE-PACK',
            type: 'repack',
            summary: repackConsolidated,
            processData: repackData,
            dateLabel: 'TODAY'
          })}
        />
        <LooseBagsBox 
          count={summary.total_loose_bags || 0}
          dayBags={summary.day_loose_bags || 0}
          nightBags={summary.night_loose_bags || 0}
          onClick={() => onSummaryClick && onSummaryClick({
            title: 'LOOSE BAGS',
            type: 'loose_bags',
            summary: looseBagsData,
            processData: looseBagsData,
            dateLabel: 'TODAY'
          })}
        />
        <ShippedBox 
          data={shipped}
          onClick={() => onSummaryClick && onSummaryClick({
            title: 'SHIPPED',
            type: 'shipped',
            summary: shipped,
            processData: shipped,
            dateLabel: 'TODAY'
          })}
        />
      </div>
    </div>
  );
};

// ===================== YESTERDAY CARD =====================
const YesterdayCard = ({ data, shipped, dates, onSummaryClick }) => {
  // API returns data under 'summary' property: data.summary.total_cartons, data.summary.produced, etc.
  const summary = data?.summary || {};
  
  const repackConsolidated = {
    total: (summary.repack?.total || 0) + (summary.reprint?.total || 0) + (summary.reprocess?.total || 0),
    lbs: (summary.repack?.lbs || 0) + (summary.reprint?.lbs || 0) + (summary.reprocess?.lbs || 0),
    day: {
      cartons: (summary.repack?.day?.cartons || 0) + (summary.reprint?.day?.cartons || 0) + (summary.reprocess?.day?.cartons || 0),
      lbs: (summary.repack?.day?.lbs || 0) + (summary.reprint?.day?.lbs || 0) + (summary.reprocess?.day?.lbs || 0)
    },
    night: {
      cartons: (summary.repack?.night?.cartons || 0) + (summary.reprint?.night?.cartons || 0) + (summary.reprocess?.night?.cartons || 0),
      lbs: (summary.repack?.night?.lbs || 0) + (summary.reprint?.night?.lbs || 0) + (summary.reprocess?.night?.lbs || 0)
    }
  };
  
  // Combine repack data - get products/brands/grades from each process
  const repackData = {
    products: [...(summary.repack?.products || []), ...(summary.reprint?.products || []), ...(summary.reprocess?.products || [])],
    brands: [...(summary.repack?.brands || []), ...(summary.reprint?.brands || []), ...(summary.reprocess?.brands || [])],
    grades: [...(summary.repack?.grades || []), ...(summary.reprint?.grades || []), ...(summary.reprocess?.grades || [])],
    total: repackConsolidated.total,
    lbs: repackConsolidated.lbs
  };
  
  // Loose bags data
  const looseBagsData = {
    products: summary.loose_bags_breakdown?.products || [],
    total: summary.total_loose_bags || 0
  };

  return (
    <div className="summary-card-main yesterday">
      <div className="card-header-row">
        <div className="date-label">
          <span>📅 YESTERDAY</span>
          <small>({formatDate(dates?.yesterday)})</small>
        </div>
        <span className="badge-complete">✅ COMPLETED</span>
      </div>
      
      <div className="total-box purple-bg">
        <div className="total-label">TOTAL CARTONS</div>
        <div className="total-value purple">{formatNumber(summary.total_cartons || 0)}</div>
        <div className="total-lbs">{formatLbs(summary.total_lbs || 0)} LBS</div>
      </div>

      <div className="process-grid-with-shifts">
        <ProcessBoxWithShift 
          label="PRODUCED" 
          data={summary.produced} 
          color="green"
          onClick={() => onSummaryClick && onSummaryClick({
            title: 'PRODUCED',
            type: 'produced',
            summary: summary.produced,
            processData: summary.produced,
            dateLabel: 'YESTERDAY'
          })}
        />
        <ProcessBoxWithShift 
          label="RE-PACK" 
          data={repackConsolidated} 
          color="blue"
          onClick={() => onSummaryClick && onSummaryClick({
            title: 'RE-PACK',
            type: 'repack',
            summary: repackConsolidated,
            processData: repackData,
            dateLabel: 'YESTERDAY'
          })}
        />
        <LooseBagsBox 
          count={summary.total_loose_bags || 0}
          dayBags={summary.day_loose_bags || 0}
          nightBags={summary.night_loose_bags || 0}
          onClick={() => onSummaryClick && onSummaryClick({
            title: 'LOOSE BAGS',
            type: 'loose_bags',
            summary: looseBagsData,
            processData: looseBagsData,
            dateLabel: 'YESTERDAY'
          })}
        />
        <ShippedBox 
          data={shipped}
          onClick={() => onSummaryClick && onSummaryClick({
            title: 'SHIPPED',
            type: 'shipped',
            summary: shipped,
            processData: shipped,
            dateLabel: 'YESTERDAY'
          })}
        />
      </div>
    </div>
  );
};

// ===================== PROCESS BOX WITH SHIFT =====================
const ProcessBoxWithShift = ({ label, data, color, onClick }) => {
  const total = data?.total || 0;
  const totalLbs = data?.lbs || 0;
  const dayCartons = data?.day?.cartons || 0;
  const dayLbs = data?.day?.lbs || 0;
  const nightCartons = data?.night?.cartons || 0;
  const nightLbs = data?.night?.lbs || 0;

  return (
    <div className={`process-box-with-shift ${color} ${onClick ? 'clickable' : ''}`} onClick={onClick}>
      <div className="process-header">
        <div className="process-label">{label}</div>
        {onClick && <span className="click-hint">Click for details</span>}
      </div>
      <div className="process-total-row">
        <div className="process-total-value">{formatNumber(total)}</div>
        <div className="process-total-lbs">{formatLbs(totalLbs)} LBS</div>
      </div>
      <div className="process-shift-row">
        <div className="shift-col day">
          <div className="shift-header">☀️ Day</div>
          <div className="shift-cartons">{formatNumber(dayCartons)}</div>
          <div className="shift-lbs">{formatLbs(dayLbs)}</div>
        </div>
        <div className="shift-col night">
          <div className="shift-header">🌙 Night</div>
          <div className="shift-cartons">{formatNumber(nightCartons)}</div>
          <div className="shift-lbs">{formatLbs(nightLbs)}</div>
        </div>
      </div>
    </div>
  );
};

// ===================== LOOSE BAGS BOX =====================
const LooseBagsBox = ({ count, dayBags, nightBags, onClick }) => (
  <div className={`process-box-with-shift orange ${onClick ? 'clickable' : ''}`} onClick={onClick}>
    <div className="process-header">
      <div className="process-label">LOOSE BAGS</div>
      {onClick && <span className="click-hint">Click for details</span>}
    </div>
    <div className="process-total-row">
      <div className="process-total-value">{formatNumber(count)}</div>
      <div className="process-total-lbs">BAGS</div>
    </div>
    <div className="process-shift-row">
      <div className="shift-col day">
        <div className="shift-header">☀️ Day</div>
        <div className="shift-cartons">{formatNumber(dayBags || 0)}</div>
      </div>
      <div className="shift-col night">
        <div className="shift-header">🌙 Night</div>
        <div className="shift-cartons">{formatNumber(nightBags || 0)}</div>
      </div>
    </div>
  </div>
);

// ===================== SHIPPED BOX (No Shift Breakup) =====================
const ShippedBox = ({ data, onClick }) => (
  <div className={`process-box-simple shipped ${onClick ? 'clickable' : ''}`} onClick={onClick}>
    <div className="process-header">
      <div className="process-label">SHIPPED</div>
      {onClick && <span className="click-hint">Click for details</span>}
    </div>
    <div className="process-total-row">
      <div className="process-total-value shipped">{formatNumber(data?.total_cartons || 0)}</div>
      <div className="process-total-lbs">{formatLbs(data?.total_lbs || 0)} LBS</div>
    </div>
  </div>
);

// ===================== SHIFT COMPARISON CHART =====================
const ShiftComparisonChart = ({ data }) => {
  const repackDayCartons = (data?.repack?.day?.cartons || 0) + (data?.reprint?.day?.cartons || 0) + (data?.reprocess?.day?.cartons || 0);
  const repackNightCartons = (data?.repack?.night?.cartons || 0) + (data?.reprint?.night?.cartons || 0) + (data?.reprocess?.night?.cartons || 0);
  const repackDayLbs = (data?.repack?.day?.lbs || 0) + (data?.reprint?.day?.lbs || 0) + (data?.reprocess?.day?.lbs || 0);
  const repackNightLbs = (data?.repack?.night?.lbs || 0) + (data?.reprint?.night?.lbs || 0) + (data?.reprocess?.night?.lbs || 0);
  
  // Get loose bags data - use correct data path
  const looseBagsDayCount = data?.day_loose_bags || data?.loose_bags_breakdown?.day?.total || 0;
  const looseBagsNightCount = data?.night_loose_bags || data?.loose_bags_breakdown?.night?.total || 0;
  const looseBagsTotal = data?.total_loose_bags || (looseBagsDayCount + looseBagsNightCount);
  
  const dayData = [
    data?.produced?.day?.cartons || 0,
    repackDayCartons,
    looseBagsDayCount,
    (data?.produced?.day?.cartons || 0) + repackDayCartons
  ];
  
  const nightData = [
    data?.produced?.night?.cartons || 0,
    repackNightCartons,
    looseBagsNightCount,
    (data?.produced?.night?.cartons || 0) + repackNightCartons
  ];

  // LBS data for tooltips
  const dayLbsData = [
    data?.produced?.day?.lbs || 0,
    repackDayLbs,
    0,
    (data?.produced?.day?.lbs || 0) + repackDayLbs
  ];
  
  const nightLbsData = [
    data?.produced?.night?.lbs || 0,
    repackNightLbs,
    0,
    (data?.produced?.night?.lbs || 0) + repackNightLbs
  ];

  const chartData = {
    labels: ['PRODUCED', 'RE-PACK', 'LOOSE BAGS', 'TOTAL'],
    datasets: [
      {
        label: '☀️ DAY SHIFT',
        data: dayData,
        lbsData: dayLbsData,
        backgroundColor: '#f97316',
        borderRadius: 4,
        barPercentage: 0.7,
      },
      {
        label: '🌙 NIGHT SHIFT',
        data: nightData,
        lbsData: nightLbsData,
        backgroundColor: '#8b5cf6',
        borderRadius: 4,
        barPercentage: 0.7,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'top', 
        labels: { usePointStyle: true, padding: 15, font: { size: 10, weight: '700' }, color: '#1e293b' } 
      },
      datalabels: { 
        anchor: 'end', 
        align: 'top', 
        color: '#1e293b', 
        font: { size: 9, weight: '800' }, 
        formatter: (value) => value > 0 ? value.toLocaleString() : ''
      },
      tooltip: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleFont: { size: 11, weight: '800' },
        bodyFont: { size: 10, weight: '600' },
        padding: 10,
        cornerRadius: 6,
        callbacks: {
          title: (context) => {
            const shift = context[0].datasetIndex === 0 ? '☀️ DAY' : '🌙 NIGHT';
            return shift + ' SHIFT - ' + context[0].label;
          },
          label: (context) => {
            const lbsData = context.dataset.lbsData;
            const lbs = lbsData ? lbsData[context.dataIndex] : 0;
            if (context.label === 'LOOSE BAGS') {
              return 'Bags: ' + context.raw.toLocaleString();
            }
            return [
              'Cartons: ' + context.raw.toLocaleString(),
              Math.round(lbs).toLocaleString() + ' LBS'
            ];
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 9 } } },
      x: { grid: { display: false } }
    }
  };

  return <div style={{ height: '300px' }}><Bar data={chartData} options={options} /></div>;
};

// ===================== COMPREHENSIVE BREAKUP TABLE =====================
// PRODUCT → BRAND → GRADE → PACK SIZE - Full Hierarchy (Uses API full_hierarchy data)
const ComprehensiveBreakdownChart = ({ fullHierarchy, dateLabel }) => {
  const [expanded, setExpanded] = React.useState({ products: {}, brands: {}, grades: {} });
  
  // Use full_hierarchy data directly from API
  const products = fullHierarchy || [];
  
  const grandTotal = products.reduce((sum, p) => sum + (p.cartons || 0), 0);
  const grandTotalLbs = products.reduce((sum, p) => sum + (p.lbs || 0), 0);
  
  const toggleProduct = (name) => setExpanded(prev => ({ ...prev, products: { ...prev.products, [name]: !prev.products[name] } }));
  const toggleBrand = (key) => setExpanded(prev => ({ ...prev, brands: { ...prev.brands, [key]: !prev.brands[key] } }));
  const toggleGrade = (key) => setExpanded(prev => ({ ...prev, grades: { ...prev.grades, [key]: !prev.grades[key] } }));

  if (products.length === 0) {
    return <div className="no-chart-data">No hierarchy data available for {dateLabel}</div>;
  }
  
  return (
    <div className="expandable-breakdown comprehensive">
      <div className="breakdown-header green-gradient">
        <div className="header-left">
          <span className="header-icon">📊</span>
          <span className="header-title">PRODUCT → BRAND → GRADE → PACK SIZE ({dateLabel})</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body" style={{ maxHeight: '500px' }}>
        <table className="expandable-table hierarchy-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>#</th>
              <th>PRODUCT / BRAND / GRADE / PACK SIZE</th>
              <th style={{ width: '120px' }}>DISTRIBUTION</th>
              <th style={{ width: '100px', textAlign: 'right' }}>CARTONS</th>
              <th style={{ width: '100px', textAlign: 'right' }}>LBS</th>
              <th style={{ width: '60px', textAlign: 'center' }}>%</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, pIdx) => {
              const pPct = grandTotal > 0 ? Math.round((product.cartons / grandTotal) * 100) : 0;
              const pExpanded = expanded.products[product.name];
              const hasBrands = product.brands && product.brands.length > 0;
              return (
                <React.Fragment key={pIdx}>
                  {/* PRODUCT ROW - Level 0 */}
                  <tr className={`level-0 ${pExpanded ? 'expanded' : ''} ${hasBrands ? 'clickable' : ''}`} onClick={() => hasBrands && toggleProduct(product.name)}>
                    <td className="col-rank">{pIdx + 1}</td>
                    <td className="col-name">
                      {hasBrands && <span className="expand-arrow">{pExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text level-0-name">{product.name}</span>
                      {hasBrands && <span className="child-count">({product.brands.length} brands)</span>}
                    </td>
                    <td className="col-bar"><div className="mini-bar"><div className="mini-fill green" style={{ width: `${pPct}%` }}></div></div></td>
                    <td className="col-num green">{formatNumber(product.cartons)}</td>
                    <td className="col-num purple">{formatLbs(product.lbs)}</td>
                    <td className="col-pct">{pPct}%</td>
                  </tr>
                  
                  {/* BRAND ROWS - Level 1 */}
                  {pExpanded && product.brands.map((brand, bIdx) => {
                    const bKey = `${product.name}-${brand.name}`;
                    const bPct = product.cartons > 0 ? Math.round((brand.cartons / product.cartons) * 100) : 0;
                    const bExpanded = expanded.brands[bKey];
                    const hasGrades = brand.grades && brand.grades.length > 0;
                    return (
                      <React.Fragment key={bIdx}>
                        <tr className={`level-1 ${bExpanded ? 'expanded' : ''} ${hasGrades ? 'clickable' : ''}`} onClick={(e) => { e.stopPropagation(); hasGrades && toggleBrand(bKey); }}>
                          <td className="col-rank"></td>
                          <td className="col-name level-1-indent">
                            {hasGrades && <span className="expand-arrow">{bExpanded ? '▼' : '▶'}</span>}
                            <span className="name-text">↳ {brand.name}</span>
                            {hasGrades && <span className="child-count">({brand.grades.length})</span>}
                          </td>
                          <td className="col-bar"><div className="mini-bar"><div className="mini-fill blue" style={{ width: `${bPct}%` }}></div></div></td>
                          <td className="col-num">{formatNumber(brand.cartons)}</td>
                          <td className="col-num">{formatLbs(brand.lbs)}</td>
                          <td className="col-pct">{bPct}%</td>
                        </tr>
                        
                        {/* GRADE ROWS - Level 2 */}
                        {bExpanded && brand.grades.map((grade, gIdx) => {
                          const gKey = `${bKey}-${grade.name}`;
                          const gPct = brand.cartons > 0 ? Math.round((grade.cartons / brand.cartons) * 100) : 0;
                          const gExpanded = expanded.grades[gKey];
                          const hasPackSizes = grade.pack_sizes && grade.pack_sizes.length > 0;
                          return (
                            <React.Fragment key={gIdx}>
                              <tr className={`level-2 ${gExpanded ? 'expanded' : ''} ${hasPackSizes ? 'clickable' : ''}`} onClick={(e) => { e.stopPropagation(); hasPackSizes && toggleGrade(gKey); }}>
                                <td className="col-rank"></td>
                                <td className="col-name level-2-indent">
                                  {hasPackSizes && <span className="expand-arrow">{gExpanded ? '▼' : '▶'}</span>}
                                  <span className="name-text">↳ {grade.name}</span>
                                  {hasPackSizes && <span className="child-count">({grade.pack_sizes.length})</span>}
                                </td>
                                <td className="col-bar"><div className="mini-bar"><div className="mini-fill purple" style={{ width: `${gPct}%` }}></div></div></td>
                                <td className="col-num">{formatNumber(grade.cartons)}</td>
                                <td className="col-num">{formatLbs(grade.lbs)}</td>
                                <td className="col-pct">{gPct}%</td>
                              </tr>
                              
                              {/* PACK SIZE ROWS - Level 3 */}
                              {gExpanded && grade.pack_sizes.map((ps, psIdx) => {
                                const psPct = grade.cartons > 0 ? Math.round((ps.cartons / grade.cartons) * 100) : 0;
                                return (
                                  <tr key={psIdx} className="level-3">
                                    <td className="col-rank"></td>
                                    <td className="col-name level-3-indent">
                                      <span className="name-text">↳ {ps.name}</span>
                                    </td>
                                    <td className="col-bar"><div className="mini-bar"><div className="mini-fill amber" style={{ width: `${psPct}%` }}></div></div></td>
                                    <td className="col-num">{formatNumber(ps.cartons)}</td>
                                    <td className="col-num">{formatLbs(ps.lbs)}</td>
                                    <td className="col-pct">{psPct}%</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===================== EXPANDABLE BREAKUP TABLES =====================
// Product → Grade Breakdown - EXPANDABLE TABLE
const ProductGradeBreakdownChart = ({ data, dateLabel }) => {
  const [expandedItems, setExpandedItems] = React.useState({});
  
  // Build Product → Grade hierarchy from production data
  const productGradeData = {};
  ['produced', 'repack', 'reprint', 'reprocess'].forEach(process => {
    const products = data?.[process]?.products || [];
    products.forEach(p => {
      if (!productGradeData[p.name]) {
        productGradeData[p.name] = { cartons: 0, lbs: 0, grades: {} };
      }
      productGradeData[p.name].cartons += p.cartons;
      productGradeData[p.name].lbs += p.lbs;
      
      if (p.grades) {
        p.grades.forEach(g => {
          if (!productGradeData[p.name].grades[g.name]) {
            productGradeData[p.name].grades[g.name] = { cartons: 0, lbs: 0 };
          }
          productGradeData[p.name].grades[g.name].cartons += g.cartons;
          productGradeData[p.name].grades[g.name].lbs += g.lbs;
        });
      }
    });
  });
  
  const allProducts = Object.entries(productGradeData)
    .map(([name, values]) => ({
      name,
      cartons: values.cartons,
      lbs: values.lbs,
      grades: Object.entries(values.grades)
        .map(([gName, gValues]) => ({ name: gName, ...gValues }))
        .sort((a, b) => b.cartons - a.cartons)
    }))
    .sort((a, b) => b.cartons - a.cartons);
  
  const grandTotal = allProducts.reduce((sum, p) => sum + (p.cartons || 0), 0);
  const grandTotalLbs = allProducts.reduce((sum, p) => sum + (p.lbs || 0), 0);
  
  const toggleItem = (name) => {
    setExpandedItems(prev => ({ ...prev, [name]: !prev[name] }));
  };

  if (allProducts.length === 0) {
    return <div className="no-chart-data">No product data for {dateLabel}</div>;
  }
  
  return (
    <div className="expandable-breakdown">
      <div className="breakdown-header purple-gradient">
        <div className="header-left">
          <span className="header-icon">🏷️</span>
          <span className="header-title">PRODUCT → GRADE BREAKUP ({dateLabel})</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body">
        <table className="expandable-table">
          <thead>
            <tr>
              <th className="col-rank">#</th>
              <th className="col-name">PRODUCT / GRADE</th>
              <th className="col-bar">DISTRIBUTION</th>
              <th className="col-num">CARTONS</th>
              <th className="col-num">LBS</th>
              <th className="col-pct">%</th>
            </tr>
          </thead>
          <tbody>
            {allProducts.map((product, idx) => {
              const pct = grandTotal > 0 ? Math.round((product.cartons / grandTotal) * 100) : 0;
              const isExpanded = expandedItems[product.name];
              const hasChildren = product.grades && product.grades.length > 0;
              return (
                <React.Fragment key={idx}>
                  <tr className={`parent-row ${isExpanded ? 'expanded' : ''} ${hasChildren ? 'clickable' : ''}`} onClick={() => hasChildren && toggleItem(product.name)}>
                    <td className="col-rank">{idx + 1}</td>
                    <td className="col-name parent">
                      {hasChildren && <span className="expand-arrow">{isExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text">{product.name}</span>
                      {hasChildren && <span className="child-count">({product.grades.length})</span>}
                    </td>
                    <td className="col-bar">
                      <div className="mini-bar"><div className="mini-fill purple" style={{ width: `${pct}%` }}></div></div>
                    </td>
                    <td className="col-num green">{formatNumber(product.cartons)}</td>
                    <td className="col-num purple">{formatLbs(product.lbs)}</td>
                    <td className="col-pct">{pct}%</td>
                  </tr>
                  {isExpanded && product.grades.map((grade, gIdx) => {
                    const gPct = product.cartons > 0 ? Math.round((grade.cartons / product.cartons) * 100) : 0;
                    return (
                      <tr key={`g-${gIdx}`} className="child-row">
                        <td className="col-rank"></td>
                        <td className="col-name child">↳ {grade.name}</td>
                        <td className="col-bar"><div className="mini-bar child"><div className="mini-fill purple-light" style={{ width: `${gPct}%` }}></div></div></td>
                        <td className="col-num">{formatNumber(grade.cartons)}</td>
                        <td className="col-num">{formatLbs(grade.lbs)}</td>
                        <td className="col-pct">{gPct}%</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Grade → Pack Size Breakdown - EXPANDABLE TABLE
const GradePackSizeBreakdownChart = ({ data, dateLabel }) => {
  const [expandedItems, setExpandedItems] = React.useState({});
  
  // Build Grade → Pack Size hierarchy
  const gradeData = {};
  ['produced', 'repack', 'reprint', 'reprocess'].forEach(process => {
    const grades = data?.[process]?.grades || [];
    grades.forEach(g => {
      if (!gradeData[g.name]) {
        gradeData[g.name] = { cartons: 0, lbs: 0, pack_sizes: {} };
      }
      gradeData[g.name].cartons += g.cartons;
      gradeData[g.name].lbs += g.lbs;
      
      if (g.pack_sizes) {
        g.pack_sizes.forEach(ps => {
          if (!gradeData[g.name].pack_sizes[ps.name]) {
            gradeData[g.name].pack_sizes[ps.name] = { cartons: 0, lbs: 0 };
          }
          gradeData[g.name].pack_sizes[ps.name].cartons += ps.cartons;
          gradeData[g.name].pack_sizes[ps.name].lbs += ps.lbs;
        });
      }
    });
  });
  
  const allGrades = Object.entries(gradeData)
    .map(([name, values]) => ({
      name,
      cartons: values.cartons,
      lbs: values.lbs,
      pack_sizes: Object.entries(values.pack_sizes)
        .map(([psName, psValues]) => ({ name: psName, ...psValues }))
        .sort((a, b) => b.cartons - a.cartons)
    }))
    .sort((a, b) => b.cartons - a.cartons);
  
  const grandTotal = allGrades.reduce((sum, g) => sum + (g.cartons || 0), 0);
  const grandTotalLbs = allGrades.reduce((sum, g) => sum + (g.lbs || 0), 0);
  
  const toggleItem = (name) => {
    setExpandedItems(prev => ({ ...prev, [name]: !prev[name] }));
  };

  if (allGrades.length === 0) {
    return <div className="no-chart-data">No grade data for {dateLabel}</div>;
  }
  
  return (
    <div className="expandable-breakdown">
      <div className="breakdown-header cyan-gradient">
        <div className="header-left">
          <span className="header-icon">📏</span>
          <span className="header-title">GRADE → PACK SIZE BREAKUP ({dateLabel})</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body">
        <table className="expandable-table">
          <thead>
            <tr>
              <th className="col-rank">#</th>
              <th className="col-name">GRADE / PACK SIZE</th>
              <th className="col-bar">DISTRIBUTION</th>
              <th className="col-num">CARTONS</th>
              <th className="col-num">LBS</th>
              <th className="col-pct">%</th>
            </tr>
          </thead>
          <tbody>
            {allGrades.map((grade, idx) => {
              const pct = grandTotal > 0 ? Math.round((grade.cartons / grandTotal) * 100) : 0;
              const isExpanded = expandedItems[grade.name];
              const hasChildren = grade.pack_sizes && grade.pack_sizes.length > 0;
              return (
                <React.Fragment key={idx}>
                  <tr className={`parent-row ${isExpanded ? 'expanded' : ''} ${hasChildren ? 'clickable' : ''}`} onClick={() => hasChildren && toggleItem(grade.name)}>
                    <td className="col-rank">{idx + 1}</td>
                    <td className="col-name parent">
                      {hasChildren && <span className="expand-arrow">{isExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text">{grade.name}</span>
                      {hasChildren && <span className="child-count">({grade.pack_sizes.length})</span>}
                    </td>
                    <td className="col-bar">
                      <div className="mini-bar"><div className="mini-fill cyan" style={{ width: `${pct}%` }}></div></div>
                    </td>
                    <td className="col-num green">{formatNumber(grade.cartons)}</td>
                    <td className="col-num purple">{formatLbs(grade.lbs)}</td>
                    <td className="col-pct">{pct}%</td>
                  </tr>
                  {isExpanded && grade.pack_sizes.map((ps, psIdx) => {
                    const psPct = grade.cartons > 0 ? Math.round((ps.cartons / grade.cartons) * 100) : 0;
                    return (
                      <tr key={`ps-${psIdx}`} className="child-row">
                        <td className="col-rank"></td>
                        <td className="col-name child">↳ {ps.name}</td>
                        <td className="col-bar"><div className="mini-bar child"><div className="mini-fill cyan-light" style={{ width: `${psPct}%` }}></div></div></td>
                        <td className="col-num">{formatNumber(ps.cartons)}</td>
                        <td className="col-num">{formatLbs(ps.lbs)}</td>
                        <td className="col-pct">{psPct}%</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Brand → Grade Breakdown - EXPANDABLE TABLE
const BrandGradeBreakdownChart = ({ data, dateLabel }) => {
  const [expandedItems, setExpandedItems] = React.useState({});
  
  // Build Brand → Grade hierarchy
  const brandData = {};
  ['produced', 'repack', 'reprint', 'reprocess'].forEach(process => {
    const brands = data?.[process]?.brands || [];
    brands.forEach(b => {
      if (!brandData[b.name]) {
        brandData[b.name] = { cartons: 0, lbs: 0, grades: {} };
      }
      brandData[b.name].cartons += b.cartons;
      brandData[b.name].lbs += b.lbs;
      
      if (b.grades) {
        b.grades.forEach(g => {
          if (!brandData[b.name].grades[g.name]) {
            brandData[b.name].grades[g.name] = { cartons: 0, lbs: 0 };
          }
          brandData[b.name].grades[g.name].cartons += g.cartons;
          brandData[b.name].grades[g.name].lbs += g.lbs;
        });
      }
    });
  });
  
  const allBrands = Object.entries(brandData)
    .map(([name, values]) => ({
      name,
      cartons: values.cartons,
      lbs: values.lbs,
      grades: Object.entries(values.grades)
        .map(([gName, gValues]) => ({ name: gName, ...gValues }))
        .sort((a, b) => b.cartons - a.cartons)
    }))
    .sort((a, b) => b.cartons - a.cartons);
  
  const grandTotal = allBrands.reduce((sum, b) => sum + (b.cartons || 0), 0);
  const grandTotalLbs = allBrands.reduce((sum, b) => sum + (b.lbs || 0), 0);
  
  const toggleItem = (name) => {
    setExpandedItems(prev => ({ ...prev, [name]: !prev[name] }));
  };

  if (allBrands.length === 0) {
    return <div className="no-chart-data">No brand data for {dateLabel}</div>;
  }
  
  return (
    <div className="expandable-breakdown">
      <div className="breakdown-header amber-gradient">
        <div className="header-left">
          <span className="header-icon">🏷️</span>
          <span className="header-title">BRAND → GRADE BREAKUP ({dateLabel})</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body">
        <table className="expandable-table">
          <thead>
            <tr>
              <th className="col-rank">#</th>
              <th className="col-name">BRAND / GRADE</th>
              <th className="col-bar">DISTRIBUTION</th>
              <th className="col-num">CARTONS</th>
              <th className="col-num">LBS</th>
              <th className="col-pct">%</th>
            </tr>
          </thead>
          <tbody>
            {allBrands.map((brand, idx) => {
              const pct = grandTotal > 0 ? Math.round((brand.cartons / grandTotal) * 100) : 0;
              const isExpanded = expandedItems[brand.name];
              const hasChildren = brand.grades && brand.grades.length > 0;
              return (
                <React.Fragment key={idx}>
                  <tr className={`parent-row ${isExpanded ? 'expanded' : ''} ${hasChildren ? 'clickable' : ''}`} onClick={() => hasChildren && toggleItem(brand.name)}>
                    <td className="col-rank">{idx + 1}</td>
                    <td className="col-name parent">
                      {hasChildren && <span className="expand-arrow">{isExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text">{brand.name}</span>
                      {hasChildren && <span className="child-count">({brand.grades.length})</span>}
                    </td>
                    <td className="col-bar">
                      <div className="mini-bar"><div className="mini-fill amber" style={{ width: `${pct}%` }}></div></div>
                    </td>
                    <td className="col-num green">{formatNumber(brand.cartons)}</td>
                    <td className="col-num purple">{formatLbs(brand.lbs)}</td>
                    <td className="col-pct">{pct}%</td>
                  </tr>
                  {isExpanded && brand.grades.map((grade, gIdx) => {
                    const gPct = brand.cartons > 0 ? Math.round((grade.cartons / brand.cartons) * 100) : 0;
                    return (
                      <tr key={`g-${gIdx}`} className="child-row">
                        <td className="col-rank"></td>
                        <td className="col-name child">↳ {grade.name}</td>
                        <td className="col-bar"><div className="mini-bar child"><div className="mini-fill amber-light" style={{ width: `${gPct}%` }}></div></div></td>
                        <td className="col-num">{formatNumber(grade.cartons)}</td>
                        <td className="col-num">{formatLbs(grade.lbs)}</td>
                        <td className="col-pct">{gPct}%</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const WeeklyProductionTrendChart = ({ data }) => {
  const [selectedDay, setSelectedDay] = React.useState(null);
  
  const chartLabels = data?.map(d => `${d.day}\n${d.date?.slice(5)}`) || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: 'PRODUCED (CTNS)',
        data: data?.map(d => d.inward) || [0, 0, 0, 0, 0, 0, 0],
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34,197,94,0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 6,
        pointBackgroundColor: '#22c55e',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      },
      {
        label: 'SHIPPED (CTNS)',
        data: data?.map(d => d.shipped || 0) || [0, 0, 0, 0, 0, 0, 0],
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 3,
        pointRadius: 6,
        pointBackgroundColor: '#ef4444',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (elements.length > 0 && data) {
        const index = elements[0].index;
        setSelectedDay(selectedDay === index ? null : index);
      }
    },
    plugins: {
      legend: { 
        position: 'top', 
        labels: { usePointStyle: true, padding: 15, font: { size: 11, weight: '600' } } 
      },
      datalabels: { display: false },
      tooltip: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleFont: { size: 12, weight: '700' },
        bodyFont: { size: 11, weight: '500' },
        padding: 12,
        cornerRadius: 8
      }
    },
    scales: {
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 10 } } },
      x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 0 } }
    }
  };

  const selectedDayData = selectedDay !== null && data ? data[selectedDay] : null;

  return (
    <div className="weekly-trend-wrapper">
      {/* Line Chart */}
      <div className="weekly-chart-section">
        <div style={{ height: '220px' }}>
          <Line data={chartData} options={options} />
        </div>
      </div>
      
      {/* Detail Breakdown - Shows when a row is clicked */}
      {selectedDayData && (
        <div className="weekly-detail-panel">
          <div className="detail-panel-header">
            <span>📅 {selectedDayData.day} ({selectedDayData.date}) - DETAIL BREAKUP</span>
            <button className="panel-close" onClick={() => setSelectedDay(null)}>✕</button>
          </div>
          <div className="detail-panel-body">
            <div className="detail-card production">
              <div className="card-header">📦 PRODUCTION</div>
              <div className="card-main-value">{formatNumber(selectedDayData.inward)} CTNS</div>
              <div className="card-sub-value">{formatLbs(selectedDayData.inward_lbs)} LBS</div>
              <div className="card-shifts">
                <span className="shift-item">☀️ DAY: {formatNumber(selectedDayData.shift?.day?.cartons || 0)}</span>
                <span className="shift-item">🌙 NIGHT: {formatNumber(selectedDayData.shift?.night?.cartons || 0)}</span>
              </div>
            </div>
            <div className="detail-card shipped">
              <div className="card-header">🚚 SHIPPED</div>
              <div className="card-main-value red">{formatNumber(selectedDayData.shipped)} CTNS</div>
              <div className="card-sub-value">{formatLbs(selectedDayData.shipped_lbs || 0)} LBS</div>
            </div>
            <div className="detail-card products">
              <div className="card-header">📊 TOP PRODUCTS</div>
              <div className="card-list">
                {selectedDayData.products?.slice(0, 5).map((p, i) => (
                  <div key={i} className="list-row">
                    <span className="list-name">{p.name}</span>
                    <span className="list-cartons">{formatNumber(p.cartons)} CTNS</span>
                    <span className="list-lbs">{formatLbs(p.lbs)} LBS</span>
                  </div>
                )) || <div className="no-data">No products</div>}
              </div>
            </div>
            <div className="detail-card grades">
              <div className="card-header">🏷️ TOP GRADES</div>
              <div className="card-list">
                {selectedDayData.grades?.slice(0, 5).map((g, i) => (
                  <div key={i} className="list-row">
                    <span className="list-name">{g.name}</span>
                    <span className="list-cartons">{formatNumber(g.cartons)} CTNS</span>
                    <span className="list-lbs">{formatLbs(g.lbs)} LBS</span>
                  </div>
                )) || <div className="no-data">No grades</div>}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Weekly Data Table */}
      <div className="weekly-table-section">
        <table className="weekly-table">
          <thead>
            <tr>
              <th>DAY</th>
              <th>DATE</th>
              <th>PRODUCED</th>
              <th>LBS</th>
              <th>SHIPPED</th>
              <th>DAY</th>
              <th>NIGHT</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((d, i) => (
              <tr 
                key={i} 
                className={selectedDay === i ? 'selected' : ''} 
                onClick={() => setSelectedDay(selectedDay === i ? null : i)}
              >
                <td className="col-day">{d.day}</td>
                <td className="col-date">{d.date?.slice(5)}</td>
                <td className="col-produced">{formatNumber(d.inward)}</td>
                <td className="col-lbs">{formatLbs(d.inward_lbs)}</td>
                <td className="col-shipped">{formatNumber(d.shipped)}</td>
                <td className="col-shift">{formatNumber(d.shift?.day?.cartons || 0)}</td>
                <td className="col-shift">{formatNumber(d.shift?.night?.cartons || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===================== MONTHLY COMPARISON COMPONENT =====================
const MonthlyComparisonCard = ({ currentMonth, previousMonth }) => {
  if (!currentMonth) return null;
  
  return (
    <div className="monthly-comparison-card">
      <div className="monthly-header">
        <span>📅 MONTHLY COMPARISON</span>
      </div>
      <div className="monthly-grid-2col">
        <div className="month-card current">
          <div className="month-title">{currentMonth?.month_name} {currentMonth?.year}</div>
          <div className="month-label">Current Month</div>
          <div className="month-stats">
            <div className="stat-row header">
              <span className="stat-label"></span>
              <span className="stat-ctns">CARTONS</span>
              <span className="stat-lbs">LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Total Production</span>
              <span className="stat-ctns">{formatNumber(currentMonth?.total_cartons || 0)}</span>
              <span className="stat-lbs">{formatLbs(currentMonth?.total_lbs || 0)} LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Produced</span>
              <span className="stat-ctns green">{formatNumber(currentMonth?.produced?.cartons || 0)}</span>
              <span className="stat-lbs green">{formatLbs(currentMonth?.produced?.lbs || 0)} LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Re-Pack</span>
              <span className="stat-ctns blue">{formatNumber(currentMonth?.repack?.cartons || 0)}</span>
              <span className="stat-lbs blue">{formatLbs(currentMonth?.repack?.lbs || 0)} LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Shipped</span>
              <span className="stat-ctns red">{formatNumber(currentMonth?.shipped?.cartons || 0)}</span>
              <span className="stat-lbs red">{formatLbs(currentMonth?.shipped?.lbs || 0)} LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Working Days</span>
              <span className="stat-ctns">{currentMonth?.working_days || 0}</span>
              <span className="stat-lbs">-</span>
            </div>
          </div>
        </div>
        
        <div className="month-card previous">
          <div className="month-title">{previousMonth?.month_name} {previousMonth?.year}</div>
          <div className="month-label">Previous Month</div>
          <div className="month-stats">
            <div className="stat-row header">
              <span className="stat-label"></span>
              <span className="stat-ctns">CARTONS</span>
              <span className="stat-lbs">LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Total Production</span>
              <span className="stat-ctns">{formatNumber(previousMonth?.total_cartons || 0)}</span>
              <span className="stat-lbs">{formatLbs(previousMonth?.total_lbs || 0)} LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Produced</span>
              <span className="stat-ctns green">{formatNumber(previousMonth?.produced?.cartons || 0)}</span>
              <span className="stat-lbs green">{formatLbs(previousMonth?.produced?.lbs || 0)} LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Re-Pack</span>
              <span className="stat-ctns blue">{formatNumber(previousMonth?.repack?.cartons || 0)}</span>
              <span className="stat-lbs blue">{formatLbs(previousMonth?.repack?.lbs || 0)} LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Shipped</span>
              <span className="stat-ctns red">{formatNumber(previousMonth?.shipped?.cartons || 0)}</span>
              <span className="stat-lbs red">{formatLbs(previousMonth?.shipped?.lbs || 0)} LBS</span>
            </div>
            <div className="stat-row">
              <span className="stat-label">Working Days</span>
              <span className="stat-ctns">{previousMonth?.working_days || 0}</span>
              <span className="stat-lbs">-</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Top Products Comparison */}
      <div className="monthly-breakdown-grid">
        <div className="breakdown-column">
          <div className="breakdown-title">🏆 Top Products - {currentMonth?.month_name}</div>
          <div className="breakdown-table">
            <div className="breakdown-header">
              <span className="col-rank">#</span>
              <span className="col-name">Product</span>
              <span className="col-ctns">CARTONS</span>
              <span className="col-lbs">LBS</span>
            </div>
            {currentMonth?.products?.slice(0, 5).map((p, i) => (
              <div key={i} className="breakdown-row">
                <span className="col-rank">{i+1}</span>
                <span className="col-name">{p.name}</span>
                <span className="col-ctns">{formatNumber(p.cartons)}</span>
                <span className="col-lbs">{formatLbs(p.lbs)} LBS</span>
              </div>
            ))}
          </div>
        </div>
        <div className="breakdown-column">
          <div className="breakdown-title">🏆 Top Products - {previousMonth?.month_name}</div>
          <div className="breakdown-table">
            <div className="breakdown-header">
              <span className="col-rank">#</span>
              <span className="col-name">Product</span>
              <span className="col-ctns">CARTONS</span>
              <span className="col-lbs">LBS</span>
            </div>
            {previousMonth?.products?.slice(0, 5).map((p, i) => (
              <div key={i} className="breakdown-row">
                <span className="col-rank">{i+1}</span>
                <span className="col-name">{p.name}</span>
                <span className="col-ctns">{formatNumber(p.cartons)}</span>
                <span className="col-lbs">{formatLbs(p.lbs)} LBS</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Top Grades Comparison */}
      <div className="monthly-breakdown-grid">
        <div className="breakdown-column">
          <div className="breakdown-title">🏷️ Top Grades - {currentMonth?.month_name}</div>
          <div className="breakdown-table">
            <div className="breakdown-header">
              <span className="col-rank">#</span>
              <span className="col-name">Grade</span>
              <span className="col-ctns">CARTONS</span>
              <span className="col-lbs">LBS</span>
            </div>
            {currentMonth?.grades?.slice(0, 5).map((g, i) => (
              <div key={i} className="breakdown-row">
                <span className="col-rank">{i+1}</span>
                <span className="col-name">{g.name}</span>
                <span className="col-ctns">{formatNumber(g.cartons)}</span>
                <span className="col-lbs">{formatLbs(g.lbs)} LBS</span>
              </div>
            ))}
          </div>
        </div>
        <div className="breakdown-column">
          <div className="breakdown-title">🏷️ Top Grades - {previousMonth?.month_name}</div>
          <div className="breakdown-table">
            <div className="breakdown-header">
              <span className="col-rank">#</span>
              <span className="col-name">Grade</span>
              <span className="col-ctns">CARTONS</span>
              <span className="col-lbs">LBS</span>
            </div>
            {previousMonth?.grades?.slice(0, 5).map((g, i) => (
              <div key={i} className="breakdown-row">
                <span className="col-rank">{i+1}</span>
                <span className="col-name">{g.name}</span>
                <span className="col-ctns">{formatNumber(g.cartons)}</span>
                <span className="col-lbs">{formatLbs(g.lbs)} LBS</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== YTD SUMMARY COMPONENT =====================
const YTDSummaryCard = ({ ytd, lastYearYtd }) => {
  if (!ytd) return null;
  
  const calcYoYChange = (curr, prev) => {
    if (!prev || prev === 0) return 0;
    return (((curr - prev) / prev) * 100).toFixed(1);
  };
  
  return (
    <div className="ytd-summary-card">
      <div className="ytd-header">
        <span>📊 YEAR TO DATE ({ytd?.year})</span>
      </div>
      
      <div className="ytd-totals-grid">
        <div className="ytd-total-card">
          <div className="ytd-total-label">Total Production</div>
          <div className="ytd-total-value">{formatNumber(ytd?.total_cartons || 0)}</div>
          <div className="ytd-total-sub">CARTONS</div>
          {lastYearYtd && (
            <div className={`ytd-change ${ytd?.total_cartons >= lastYearYtd?.total_cartons ? 'positive' : 'negative'}`}>
              vs {lastYearYtd?.year}: {calcYoYChange(ytd?.total_cartons, lastYearYtd?.total_cartons)}%
            </div>
          )}
        </div>
        <div className="ytd-total-card">
          <div className="ytd-total-label">Total Weight</div>
          <div className="ytd-total-value">{formatLbs(ytd?.total_lbs || 0)}</div>
          <div className="ytd-total-sub">LBS</div>
        </div>
        <div className="ytd-total-card green">
          <div className="ytd-total-label">Produced</div>
          <div className="ytd-total-value">{formatNumber(ytd?.produced?.cartons || 0)}</div>
          <div className="ytd-total-sub">{formatLbs(ytd?.produced?.lbs || 0)} LBS</div>
        </div>
        <div className="ytd-total-card blue">
          <div className="ytd-total-label">Re-Pack</div>
          <div className="ytd-total-value">{formatNumber(ytd?.repack?.cartons || 0)}</div>
          <div className="ytd-total-sub">{formatLbs(ytd?.repack?.lbs || 0)} LBS</div>
        </div>
        <div className="ytd-total-card red">
          <div className="ytd-total-label">Shipped</div>
          <div className="ytd-total-value">{formatNumber(ytd?.shipped?.cartons || 0)}</div>
          <div className="ytd-total-sub">{formatLbs(ytd?.shipped?.lbs || 0)} LBS</div>
        </div>
      </div>
      
      {/* YTD Products and Grades */}
      <div className="ytd-breakdown-grid">
        <div className="ytd-breakdown-column">
          <div className="breakdown-title">🏆 YTD Top Products</div>
          <div className="breakdown-table">
            <div className="breakdown-header">
              <span className="col-rank">#</span>
              <span className="col-name">Product</span>
              <span className="col-ctns">CARTONS</span>
              <span className="col-lbs">LBS</span>
            </div>
            {ytd?.products?.slice(0, 5).map((p, i) => (
              <div key={i} className="breakdown-row">
                <span className="col-rank">{i+1}</span>
                <span className="col-name">{p.name}</span>
                <span className="col-ctns green">{formatNumber(p.cartons)}</span>
                <span className="col-lbs">{formatLbs(p.lbs)} LBS</span>
              </div>
            ))}
          </div>
        </div>
        <div className="ytd-breakdown-column">
          <div className="breakdown-title">🏷️ YTD Top Grades</div>
          <div className="breakdown-table">
            <div className="breakdown-header">
              <span className="col-rank">#</span>
              <span className="col-name">Grade</span>
              <span className="col-ctns">CARTONS</span>
              <span className="col-lbs">LBS</span>
            </div>
            {ytd?.grades?.slice(0, 5).map((g, i) => (
              <div key={i} className="breakdown-row">
                <span className="col-rank">{i+1}</span>
                <span className="col-name">{g.name}</span>
                <span className="col-ctns blue">{formatNumber(g.cartons)}</span>
                <span className="col-lbs">{formatLbs(g.lbs)} LBS</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== SHIPMENT ANALYSIS TAB =====================
const ShipmentTab = ({ data }) => {
  const [periodFilter, setPeriodFilter] = React.useState('monthly');
  const [viewMode, setViewMode] = React.useState('data'); // 'graphical' or 'data'
  
  if (!data) return null;
  
  const { today_shipment, yesterday_shipment, mtd_shipment, weekly_trend, current_month, ytd, previous_month, shipment_analysis } = data;
  
  // Get shipment data based on period filter
  const getShipmentData = () => {
    const analysis = shipment_analysis || {};
    
    switch (periodFilter) {
      case 'today':
        return {
          label: 'TODAY SHIPMENT',
          date: data.dates?.today,
          total_cartons: today_shipment?.total_cartons || 0,
          total_lbs: today_shipment?.total_lbs || 0,
          products: analysis.breakup_today || today_shipment?.products || [],
          brands: today_shipment?.brands || [],
          grades: today_shipment?.grades || [],
          top5: analysis.top5_today || { products: [], brands: [], grades: [] }
        };
      case 'yesterday':
        return {
          label: 'YESTERDAY SHIPMENT',
          date: data.dates?.yesterday,
          total_cartons: yesterday_shipment?.total_cartons || 0,
          total_lbs: yesterday_shipment?.total_lbs || 0,
          products: analysis.breakup_yesterday || yesterday_shipment?.products || [],
          brands: yesterday_shipment?.brands || [],
          grades: yesterday_shipment?.grades || [],
          top5: analysis.top5_yesterday || { products: [], brands: [], grades: [] }
        };
      case 'weekly':
        const weeklyTotal = (weekly_trend || []).reduce((sum, d) => sum + (d.shipped || 0), 0);
        const weeklyLbs = (weekly_trend || []).reduce((sum, d) => sum + (d.shipped_lbs || 0), 0);
        return {
          label: 'WEEKLY SHIPMENT (Last 7 Days)',
          total_cartons: weeklyTotal,
          total_lbs: weeklyLbs,
          products: analysis.breakup_weekly || [],
          brands: [],
          grades: [],
          weekly_data: weekly_trend,
          daily_trend: analysis.daily_trend,
          top5: analysis.top5_weekly || { products: [], brands: [], grades: [] }
        };
      case 'monthly':
        return {
          label: `${current_month?.month_name || 'FEBRUARY'} SHIPMENT`,
          total_cartons: mtd_shipment?.cartons || current_month?.shipped?.cartons || 0,
          total_lbs: mtd_shipment?.lbs || current_month?.shipped?.lbs || 0,
          pallets: mtd_shipment?.pallets || 0,
          products: analysis.breakup_monthly || mtd_shipment?.products || [],
          brands: mtd_shipment?.brands || [],
          grades: mtd_shipment?.grades || [],
          last_month: previous_month?.shipped || {},
          last_month_name: previous_month?.month_name || 'JAN',
          top5: analysis.top5_monthly || { products: [], brands: [], grades: [] },
          weekly_trend: analysis.weekly_trend,
          monthly_trend: analysis.monthly_trend
        };
      case 'ytd':
        return {
          label: `YTD ${ytd?.year || new Date().getFullYear()} SHIPMENT`,
          total_cartons: ytd?.shipped?.cartons || 0,
          total_lbs: ytd?.shipped?.lbs || 0,
          products: analysis.breakup_ytd || ytd?.shipped?.products || [],
          brands: ytd?.shipped?.brands || [],
          grades: ytd?.shipped?.grades || [],
          monthly_breakdown: ytd?.monthly_breakdown,
          monthly_trend: analysis.monthly_trend,
          year_comparison: analysis.year_comparison,
          top5: analysis.top5_ytd || { products: [], brands: [], grades: [] }
        };
      default:
        return { label: 'SHIPMENT', total_cartons: 0, total_lbs: 0, products: [], brands: [], grades: [], top5: { products: [], brands: [], grades: [] } };
    }
  };
  
  const shipmentData = getShipmentData();
  
  // Build Product → Grade hierarchy from breakup data
  const buildProductGradeData = () => {
    const products = shipmentData.products || [];
    return products.map(p => ({
      name: p.name,
      cartons: p.cartons || 0,
      lbs: p.lbs || 0,
      grades: p.grades || [],
      brands: p.brands || []
    })).sort((a, b) => b.cartons - a.cartons);
  };
  
  // Build Brand → Grade hierarchy
  const buildBrandGradeData = () => {
    const products = shipmentData.products || [];
    const brandMap = {};
    
    products.forEach(p => {
      (p.brands || []).forEach(b => {
        if (!brandMap[b.name]) {
          brandMap[b.name] = { name: b.name, cartons: 0, lbs: 0, grades: [] };
        }
        brandMap[b.name].cartons += b.cartons || 0;
        brandMap[b.name].lbs += b.lbs || 0;
      });
    });
    
    return Object.values(brandMap).sort((a, b) => b.cartons - a.cartons);
  };
  
  // Build Grade → Pack Size hierarchy
  const buildGradePackSizeData = () => {
    const products = shipmentData.products || [];
    const gradeMap = {};
    
    products.forEach(p => {
      (p.grades || []).forEach(g => {
        if (!gradeMap[g.name]) {
          gradeMap[g.name] = { name: g.name, cartons: 0, lbs: 0, pack_sizes: [] };
        }
        gradeMap[g.name].cartons += g.cartons || 0;
        gradeMap[g.name].lbs += g.lbs || 0;
        if (g.pack_sizes) {
          g.pack_sizes.forEach(ps => {
            const existing = gradeMap[g.name].pack_sizes.find(x => x.name === ps.name);
            if (existing) {
              existing.cartons += ps.cartons || 0;
              existing.lbs += ps.lbs || 0;
            } else {
              gradeMap[g.name].pack_sizes.push({ ...ps });
            }
          });
        }
      });
    });
    
    return Object.values(gradeMap).sort((a, b) => b.cartons - a.cartons);
  };

  const productData = buildProductGradeData();
  const brandData = buildBrandGradeData();
  const gradeData = buildGradePackSizeData();
  const top5 = shipmentData.top5 || { products: [], brands: [], grades: [] };

  // Excel Export Handler
  const handleExcelExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE_URL}/coldstore_dashboard_api.php?action=export_shipments&period=${periodFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const result = await response.json();
      
      if (result.success) {
        // Convert to CSV and download
        let csv = 'SHIPMENT EXPORT REPORT\n';
        csv += `Period: ${periodFilter.toUpperCase()}\n`;
        csv += `Total Records: ${result.summary?.total_records || 0}\n`;
        csv += `Total Cartons: ${result.summary?.total_cartons || 0}\n`;
        csv += `Total LBS: ${result.summary?.total_lbs || 0}\n`;
        csv += `Total Pallets: ${result.summary?.total_pallets || 0}\n\n`;
        
        csv += 'DATE,FCL MONTH,FCL NO,PRODUCT,BRAND,GRADE,PACK SIZE,PACK COUNT,PACK WEIGHT,PACK UNIT,CARTONS,LOOSE BAGS,LBS,PALLET ID,NAV ID,TRACE ID,DAY CODE,LOT NO\n';
        
        (result.data || []).forEach(row => {
          csv += `${row.date || ''},${row.fcl_month || ''},${row.fcl_no || ''},${row.product || ''},${row.brand || ''},${row.grade || ''},${row.pack_size || ''},${row.pack_count || ''},${row.pack_weight || ''},${row.pack_unit || ''},${row.cartons || 0},${row.loose_bags || 0},${row.lbs || 0},${row.pallet_id || ''},${row.nav_id || ''},${row.trace_id || ''},${row.day_code || ''},${row.lot_no || ''}\n`;
        });
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Shipment_${periodFilter}_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
      }
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  return (
    <div className="shipment-tab">
      <div className="section-title" style={{ marginBottom: '16px' }}>
        <div className="section-icon orange">🚚</div>
        <span>SHIPMENT ANALYSIS</span>
        <button className="export-excel-btn" onClick={handleExcelExport}>
          📥 Export Excel
        </button>
      </div>

      {/* Period Filter Tabs */}
      <div className="shipment-period-tabs">
        <button className={`period-tab ${periodFilter === 'today' ? 'active' : ''}`} onClick={() => setPeriodFilter('today')}>
          📅 Today
        </button>
        <button className={`period-tab ${periodFilter === 'yesterday' ? 'active' : ''}`} onClick={() => setPeriodFilter('yesterday')}>
          📆 Yesterday
        </button>
        <button className={`period-tab ${periodFilter === 'weekly' ? 'active' : ''}`} onClick={() => setPeriodFilter('weekly')}>
          📊 Weekly
        </button>
        <button className={`period-tab ${periodFilter === 'monthly' ? 'active' : ''}`} onClick={() => setPeriodFilter('monthly')}>
          🗓️ Monthly
        </button>
        <button className={`period-tab ${periodFilter === 'ytd' ? 'active' : ''}`} onClick={() => setPeriodFilter('ytd')}>
          📈 YTD
        </button>
      </div>

      {/* Summary Banner */}
      <div className="shipment-summary-banner">
        <div className="shipment-summary-main">
          <div className="shipment-summary-title">{shipmentData.label}</div>
          <div className="shipment-summary-value">{formatNumber(shipmentData.total_cartons)}</div>
          <div className="shipment-summary-lbs">{formatLbs(shipmentData.total_lbs)} LBS</div>
        </div>
        {/* Last Month Comparison for Monthly view */}
        {periodFilter === 'monthly' && shipmentData.last_month && (
          <div className="shipment-comparison-card">
            <div className="comparison-label">{shipmentData.last_month_name} 2026</div>
            <div className="comparison-value">{formatNumber(shipmentData.last_month.cartons || 0)}</div>
            <div className="comparison-lbs">{formatLbs(shipmentData.last_month.lbs || 0)} LBS</div>
          </div>
        )}
      </div>

      {/* Product Distribution Chart (for Today/Yesterday) */}
      {(periodFilter === 'today' || periodFilter === 'yesterday') && productData.length > 0 && (
        <div className="shipment-trend-section">
          <h3 className="trend-title">📊 PRODUCT DISTRIBUTION</h3>
          <div className="product-distribution-chart">
            {productData.slice(0, 6).map((product, idx) => {
              const maxVal = Math.max(...productData.map(p => p.cartons || 1));
              const widthPct = Math.max(5, ((product.cartons || 0) / maxVal) * 100);
              const totalPct = shipmentData.total_cartons > 0 ? Math.round((product.cartons / shipmentData.total_cartons) * 100) : 0;
              return (
                <div key={idx} className="product-bar-row">
                  <div className="product-bar-name">{product.name}</div>
                  <div className="product-bar-track">
                    <div className="product-bar-fill has-tooltip" style={{ width: `${widthPct}%` }}>
                      <div className="chart-tooltip">
                        <div className="tooltip-single">
                          <div className="tooltip-title">{product.name}</div>
                          <div className="tooltip-stats">
                            <span className="stat-label">Cartons:</span>
                            <span className="stat-value">{formatNumber(product.cartons)}</span>
                          </div>
                          <div className="tooltip-stats">
                            <span className="stat-label"></span>
                            <span className="stat-value">{formatLbs(product.lbs)} LBS</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="product-bar-value">{formatNumber(product.cartons)} ({totalPct}%)</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Daily Shipment Trend (for Weekly view) */}
      {periodFilter === 'weekly' && (
        <div className="shipment-trend-section">
          <h3 className="trend-title">📊 DAILY SHIPMENT TREND (LAST 7 DAYS)</h3>
          <div className="daily-trend-chart">
            {(shipmentData.daily_trend?.current_week || weekly_trend || []).map((day, idx) => {
              const maxVal = Math.max(...(shipmentData.daily_trend?.current_week || weekly_trend || []).map(d => d.cartons || d.shipped || 1));
              const val = day.cartons || day.shipped || 0;
              const lbs = day.lbs || day.shipped_lbs || 0;
              const pallets = day.pallets || 0;
              const heightPct = Math.max(5, (val / maxVal) * 100);
              return (
                <div key={idx} className="trend-bar-col">
                  <div className="trend-bar-value">{formatNumber(val)}</div>
                  <div className="trend-bar-track">
                    <div className="trend-bar-fill has-tooltip" style={{ height: `${heightPct}%` }}>
                      <div className="chart-tooltip">
                        <div className="tooltip-single">
                          <div className="tooltip-title">{day.day_name || day.day} - SHIPPED</div>
                          <div className="tooltip-stats">
                            <span className="stat-label">Cartons:</span>
                            <span className="stat-value">{formatNumber(val)}</span>
                          </div>
                          <div className="tooltip-stats">
                            <span className="stat-label"></span>
                            <span className="stat-value">{formatLbs(lbs)} LBS</span>
                          </div>
                          <div className="tooltip-stats">
                            <span className="stat-label">Pallets:</span>
                            <span className="stat-value">{formatNumber(pallets)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="trend-bar-label">{day.day_name || day.day}</div>
                  <div className="trend-bar-date">{day.date?.slice(5) || ''}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Weekly Trend Chart (for Monthly view) - Week 1-4 comparison */}
      {periodFilter === 'monthly' && shipmentData.weekly_trend && (
        <div className="shipment-trend-section">
          <h3 className="trend-title">📊 WEEKLY SHIPMENT TREND (Current vs Last Month)</h3>
          <div className="weekly-comparison-chart">
            <div className="week-comparison-legend">
              <span className="legend-item current">● {shipmentData.weekly_trend?.current_month?.name || 'Current'}</span>
              <span className="legend-item last">● {shipmentData.weekly_trend?.last_month?.name || 'Last'}</span>
            </div>
            <div className="week-bars-container">
              {(shipmentData.weekly_trend?.current_month?.weeks || []).map((week, idx) => {
                const lastWeek = shipmentData.weekly_trend?.last_month?.weeks?.[idx] || {};
                const maxVal = Math.max(
                  ...((shipmentData.weekly_trend?.current_month?.weeks || []).map(w => w.cartons || 0)),
                  ...((shipmentData.weekly_trend?.last_month?.weeks || []).map(w => w.cartons || 0)),
                  1
                );
                const currentPct = Math.max(5, ((week.cartons || 0) / maxVal) * 100);
                const lastPct = Math.max(5, ((lastWeek.cartons || 0) / maxVal) * 100);
                const currentMonthName = shipmentData.weekly_trend?.current_month?.name || 'Current';
                const lastMonthName = shipmentData.weekly_trend?.last_month?.name || 'Last';
                return (
                  <div key={idx} className="week-comparison-col">
                    <div className="week-bars">
                      <div className="week-bar-pair">
                        <div className="week-bar current has-tooltip" style={{ height: `${currentPct}%` }}>
                          <span className="bar-value">{formatNumber(week.cartons || 0)}</span>
                          <div className="chart-tooltip">
                            <div className="tooltip-single">
                              <div className="tooltip-title">WEEK {week.week} ({currentMonthName})</div>
                              <div className="tooltip-stats">
                                <span className="stat-label">Cartons:</span>
                                <span className="stat-value">{formatNumber(week.cartons || 0)}</span>
                              </div>
                              <div className="tooltip-stats">
                                <span className="stat-label"></span>
                                <span className="stat-value">{formatLbs(week.lbs || 0)} LBS</span>
                              </div>
                              <div className="tooltip-stats">
                                <span className="stat-label">Pallets:</span>
                                <span className="stat-value">{formatNumber(week.pallets || 0)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="week-bar last has-tooltip" style={{ height: `${lastPct}%` }}>
                          <span className="bar-value">{formatNumber(lastWeek.cartons || 0)}</span>
                          <div className="chart-tooltip">
                            <div className="tooltip-single">
                              <div className="tooltip-title">WEEK {week.week} ({lastMonthName})</div>
                              <div className="tooltip-stats">
                                <span className="stat-label">Cartons:</span>
                                <span className="stat-value">{formatNumber(lastWeek.cartons || 0)}</span>
                              </div>
                              <div className="tooltip-stats">
                                <span className="stat-label"></span>
                                <span className="stat-value">{formatLbs(lastWeek.lbs || 0)} LBS</span>
                              </div>
                              <div className="tooltip-stats">
                                <span className="stat-label">Pallets:</span>
                                <span className="stat-value">{formatNumber(lastWeek.pallets || 0)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="week-label">Week {week.week}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Monthly Shipment Trend (for YTD view) */}
      {periodFilter === 'ytd' && shipmentData.monthly_trend && (
        <div className="shipment-trend-section">
          <h3 className="trend-title">📊 MONTHLY SHIPMENT TREND</h3>
          <div className="monthly-trend-chart">
            {shipmentData.monthly_trend.map((month, idx) => {
              const maxVal = Math.max(...shipmentData.monthly_trend.map(m => m.cartons || 1));
              const heightPct = Math.max(5, ((month.cartons || 0) / maxVal) * 100);
              return (
                <div key={idx} className="trend-bar-col">
                  <div className="trend-bar-value">{formatNumber(month.cartons || 0)}</div>
                  <div className="trend-bar-track">
                    <div className="trend-bar-fill monthly has-tooltip" style={{ height: `${heightPct}%` }}>
                      <div className="chart-tooltip">
                        <div className="tooltip-single">
                          <div className="tooltip-title">{month.month} - SHIPPED</div>
                          <div className="tooltip-stats">
                            <span className="stat-label">Cartons:</span>
                            <span className="stat-value">{formatNumber(month.cartons || 0)}</span>
                          </div>
                          <div className="tooltip-stats">
                            <span className="stat-label"></span>
                            <span className="stat-value">{formatLbs(month.lbs || 0)} LBS</span>
                          </div>
                          <div className="tooltip-stats">
                            <span className="stat-label">Pallets:</span>
                            <span className="stat-value">{formatNumber(month.pallets || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="trend-bar-label">{month.month}</div>
                </div>
              );
            })}
          </div>
          
          {/* Monthly Breakdown Table */}
          <div className="monthly-breakdown-table">
            <div className="monthly-table-header">
              <span className="col-month">MONTH</span>
              <span className="col-num">SHIPPED CARTONS</span>
              <span className="col-num">SHIPPED LBS</span>
            </div>
            {shipmentData.monthly_trend.map((month, idx) => (
              <div key={idx} className="monthly-table-row">
                <span className="col-month">{month.month}</span>
                <span className="col-num">{formatNumber(month.cartons || 0)}</span>
                <span className="col-num">{formatLbs(month.lbs || 0)} LBS</span>
              </div>
            ))}
            <div className="monthly-table-row total">
              <span className="col-month">TOTAL</span>
              <span className="col-num">{formatNumber(shipmentData.total_cartons)}</span>
              <span className="col-num">{formatLbs(shipmentData.total_lbs)} LBS</span>
            </div>
          </div>
        </div>
      )}

      {/* Top 5 Section */}
      <div className="top5-section">
        <div className="top5-grid">
          {/* Top 5 Products */}
          <div className="top5-card">
            <h4>🏆 Top 5 Products</h4>
            <div className="top5-list">
              {(top5.products || []).slice(0, 5).map((item, idx) => (
                <div key={idx} className="top5-item">
                  <span className="top5-rank">{idx + 1}</span>
                  <span className="top5-name">{item.name}</span>
                  <span className="top5-value">{formatNumber(item.cartons)}</span>
                </div>
              ))}
              {(!top5.products || top5.products.length === 0) && (
                <div className="no-data-small">No data</div>
              )}
            </div>
          </div>
          
          {/* Top 5 Brands */}
          <div className="top5-card">
            <h4>🏅 Top 5 Brands</h4>
            <div className="top5-list">
              {(top5.brands || []).slice(0, 5).map((item, idx) => (
                <div key={idx} className="top5-item">
                  <span className="top5-rank">{idx + 1}</span>
                  <span className="top5-name">{item.name}</span>
                  <span className="top5-value">{formatNumber(item.cartons)}</span>
                </div>
              ))}
              {(!top5.brands || top5.brands.length === 0) && (
                <div className="no-data-small">No data</div>
              )}
            </div>
          </div>
          
          {/* Top 5 Grades */}
          <div className="top5-card">
            <h4>🎯 Top 5 Grades</h4>
            <div className="top5-list">
              {(top5.grades || []).slice(0, 5).map((item, idx) => (
                <div key={idx} className="top5-item">
                  <span className="top5-rank">{idx + 1}</span>
                  <span className="top5-name">{item.name}</span>
                  <span className="top5-value">{formatNumber(item.cartons)}</span>
                </div>
              ))}
              {(!top5.grades || top5.grades.length === 0) && (
                <div className="no-data-small">No data</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Breakup Tables Section */}
      <div className="shipment-breakup-section">
        <div className="shipment-data-grid">
          {/* Product → Grade Breakup */}
          <ShipmentBreakupTable 
            title="PRODUCT — GRADE BREAKUP"
            data={productData}
            headerBg="purple"
            parentLabel="PRODUCT / GRADE"
            totalCartons={shipmentData.total_cartons}
            totalLbs={shipmentData.total_lbs}
            itemCount={productData.length}
            itemLabel="PRODUCTS"
          />

          {/* Brand → Grade Breakup */}
          <ShipmentBreakupTable 
            title="BRAND — GRADE BREAKUP"
            data={brandData}
            headerBg="orange"
            parentLabel="BRAND / GRADE"
            totalCartons={shipmentData.total_cartons}
            totalLbs={shipmentData.total_lbs}
            itemCount={brandData.length}
            itemLabel="BRANDS"
          />
        </div>

        {/* Grade → Pack Size Breakup */}
        <div className="grade-packsize-section">
          <GradePackSizeBreakupTable 
            title="GRADE — PACK SIZE BREAKUP"
            data={gradeData}
            totalCartons={shipmentData.total_cartons}
            totalLbs={shipmentData.total_lbs}
          />
        </div>
      </div>
    </div>
  );
};

// Shipment Breakup Table Component (Enhanced for filter-based data)
const ShipmentBreakupTable = ({ title, data, headerBg, parentLabel, totalCartons, totalLbs, itemCount, itemLabel }) => {
  const [expandedItem, setExpandedItem] = React.useState(null);
  
  const tableTotalCartons = data.reduce((sum, item) => sum + (item.cartons || 0), 0);
  const tableTotalLbs = data.reduce((sum, item) => sum + (item.lbs || 0), 0);
  
  return (
    <div className="shipment-breakup-table">
      <div className="breakup-table-title">
        <span className={`title-icon ${headerBg}`}>🏷️</span>
        <span>{title}</span>
        <span className="title-stats">TOTAL: {formatNumber(tableTotalCartons)} CTNS {formatLbs(tableTotalLbs)} LBS ({itemCount} {itemLabel})</span>
      </div>
      <div className={`breakup-table-header ${headerBg}`}>
        <span className="col-name">{parentLabel}</span>
        <span className="col-progress">PROGRESS</span>
        <span className="col-num">CARTONS</span>
        <span className="col-num">LBS</span>
        <span className="col-pct">%</span>
      </div>
      <div className="breakup-table-body">
        {data.length === 0 ? (
          <div className="no-data-row">NO {itemLabel} DATA AVAILABLE</div>
        ) : (
          <>
            {data.map((item, idx) => {
              const pct = totalCartons > 0 ? Math.round((item.cartons / totalCartons) * 100) : 0;
              const hasChildren = (item.grades && item.grades.length > 0);
              return (
                <React.Fragment key={idx}>
                  <div 
                    className={`breakup-row parent ${expandedItem === item.name ? 'expanded' : ''} ${hasChildren ? 'clickable' : ''}`}
                    onClick={() => hasChildren && setExpandedItem(expandedItem === item.name ? null : item.name)}
                  >
                    <span className="col-name">
                      {hasChildren && <span className="expand-arrow">{expandedItem === item.name ? '▼' : '▶'}</span>}
                      {item.name}
                    </span>
                    <span className="col-progress">
                      <div className="progress-bar-mini">
                        <div className={`progress-fill-mini ${headerBg}`} style={{ width: `${pct}%` }}></div>
                      </div>
                    </span>
                    <span className="col-num">{formatNumber(item.cartons)}</span>
                    <span className="col-num lbs">{formatLbs(item.lbs)} LBS</span>
                    <span className="col-pct">{pct}%</span>
                  </div>
                  {expandedItem === item.name && item.grades && item.grades.length > 0 && (
                    <div className="breakup-children">
                      {item.grades.map((grade, gIdx) => {
                        const gradePct = item.cartons > 0 ? Math.round((grade.cartons / item.cartons) * 100) : 0;
                        return (
                          <div key={gIdx} className="breakup-row child">
                            <span className="col-name">↳ {grade.name}</span>
                            <span className="col-progress">
                              <div className="progress-bar-mini">
                                <div className="progress-fill-mini cyan" style={{ width: `${gradePct}%` }}></div>
                              </div>
                            </span>
                            <span className="col-num">{formatNumber(grade.cartons)}</span>
                            <span className="col-num lbs">{formatLbs(grade.lbs)} LBS</span>
                            <span className="col-pct">{gradePct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            <div className="breakup-row total">
              <span className="col-name">TOTAL ({data.length} {itemLabel})</span>
              <span className="col-progress"></span>
              <span className="col-num">{formatNumber(tableTotalCartons)}</span>
              <span className="col-num lbs">{formatLbs(tableTotalLbs)} LBS</span>
              <span className="col-pct">100%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Grade → Pack Size Breakup Table
const GradePackSizeBreakupTable = ({ title, data, totalCartons, totalLbs }) => {
  const [expandedItem, setExpandedItem] = React.useState(null);
  
  const tableTotalCartons = data.reduce((sum, item) => sum + (item.cartons || 0), 0);
  const tableTotalLbs = data.reduce((sum, item) => sum + (item.lbs || 0), 0);
  
  return (
    <div className="shipment-breakup-table grade-packsize">
      <div className="breakup-table-title">
        <span className="title-icon cyan">📦</span>
        <span>{title}</span>
        <span className="title-stats">TOTAL: {formatNumber(tableTotalCartons)} CTNS {formatLbs(tableTotalLbs)} LBS ({data.length} GRADES)</span>
      </div>
      <div className="breakup-table-header cyan">
        <span className="col-name">GRADE / PACK SIZE</span>
        <span className="col-progress">PROGRESS</span>
        <span className="col-num">CARTONS</span>
        <span className="col-num">LBS</span>
        <span className="col-pct">%</span>
      </div>
      <div className="breakup-table-body">
        {data.length === 0 ? (
          <div className="no-data-row">NO GRADE DATA AVAILABLE</div>
        ) : (
          <>
            {data.map((item, idx) => {
              const pct = totalCartons > 0 ? Math.round((item.cartons / totalCartons) * 100) : 0;
              const hasChildren = (item.pack_sizes && item.pack_sizes.length > 0);
              return (
                <React.Fragment key={idx}>
                  <div 
                    className={`breakup-row parent ${expandedItem === item.name ? 'expanded' : ''} ${hasChildren ? 'clickable' : ''}`}
                    onClick={() => hasChildren && setExpandedItem(expandedItem === item.name ? null : item.name)}
                  >
                    <span className="col-name">
                      {hasChildren && <span className="expand-arrow">{expandedItem === item.name ? '▼' : '▶'}</span>}
                      {item.name}
                    </span>
                    <span className="col-progress">
                      <div className="progress-bar-mini">
                        <div className="progress-fill-mini cyan" style={{ width: `${pct}%` }}></div>
                      </div>
                    </span>
                    <span className="col-num">{formatNumber(item.cartons)}</span>
                    <span className="col-num lbs">{formatLbs(item.lbs)} LBS</span>
                    <span className="col-pct">{pct}%</span>
                  </div>
                  {expandedItem === item.name && item.pack_sizes && item.pack_sizes.length > 0 && (
                    <div className="breakup-children">
                      {item.pack_sizes.map((ps, psIdx) => {
                        const psPct = item.cartons > 0 ? Math.round((ps.cartons / item.cartons) * 100) : 0;
                        return (
                          <div key={psIdx} className="breakup-row child">
                            <span className="col-name">↳ {ps.name}</span>
                            <span className="col-progress">
                              <div className="progress-bar-mini">
                                <div className="progress-fill-mini purple" style={{ width: `${psPct}%` }}></div>
                              </div>
                            </span>
                            <span className="col-num">{formatNumber(ps.cartons)}</span>
                            <span className="col-num lbs">{formatLbs(ps.lbs)} LBS</span>
                            <span className="col-pct">{psPct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            <div className="breakup-row total">
              <span className="col-name">TOTAL ({data.length} GRADES)</span>
              <span className="col-progress"></span>
              <span className="col-num">{formatNumber(tableTotalCartons)}</span>
              <span className="col-num lbs">{formatLbs(tableTotalLbs)} LBS</span>
              <span className="col-pct">100%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ===================== CURRENT STOCK DISTRIBUTION TABLE (Option D - Two Column Like Reference) =====================
const CurrentStockDistributionTable = ({ data }) => {
  const [expandedGrade, setExpandedGrade] = React.useState(null);
  const [expandedBrand, setExpandedBrand] = React.useState(null);
  
  if (!data) return null;
  
  const products = data.products || [];
  const grades = data.grades || [];
  const brands = data.brands || [];
  const packSizes = data.pack_sizes || [];
  
  const totalCartons = data.cartons || 0;
  const totalLbs = data.lbs || 0;
  const productCount = products.length;
  
  // Sort all arrays by cartons descending
  const sortedProducts = [...products].sort((a, b) => b.cartons - a.cartons);
  const sortedGrades = [...grades].sort((a, b) => b.cartons - a.cartons);
  const sortedBrands = [...brands].sort((a, b) => b.cartons - a.cartons);
  const sortedPackSizes = [...packSizes].sort((a, b) => b.cartons - a.cartons);

  return (
    <div className="stock-dist-container-v2">
      {/* Header Summary Banner */}
      <div className="stock-dist-header-v2">
        <div className="stock-dist-stat">
          <span className="stock-dist-stat-label">PRODUCT</span>
          <span className="stock-dist-stat-value">{productCount}</span>
        </div>
        <div className="stock-dist-stat primary">
          <span className="stock-dist-stat-label">TOTAL CARTONS</span>
          <span className="stock-dist-stat-value">{formatNumber(totalCartons)}</span>
        </div>
        <div className="stock-dist-stat">
          <span className="stock-dist-stat-label">LBS</span>
          <span className="stock-dist-stat-value lbs">{formatLbs(totalLbs)} LBS</span>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="stock-dist-two-col">
        {/* Left Column - Product Table */}
        <div className="stock-dist-panel-v2">
          <div className="stock-dist-panel-header purple">
            <span className="col-name">PRODUCT</span>
            <span className="col-num">CARTONS</span>
            <span className="col-num">LBS</span>
          </div>
          <div className="stock-dist-panel-body">
            {sortedProducts.map((item, idx) => (
              <div key={idx} className="stock-dist-data-row">
                <span className="col-name">{item.name}</span>
                <span className="col-num">{formatNumber(item.cartons)}</span>
                <span className="col-num lbs">{formatLbs(item.lbs)} LBS</span>
              </div>
            ))}
            <div className="stock-dist-data-row total-row">
              <span className="col-name">TOTAL</span>
              <span className="col-num">{formatNumber(totalCartons)}</span>
              <span className="col-num lbs">{formatLbs(totalLbs)} LBS</span>
            </div>
          </div>
        </div>

        {/* Right Column - Grade / Pack Size Table with Expandable Rows */}
        <div className="stock-dist-panel-v2">
          <div className="stock-dist-panel-header cyan">
            <span className="col-name">GRADE / PACK SIZE</span>
            <span className="col-num">CARTONS</span>
            <span className="col-num">LBS</span>
          </div>
          <div className="stock-dist-panel-body scrollable">
            {sortedGrades.map((grade, gIdx) => (
              <React.Fragment key={gIdx}>
                <div 
                  className={`stock-dist-data-row parent-row ${expandedGrade === grade.name ? 'expanded' : ''}`}
                  onClick={() => setExpandedGrade(expandedGrade === grade.name ? null : grade.name)}
                >
                  <span className="col-name">
                    <span className="expand-icon">{expandedGrade === grade.name ? '▼' : '▶'}</span>
                    <strong>{grade.name}</strong>
                  </span>
                  <span className="col-num"><strong>{formatNumber(grade.cartons)}</strong></span>
                  <span className="col-num lbs"><strong>{formatLbs(grade.lbs)}</strong></span>
                </div>
                {expandedGrade === grade.name && (
                  <div className="stock-dist-sub-rows">
                    {sortedPackSizes.slice(0, 3).map((ps, psIdx) => (
                      <div key={psIdx} className="stock-dist-data-row sub-row">
                        <span className="col-name">↳ {ps.name}</span>
                        <span className="col-num">{formatNumber(Math.round(ps.cartons * (grade.cartons / totalCartons)))}</span>
                        <span className="col-num lbs">{formatLbs(Math.round(ps.lbs * (grade.cartons / totalCartons)))} LBS</span>
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Section - Brand Breakdown with Expandable Grades */}
      <div className="stock-dist-brand-section-v2">
        <div className="stock-dist-panel-header orange">
          <span className="col-brand">BRAND</span>
          <span className="col-grade">GRADE / PACK SIZE</span>
          <span className="col-num">LBS</span>
        </div>
        <div className="stock-dist-panel-body">
          {sortedBrands.map((brand, bIdx) => (
            <React.Fragment key={bIdx}>
              <div 
                className={`stock-dist-data-row parent-row ${expandedBrand === brand.name ? 'expanded' : ''}`}
                onClick={() => setExpandedBrand(expandedBrand === brand.name ? null : brand.name)}
              >
                <span className="col-brand">
                  <span className="expand-icon">{expandedBrand === brand.name ? '▼' : '▶'}</span>
                  <strong>{brand.name}</strong>
                </span>
                <span className="col-grade">{formatNumber(brand.cartons)}</span>
                <span className="col-num lbs"><strong>{formatLbs(brand.lbs)}</strong></span>
              </div>
              {expandedBrand === brand.name && (
                <div className="stock-dist-sub-rows">
                  {sortedGrades.slice(0, 4).map((grade, gIdx) => (
                    <div key={gIdx} className="stock-dist-data-row sub-row">
                      <span className="col-brand"></span>
                      <span className="col-grade">↳ {grade.name}</span>
                      <span className="col-num lbs">{formatLbs(Math.round(brand.lbs * (grade.cartons / totalCartons)))} LBS</span>
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// ===================== COMBINED TAB - UPDATED with Stock Aging Product Breakdown =====================
const CombinedTab = ({ data }) => {
  const [expandedAgeGroup, setExpandedAgeGroup] = React.useState(null);
  const [expandedProduct, setExpandedProduct] = React.useState(null);
  
  if (!data) return null;
  const { quick_stats, coldstore, stock_aging, weekly_trend, current_stock } = data;

  const toggleAgeGroup = (ageGroup) => {
    setExpandedAgeGroup(expandedAgeGroup === ageGroup ? null : ageGroup);
    setExpandedProduct(null);
  };

  const toggleProduct = (productName) => {
    setExpandedProduct(expandedProduct === productName ? null : productName);
  };

  return (
    <div className="combined-tab">
      <div className="section-title" style={{ marginBottom: '16px' }}>
        <div className="section-icon cyan">📊</div>
        <span>COMBINED SUMMARY</span>
        <ExportButton 
          onClick={() => exportToExcel({ 
            type: 'combined', 
            combined: {
              total_cartons: quick_stats?.current_stock,
              total_lbs: current_stock?.lbs || 0,
              today_production: quick_stats?.today_production,
              today_inward: quick_stats?.today_inward,
              today_shipped: quick_stats?.today_shipped,
              mtd_shipped: quick_stats?.mtd_shipped
            },
            cs1: coldstore?.cs1,
            cs2: coldstore?.cs2,
            current_stock,
            stock_aging
          }, 'Combined_Summary')}
          label="Export"
        />
      </div>
      
      <div className="quick-stats-6">
        <QuickStat icon="🏭" label="TODAY PRODUCTION" value={formatNumber(quick_stats?.today_production)} unit="CTN" type="production" />
        <QuickStat icon="📥" label="TODAY INWARD" value={formatNumber(quick_stats?.today_inward)} unit="CTN" type="inward" />
        <QuickStat icon="📦" label="CURRENT STOCK" value={formatNumber(quick_stats?.current_stock)} unit="CTN" type="stock" />
        <QuickStat icon="🛍️" label="LOOSE BAGS" value={formatNumber(quick_stats?.total_loose_bags || 0)} unit="BAGS" type="loose" />
        <QuickStat icon="🚚" label="TODAY SHIPPED" value={formatNumber(quick_stats?.today_shipped)} unit="CTN" type="shipped" />
        <QuickStat icon="📊" label="MTD SHIPPED" value={formatNumber(quick_stats?.mtd_shipped)} unit="CTN" type="aging" />
      </div>

      <div className="section-title">
        <div className="section-icon green">📊</div>
        <span>CURRENT STOCK DISTRIBUTION</span>
      </div>

      {/* New Table-based Stock Distribution Component */}
      <CurrentStockDistributionTable data={current_stock} />

      <div className="section-title">
        <div className="section-icon cyan">❄️</div>
        <span>COLD STORE UTILIZATION</span>
      </div>

      <div className="utilization-combined">
        <UtilizationBar label="CS1" percent={coldstore?.utilization?.cs1?.percent || 0} occupied={coldstore?.utilization?.cs1?.bins_occupied || 0} total={coldstore?.utilization?.cs1?.bins_total || 744} color="#22c55e" />
        <UtilizationBar label="CS2" percent={coldstore?.utilization?.cs2?.percent || 0} occupied={coldstore?.utilization?.cs2?.bins_occupied || 0} total={coldstore?.utilization?.cs2?.bins_total || 744} color="#3b82f6" />
        <div className="total-utilization">
          <div className="total-util-header">
            <span>TOTAL</span>
            <span className="total-util-percent">{coldstore?.utilization?.total?.percent || 0}%</span>
          </div>
          <div className="total-util-info">{formatNumber(coldstore?.utilization?.total?.bins_occupied || 0)} / {formatNumber(coldstore?.utilization?.total?.bins_total || 1488)} Bins</div>
        </div>
      </div>

      <div className="charts-row-2col">
        <div className="chart-card">
          <h3>📈 Weekly Production Trend</h3>
          {weekly_trend && <WeeklyProductionTrendChart data={weekly_trend} />}
        </div>
        <div className="chart-card">
          <h3>⏰ Stock Aging - Click to Expand</h3>
          {stock_aging && (
            <StockAgingWithProductGrades 
              data={stock_aging} 
              expandedAgeGroup={expandedAgeGroup}
              expandedProduct={expandedProduct}
              onToggleAge={toggleAgeGroup}
              onToggleProduct={toggleProduct}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ===================== STOCK AGING WITH PRODUCT-GRADE HIERARCHY =====================
const StockAgingWithProductGrades = ({ data, expandedAgeGroup, expandedProduct, onToggleAge, onToggleProduct }) => {
  if (!data || data.length === 0) {
    return <div className="no-chart-data">No aging data available</div>;
  }

  const colors = {
    '0-7 Days': { bg: 'rgba(34,197,94,0.15)', border: '#22c55e', text: '#15803d' },
    '8-15 Days': { bg: 'rgba(59,130,246,0.15)', border: '#3b82f6', text: '#1d4ed8' },
    '16-30 Days': { bg: 'rgba(245,158,11,0.15)', border: '#f59e0b', text: '#b45309' },
    '31-60 Days': { bg: 'rgba(249,115,22,0.15)', border: '#f97316', text: '#c2410c' },
    '60+ Days': { bg: 'rgba(239,68,68,0.15)', border: '#ef4444', text: '#b91c1c' }
  };

  const totalCartons = data.reduce((sum, d) => sum + d.cartons, 0);

  return (
    <div className="stock-aging-products">
      {data.map((item, idx) => {
        const colorScheme = colors[item.age_group] || colors['0-7 Days'];
        const percentage = totalCartons > 0 ? Math.round((item.cartons / totalCartons) * 100) : 0;
        const isExpanded = expandedAgeGroup === item.age_group;
        
        return (
          <div key={idx} className="aging-group">
            <div 
              className="aging-group-header"
              onClick={() => onToggleAge(item.age_group)}
              style={{ 
                background: colorScheme.bg, 
                borderLeft: `4px solid ${colorScheme.border}`,
                cursor: 'pointer'
              }}
            >
              <div className="aging-group-info">
                <span className="aging-group-name" style={{ color: colorScheme.text }}>
                  {isExpanded ? '▼' : '▶'} {item.age_group}
                </span>
                <span className="aging-group-percent">{percentage}%</span>
              </div>
              <div className="aging-group-values">
                <span className="aging-cartons">{formatNumber(item.cartons)} CTN</span>
                <span className="aging-lbs">{formatLbs(item.lbs)} LBS</span>
              </div>
            </div>
            
            {isExpanded && item.products && item.products.length > 0 && (
              <div className="aging-products-expanded">
                {item.products.map((prod, pIdx) => (
                  <div key={pIdx} className="aging-product-item">
                    <div 
                      className="aging-product-header"
                      onClick={() => onToggleProduct(prod.name)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="aging-product-name">
                        {expandedProduct === prod.name ? '▼' : '▶'} {prod.name}
                      </span>
                      <span className="aging-product-values">
                        <span className="aging-cartons">{formatNumber(prod.cartons)}</span>
                        <span className="aging-lbs">{formatLbs(prod.lbs)} LBS</span>
                      </span>
                    </div>
                    {expandedProduct === prod.name && prod.grades && prod.grades.length > 0 && (
                      <div className="aging-grades-table">
                        {prod.grades.map((grade, gIdx) => (
                          <div key={gIdx} className="aging-grade-row">
                            <span className="aging-grade-name">↳ {grade.name}</span>
                            <span className="aging-grade-values">
                              <span>{formatNumber(grade.cartons)}</span>
                              <span className="lbs">{formatLbs(grade.lbs)} LBS</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      
      <div 
        className="aging-group-header aging-total"
        style={{ 
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', 
          borderLeft: '4px solid #64748b',
          color: '#fff',
          marginTop: '8px'
        }}
      >
        <div className="aging-group-info">
          <span className="aging-group-name" style={{ color: '#fff', fontWeight: '700' }}>
            📊 TOTAL STOCK
          </span>
        </div>
        <div className="aging-group-values">
          <span className="aging-cartons" style={{ color: '#22d3ee' }}>{formatNumber(totalCartons)} CTN</span>
          <span className="aging-lbs" style={{ color: '#a78bfa' }}>
            {formatLbs(data.reduce((sum, d) => sum + d.lbs, 0))} LBS
          </span>
        </div>
      </div>
    </div>
  );
};

// ===================== SUB COMPONENTS =====================
const QuickStat = ({ icon, label, value, unit, type }) => (
  <div className={`quick-stat ${type}`}>
    <div className="qs-icon">{icon}</div>
    <div className="qs-content">
      <div className="qs-label">{label}</div>
      <div className="qs-value">{value}</div>
      <div className="qs-unit">{unit}</div>
    </div>
  </div>
);

const StockStatusCard = ({ status, cartons, kgs, lbs }) => (
  <div className="stock-status-card">
    <div className="ssc-icon">📦</div>
    <div className="ssc-label">{status}</div>
    <div className="ssc-value">{formatNumber(cartons)}</div>
    <div className="ssc-weight">{formatNumber(kgs)} KGs • {formatLbs(lbs)} LBs</div>
  </div>
);

const UtilizationBar = ({ label, percent, occupied, total, color }) => (
  <div className="util-card">
    <div className="util-header">
      <span className="util-label">{label}</span>
      <span className="util-percent" style={{ color }}>{percent}%</span>
    </div>
    <div className="util-bar-bg">
      <div className="util-bar-fill" style={{ width: `${percent}%`, backgroundColor: color }}></div>
    </div>
    <div className="util-footer">{formatNumber(occupied)} / {formatNumber(total)} Bins</div>
  </div>
);

const ProductionLineChart = ({ data }) => {
  const chartData = {
    labels: data.map(d => d.day),
    datasets: [
      { label: 'Produced', data: data.map(d => d.inward), borderColor: '#22c55e', backgroundColor: 'rgba(34,197,94,0.1)', fill: true, tension: 0.4 },
      { label: 'Repack', data: data.map(d => Math.round(d.inward * 0.08)), borderColor: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)', fill: true, tension: 0.4 }
    ]
  };

  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'top', labels: { usePointStyle: true, padding: 15, font: { size: 10 } } }, datalabels: { display: false } },
    scales: { y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } }, x: { grid: { display: false } } }
  };

  return <div style={{ height: '220px' }}><Line data={chartData} options={options} /></div>;
};

const CurrentStockPieChart = ({ data, type }) => {
  if (!data || data.length === 0) {
    return <div className="no-chart-data">No data available</div>;
  }

  const colors = [
    'rgba(59,130,246,0.9)',   // Blue
    'rgba(34,197,94,0.9)',    // Green
    'rgba(245,158,11,0.9)',   // Amber
    'rgba(139,92,246,0.9)',   // Purple
    'rgba(6,182,212,0.9)',    // Cyan
    'rgba(239,68,68,0.9)',    // Red
    'rgba(236,72,153,0.9)',   // Pink
    'rgba(20,184,166,0.9)',   // Teal
  ];

  const sortedData = [...data].sort((a, b) => b.cartons - a.cartons).slice(0, 8);
  const totalCartons = sortedData.reduce((sum, d) => sum + d.cartons, 0);

  const chartData = {
    labels: sortedData.map(d => d.name),
    datasets: [{
      data: sortedData.map(d => d.cartons),
      backgroundColor: colors.slice(0, sortedData.length),
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };

  // Center text plugin
  const centerTextPlugin = {
    id: 'centerText',
    afterDraw: (chart) => {
      const { ctx, chartArea } = chart;
      const centerX = (chartArea.left + chartArea.right) / 2;
      const centerY = (chartArea.top + chartArea.bottom) / 2;
      
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Total value
      ctx.font = 'bold 16px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#1e293b';
      ctx.fillText(formatNumber(totalCartons), centerX, centerY - 8);
      
      // Label
      ctx.font = '10px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#64748b';
      ctx.fillText('CTNS', centerX, centerY + 10);
      
      ctx.restore();
    }
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 6,
          font: { size: 9, weight: '600' },
          boxWidth: 8,
          generateLabels: (chart) => {
            const dataset = chart.data.datasets[0];
            const total = dataset.data.reduce((a, b) => a + b, 0);
            return chart.data.labels.map((label, i) => ({
              text: `${label} ${Math.round((dataset.data[i] / total) * 100)}%`,
              fillStyle: dataset.backgroundColor[i],
              strokeStyle: '#fff',
              lineWidth: 1,
              pointStyle: 'circle',
              hidden: false,
              index: i
            }));
          }
        }
      },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = Math.round((value / total) * 100);
            return `${context.label}: ${formatNumber(value)} CTN (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <div style={{ height: '200px', position: 'relative' }}>
      <Doughnut data={chartData} options={options} plugins={[centerTextPlugin]} />
    </div>
  );
};

// ===================== COLD STORE TAB =====================
// ===================== COLD STORE BREAKUP CHARTS - EXPANDABLE TABLES =====================
// Product → Grade Breakdown
const ColdStoreProductGradeChart = ({ data }) => {
  const [expandedItems, setExpandedItems] = React.useState({});
  
  if (!data || data.length === 0) return <div className="no-chart-data">No product data available</div>;
  
  const grandTotal = data.reduce((sum, item) => sum + (item.cartons || 0), 0);
  const grandTotalLbs = data.reduce((sum, item) => sum + (item.lbs || 0), 0);
  
  const toggleItem = (name) => {
    setExpandedItems(prev => ({ ...prev, [name]: !prev[name] }));
  };
  
  return (
    <div className="expandable-breakdown">
      <div className="breakdown-header purple-gradient">
        <div className="header-left">
          <span className="header-icon">🏷️</span>
          <span className="header-title">PRODUCT → GRADE BREAKUP (CURRENT STOCK)</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body">
        <table className="expandable-table">
          <thead>
            <tr>
              <th className="col-rank">#</th>
              <th className="col-name">PRODUCT / GRADE</th>
              <th className="col-bar">DISTRIBUTION</th>
              <th className="col-num">CARTONS</th>
              <th className="col-num">LBS</th>
              <th className="col-pct">%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((product, idx) => {
              const pct = grandTotal > 0 ? Math.round((product.cartons / grandTotal) * 100) : 0;
              const isExpanded = expandedItems[product.name];
              const hasChildren = product.grades && product.grades.length > 0;
              return (
                <React.Fragment key={idx}>
                  <tr className={`parent-row ${isExpanded ? 'expanded' : ''} ${hasChildren ? 'clickable' : ''}`} onClick={() => hasChildren && toggleItem(product.name)}>
                    <td className="col-rank">{idx + 1}</td>
                    <td className="col-name parent">
                      {hasChildren && <span className="expand-arrow">{isExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text">{product.name}</span>
                      {hasChildren && <span className="child-count">({product.grades.length})</span>}
                    </td>
                    <td className="col-bar">
                      <div className="mini-bar"><div className="mini-fill purple" style={{ width: `${pct}%` }}></div></div>
                    </td>
                    <td className="col-num green">{formatNumber(product.cartons)}</td>
                    <td className="col-num purple">{formatLbs(product.lbs)}</td>
                    <td className="col-pct">{pct}%</td>
                  </tr>
                  {isExpanded && (product.grades || []).map((grade, gIdx) => {
                    const gPct = product.cartons > 0 ? Math.round((grade.cartons / product.cartons) * 100) : 0;
                    return (
                      <tr key={`g-${gIdx}`} className="child-row">
                        <td className="col-rank"></td>
                        <td className="col-name child">↳ {grade.name}</td>
                        <td className="col-bar"><div className="mini-bar child"><div className="mini-fill purple-light" style={{ width: `${gPct}%` }}></div></div></td>
                        <td className="col-num">{formatNumber(grade.cartons)}</td>
                        <td className="col-num">{formatLbs(grade.lbs)}</td>
                        <td className="col-pct">{gPct}%</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Cold Store Comprehensive Breakup - PRODUCT → BRAND → GRADE → PACK SIZE (4-level hierarchy)
const ColdStoreComprehensiveBreakdown = ({ data }) => {
  const [expanded, setExpanded] = React.useState({ products: {}, brands: {}, grades: {} });
  
  if (!data || data.length === 0) return <div className="no-chart-data">No data available</div>;
  
  const grandTotal = data.reduce((sum, item) => sum + (item.cartons || 0), 0);
  const grandTotalLbs = data.reduce((sum, item) => sum + (item.lbs || 0), 0);
  
  const toggleProduct = (name) => setExpanded(prev => ({ ...prev, products: { ...prev.products, [name]: !prev.products[name] } }));
  const toggleBrand = (key) => setExpanded(prev => ({ ...prev, brands: { ...prev.brands, [key]: !prev.brands[key] } }));
  const toggleGrade = (key) => setExpanded(prev => ({ ...prev, grades: { ...prev.grades, [key]: !prev.grades[key] } }));

  return (
    <div className="expandable-breakdown comprehensive">
      <div className="breakdown-header green-gradient">
        <div className="header-left">
          <span className="header-icon">📊</span>
          <span className="header-title">PRODUCT → BRAND → GRADE → PACK SIZE (CURRENT STOCK)</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body" style={{ maxHeight: '500px' }}>
        <table className="expandable-table hierarchy-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>#</th>
              <th>PRODUCT / BRAND / GRADE / PACK SIZE</th>
              <th style={{ width: '120px' }}>DISTRIBUTION</th>
              <th style={{ width: '100px', textAlign: 'right' }}>CARTONS</th>
              <th style={{ width: '100px', textAlign: 'right' }}>LBS</th>
              <th style={{ width: '60px', textAlign: 'center' }}>%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((product, pIdx) => {
              const pPct = grandTotal > 0 ? Math.round((product.cartons / grandTotal) * 100) : 0;
              const pExpanded = expanded.products[product.name];
              const hasBrands = product.brands && product.brands.length > 0;
              return (
                <React.Fragment key={pIdx}>
                  {/* PRODUCT ROW - Level 0 */}
                  <tr className={`level-0 ${pExpanded ? 'expanded' : ''} ${hasBrands ? 'clickable' : ''}`} onClick={() => hasBrands && toggleProduct(product.name)}>
                    <td className="col-rank">{pIdx + 1}</td>
                    <td className="col-name">
                      {hasBrands && <span className="expand-arrow">{pExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text level-0-name">{product.name}</span>
                      {hasBrands && <span className="child-count">({product.brands.length} brands)</span>}
                    </td>
                    <td className="col-bar"><div className="mini-bar"><div className="mini-fill green" style={{ width: `${pPct}%` }}></div></div></td>
                    <td className="col-num green">{formatNumber(product.cartons)}</td>
                    <td className="col-num purple">{formatLbs(product.lbs)}</td>
                    <td className="col-pct">{pPct}%</td>
                  </tr>
                  
                  {/* BRAND ROWS - Level 1 */}
                  {pExpanded && product.brands.map((brand, bIdx) => {
                    const bKey = `${product.name}-${brand.name}`;
                    const bPct = product.cartons > 0 ? Math.round((brand.cartons / product.cartons) * 100) : 0;
                    const bExpanded = expanded.brands[bKey];
                    const hasGrades = brand.grades && brand.grades.length > 0;
                    return (
                      <React.Fragment key={bIdx}>
                        <tr className={`level-1 ${bExpanded ? 'expanded' : ''} ${hasGrades ? 'clickable' : ''}`} onClick={(e) => { e.stopPropagation(); hasGrades && toggleBrand(bKey); }}>
                          <td className="col-rank"></td>
                          <td className="col-name level-1-indent">
                            {hasGrades && <span className="expand-arrow">{bExpanded ? '▼' : '▶'}</span>}
                            <span className="name-text">↳ {brand.name}</span>
                            {hasGrades && <span className="child-count">({brand.grades.length})</span>}
                          </td>
                          <td className="col-bar"><div className="mini-bar"><div className="mini-fill blue" style={{ width: `${bPct}%` }}></div></div></td>
                          <td className="col-num">{formatNumber(brand.cartons)}</td>
                          <td className="col-num">{formatLbs(brand.lbs)}</td>
                          <td className="col-pct">{bPct}%</td>
                        </tr>
                        
                        {/* GRADE ROWS - Level 2 */}
                        {bExpanded && brand.grades.map((grade, gIdx) => {
                          const gKey = `${bKey}-${grade.name}`;
                          const gPct = brand.cartons > 0 ? Math.round((grade.cartons / brand.cartons) * 100) : 0;
                          const gExpanded = expanded.grades[gKey];
                          const hasPackSizes = grade.pack_sizes && grade.pack_sizes.length > 0;
                          return (
                            <React.Fragment key={gIdx}>
                              <tr className={`level-2 ${gExpanded ? 'expanded' : ''} ${hasPackSizes ? 'clickable' : ''}`} onClick={(e) => { e.stopPropagation(); hasPackSizes && toggleGrade(gKey); }}>
                                <td className="col-rank"></td>
                                <td className="col-name level-2-indent">
                                  {hasPackSizes && <span className="expand-arrow">{gExpanded ? '▼' : '▶'}</span>}
                                  <span className="name-text">↳ {grade.name}</span>
                                  {hasPackSizes && <span className="child-count">({grade.pack_sizes.length})</span>}
                                </td>
                                <td className="col-bar"><div className="mini-bar"><div className="mini-fill purple" style={{ width: `${gPct}%` }}></div></div></td>
                                <td className="col-num">{formatNumber(grade.cartons)}</td>
                                <td className="col-num">{formatLbs(grade.lbs)}</td>
                                <td className="col-pct">{gPct}%</td>
                              </tr>
                              
                              {/* PACK SIZE ROWS - Level 3 */}
                              {gExpanded && grade.pack_sizes.map((ps, psIdx) => {
                                const psPct = grade.cartons > 0 ? Math.round((ps.cartons / grade.cartons) * 100) : 0;
                                return (
                                  <tr key={psIdx} className="level-3">
                                    <td className="col-rank"></td>
                                    <td className="col-name level-3-indent">
                                      <span className="name-text">↳ {ps.name}</span>
                                    </td>
                                    <td className="col-bar"><div className="mini-bar"><div className="mini-fill amber" style={{ width: `${psPct}%` }}></div></div></td>
                                    <td className="col-num">{formatNumber(ps.cartons)}</td>
                                    <td className="col-num">{formatLbs(ps.lbs)}</td>
                                    <td className="col-pct">{psPct}%</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Grade → Pack Size Breakdown
const ColdStoreGradePackSizeChart = ({ data }) => {
  const [expandedItems, setExpandedItems] = React.useState({});
  
  if (!data || data.length === 0) return <div className="no-chart-data">No grade data available</div>;
  
  const grandTotal = data.reduce((sum, item) => sum + (item.cartons || 0), 0);
  const grandTotalLbs = data.reduce((sum, item) => sum + (item.lbs || 0), 0);
  
  const toggleItem = (name) => {
    setExpandedItems(prev => ({ ...prev, [name]: !prev[name] }));
  };
  
  return (
    <div className="expandable-breakdown">
      <div className="breakdown-header cyan-gradient">
        <div className="header-left">
          <span className="header-icon">📏</span>
          <span className="header-title">GRADE → PACK SIZE BREAKUP (CURRENT STOCK)</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body">
        <table className="expandable-table">
          <thead>
            <tr>
              <th className="col-rank">#</th>
              <th className="col-name">GRADE / PACK SIZE</th>
              <th className="col-bar">DISTRIBUTION</th>
              <th className="col-num">CARTONS</th>
              <th className="col-num">LBS</th>
              <th className="col-pct">%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((grade, idx) => {
              const pct = grandTotal > 0 ? Math.round((grade.cartons / grandTotal) * 100) : 0;
              const isExpanded = expandedItems[grade.name];
              const hasChildren = grade.pack_sizes && grade.pack_sizes.length > 0;
              return (
                <React.Fragment key={idx}>
                  <tr className={`parent-row ${isExpanded ? 'expanded' : ''} ${hasChildren ? 'clickable' : ''}`} onClick={() => hasChildren && toggleItem(grade.name)}>
                    <td className="col-rank">{idx + 1}</td>
                    <td className="col-name parent">
                      {hasChildren && <span className="expand-arrow">{isExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text">{grade.name}</span>
                      {hasChildren && <span className="child-count">({grade.pack_sizes.length})</span>}
                    </td>
                    <td className="col-bar">
                      <div className="mini-bar"><div className="mini-fill cyan" style={{ width: `${pct}%` }}></div></div>
                    </td>
                    <td className="col-num green">{formatNumber(grade.cartons)}</td>
                    <td className="col-num purple">{formatLbs(grade.lbs)}</td>
                    <td className="col-pct">{pct}%</td>
                  </tr>
                  {isExpanded && (grade.pack_sizes || []).map((ps, psIdx) => {
                    const psPct = grade.cartons > 0 ? Math.round((ps.cartons / grade.cartons) * 100) : 0;
                    return (
                      <tr key={`ps-${psIdx}`} className="child-row">
                        <td className="col-rank"></td>
                        <td className="col-name child">↳ {ps.name}</td>
                        <td className="col-bar"><div className="mini-bar child"><div className="mini-fill cyan-light" style={{ width: `${psPct}%` }}></div></div></td>
                        <td className="col-num">{formatNumber(ps.cartons)}</td>
                        <td className="col-num">{formatLbs(ps.lbs)}</td>
                        <td className="col-pct">{psPct}%</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Brand → Grade Breakdown
const ColdStoreBrandGradeChart = ({ data }) => {
  const [expandedItems, setExpandedItems] = React.useState({});
  
  if (!data || data.length === 0) return <div className="no-chart-data">No brand data available</div>;
  
  const grandTotal = data.reduce((sum, item) => sum + (item.cartons || 0), 0);
  const grandTotalLbs = data.reduce((sum, item) => sum + (item.lbs || 0), 0);
  
  const toggleItem = (name) => {
    setExpandedItems(prev => ({ ...prev, [name]: !prev[name] }));
  };
  
  return (
    <div className="expandable-breakdown">
      <div className="breakdown-header amber-gradient">
        <div className="header-left">
          <span className="header-icon">🏷️</span>
          <span className="header-title">BRAND → GRADE BREAKUP (CURRENT STOCK)</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body">
        <table className="expandable-table">
          <thead>
            <tr>
              <th className="col-rank">#</th>
              <th className="col-name">BRAND / GRADE</th>
              <th className="col-bar">DISTRIBUTION</th>
              <th className="col-num">CARTONS</th>
              <th className="col-num">LBS</th>
              <th className="col-pct">%</th>
            </tr>
          </thead>
          <tbody>
            {data.map((brand, idx) => {
              const pct = grandTotal > 0 ? Math.round((brand.cartons / grandTotal) * 100) : 0;
              const isExpanded = expandedItems[brand.name];
              const hasChildren = brand.grades && brand.grades.length > 0;
              return (
                <React.Fragment key={idx}>
                  <tr className={`parent-row ${isExpanded ? 'expanded' : ''} ${hasChildren ? 'clickable' : ''}`} onClick={() => hasChildren && toggleItem(brand.name)}>
                    <td className="col-rank">{idx + 1}</td>
                    <td className="col-name parent">
                      {hasChildren && <span className="expand-arrow">{isExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text">{brand.name}</span>
                      {hasChildren && <span className="child-count">({brand.grades.length})</span>}
                    </td>
                    <td className="col-bar">
                      <div className="mini-bar"><div className="mini-fill amber" style={{ width: `${pct}%` }}></div></div>
                    </td>
                    <td className="col-num green">{formatNumber(brand.cartons)}</td>
                    <td className="col-num purple">{formatLbs(brand.lbs)}</td>
                    <td className="col-pct">{pct}%</td>
                  </tr>
                  {isExpanded && (brand.grades || []).map((grade, gIdx) => {
                    const gPct = brand.cartons > 0 ? Math.round((grade.cartons / brand.cartons) * 100) : 0;
                    return (
                      <tr key={`g-${gIdx}`} className="child-row">
                        <td className="col-rank"></td>
                        <td className="col-name child">↳ {grade.name}</td>
                        <td className="col-bar"><div className="mini-bar child"><div className="mini-fill amber-light" style={{ width: `${gPct}%` }}></div></div></td>
                        <td className="col-num">{formatNumber(grade.cartons)}</td>
                        <td className="col-num">{formatLbs(grade.lbs)}</td>
                        <td className="col-pct">{gPct}%</td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===================== COLD STORE COMPREHENSIVE BREAKUP =====================
// PRODUCT → BRAND → GRADE → PACK SIZE - Full Hierarchy for Cold Store
const ColdStoreComprehensiveBreakdownChart = ({ productData, brandData, gradeData }) => {
  const [expanded, setExpanded] = React.useState({ products: {}, brands: {}, grades: {} });
  
  // Build hierarchy from available data
  const products = (productData || []).map(p => ({
    name: p.name,
    cartons: p.cartons || 0,
    lbs: p.lbs || 0,
    brands: (p.brands || []).map(b => ({
      name: b.name,
      cartons: b.cartons || 0,
      lbs: b.lbs || 0,
      grades: (b.grades || []).map(g => ({
        name: g.name,
        cartons: g.cartons || 0,
        lbs: g.lbs || 0,
        pack_sizes: (g.pack_sizes || []).map(ps => ({
          name: ps.name,
          cartons: ps.cartons || 0,
          lbs: ps.lbs || 0
        })).sort((a, b) => b.cartons - a.cartons)
      })).sort((a, b) => b.cartons - a.cartons)
    })).sort((a, b) => b.cartons - a.cartons)
  })).sort((a, b) => b.cartons - a.cartons);
  
  const grandTotal = products.reduce((sum, p) => sum + p.cartons, 0);
  const grandTotalLbs = products.reduce((sum, p) => sum + p.lbs, 0);
  
  const toggleProduct = (name) => setExpanded(prev => ({ ...prev, products: { ...prev.products, [name]: !prev.products[name] } }));
  const toggleBrand = (key) => setExpanded(prev => ({ ...prev, brands: { ...prev.brands, [key]: !prev.brands[key] } }));
  const toggleGrade = (key) => setExpanded(prev => ({ ...prev, grades: { ...prev.grades, [key]: !prev.grades[key] } }));

  if (products.length === 0) {
    return <div className="no-chart-data">No data available</div>;
  }
  
  return (
    <div className="expandable-breakdown comprehensive">
      <div className="breakdown-header green-gradient">
        <div className="header-left">
          <span className="header-icon">📊</span>
          <span className="header-title">PRODUCT → BRAND → GRADE → PACK SIZE (CURRENT STOCK)</span>
        </div>
        <div className="header-right">
          <span className="header-total">{formatNumber(grandTotal)} CARTONS</span>
          <span className="header-lbs">{formatLbs(grandTotalLbs)} LBS</span>
        </div>
      </div>
      <div className="breakdown-body" style={{ maxHeight: '500px' }}>
        <table className="expandable-table hierarchy-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>#</th>
              <th>PRODUCT / BRAND / GRADE / PACK SIZE</th>
              <th style={{ width: '120px' }}>DISTRIBUTION</th>
              <th style={{ width: '100px', textAlign: 'right' }}>CARTONS</th>
              <th style={{ width: '100px', textAlign: 'right' }}>LBS</th>
              <th style={{ width: '60px', textAlign: 'center' }}>%</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product, pIdx) => {
              const pPct = grandTotal > 0 ? Math.round((product.cartons / grandTotal) * 100) : 0;
              const pExpanded = expanded.products[product.name];
              const hasBrands = product.brands && product.brands.length > 0;
              return (
                <React.Fragment key={pIdx}>
                  {/* PRODUCT ROW - Level 0 */}
                  <tr className={`level-0 ${pExpanded ? 'expanded' : ''} ${hasBrands ? 'clickable' : ''}`} onClick={() => hasBrands && toggleProduct(product.name)}>
                    <td className="col-rank">{pIdx + 1}</td>
                    <td className="col-name">
                      {hasBrands && <span className="expand-arrow">{pExpanded ? '▼' : '▶'}</span>}
                      <span className="name-text level-0-name">{product.name}</span>
                      {hasBrands && <span className="child-count">({product.brands.length} brands)</span>}
                    </td>
                    <td className="col-bar"><div className="mini-bar"><div className="mini-fill green" style={{ width: `${pPct}%` }}></div></div></td>
                    <td className="col-num green">{formatNumber(product.cartons)}</td>
                    <td className="col-num purple">{formatLbs(product.lbs)}</td>
                    <td className="col-pct">{pPct}%</td>
                  </tr>
                  
                  {/* BRAND ROWS - Level 1 */}
                  {pExpanded && product.brands.map((brand, bIdx) => {
                    const bKey = `${product.name}-${brand.name}`;
                    const bPct = product.cartons > 0 ? Math.round((brand.cartons / product.cartons) * 100) : 0;
                    const bExpanded = expanded.brands[bKey];
                    const hasGrades = brand.grades && brand.grades.length > 0;
                    return (
                      <React.Fragment key={bIdx}>
                        <tr className={`level-1 ${bExpanded ? 'expanded' : ''} ${hasGrades ? 'clickable' : ''}`} onClick={(e) => { e.stopPropagation(); hasGrades && toggleBrand(bKey); }}>
                          <td className="col-rank"></td>
                          <td className="col-name level-1-indent">
                            {hasGrades && <span className="expand-arrow">{bExpanded ? '▼' : '▶'}</span>}
                            <span className="name-text">↳ {brand.name}</span>
                            {hasGrades && <span className="child-count">({brand.grades.length})</span>}
                          </td>
                          <td className="col-bar"><div className="mini-bar"><div className="mini-fill blue" style={{ width: `${bPct}%` }}></div></div></td>
                          <td className="col-num">{formatNumber(brand.cartons)}</td>
                          <td className="col-num">{formatLbs(brand.lbs)}</td>
                          <td className="col-pct">{bPct}%</td>
                        </tr>
                        
                        {/* GRADE ROWS - Level 2 */}
                        {bExpanded && brand.grades.map((grade, gIdx) => {
                          const gKey = `${bKey}-${grade.name}`;
                          const gPct = brand.cartons > 0 ? Math.round((grade.cartons / brand.cartons) * 100) : 0;
                          const gExpanded = expanded.grades[gKey];
                          const hasPackSizes = grade.pack_sizes && grade.pack_sizes.length > 0;
                          return (
                            <React.Fragment key={gIdx}>
                              <tr className={`level-2 ${gExpanded ? 'expanded' : ''} ${hasPackSizes ? 'clickable' : ''}`} onClick={(e) => { e.stopPropagation(); hasPackSizes && toggleGrade(gKey); }}>
                                <td className="col-rank"></td>
                                <td className="col-name level-2-indent">
                                  {hasPackSizes && <span className="expand-arrow">{gExpanded ? '▼' : '▶'}</span>}
                                  <span className="name-text">↳ {grade.name}</span>
                                  {hasPackSizes && <span className="child-count">({grade.pack_sizes.length})</span>}
                                </td>
                                <td className="col-bar"><div className="mini-bar"><div className="mini-fill purple" style={{ width: `${gPct}%` }}></div></div></td>
                                <td className="col-num">{formatNumber(grade.cartons)}</td>
                                <td className="col-num">{formatLbs(grade.lbs)}</td>
                                <td className="col-pct">{gPct}%</td>
                              </tr>
                              
                              {/* PACK SIZE ROWS - Level 3 */}
                              {gExpanded && grade.pack_sizes.map((ps, psIdx) => {
                                const psPct = grade.cartons > 0 ? Math.round((ps.cartons / grade.cartons) * 100) : 0;
                                return (
                                  <tr key={psIdx} className="level-3">
                                    <td className="col-rank"></td>
                                    <td className="col-name level-3-indent">
                                      <span className="name-text">↳ {ps.name}</span>
                                    </td>
                                    <td className="col-bar"><div className="mini-bar"><div className="mini-fill amber" style={{ width: `${psPct}%` }}></div></div></td>
                                    <td className="col-num">{formatNumber(ps.cartons)}</td>
                                    <td className="col-num">{formatLbs(ps.lbs)}</td>
                                    <td className="col-pct">{psPct}%</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===================== COLD STORE DETAIL MODAL =====================
const ColdStoreDetailModal = ({ modal, onClose }) => {
  const { title, cartons, lbs, color, products, pack_sizes, brands, grades } = modal;

  return (
    <div className="summary-popup-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="summary-popup-modal wide">
        <div className={`summary-popup-header ${color || 'blue'}`}>
          <div className="summary-popup-header-content">
            <h2>{title}</h2>
            <span className="summary-popup-subtitle">PRODUCT / BRAND / GRADE / PACK SIZE BREAKUP</span>
          </div>
          <button className="summary-popup-close" onClick={onClose}>×</button>
        </div>
        
        <div className="summary-popup-stats">
          <div className="stat-box">
            <span className="label">TOTAL CARTONS</span>
            <span className="value green">{formatNumber(cartons)}</span>
          </div>
          <div className="stat-box">
            <span className="label">LBS</span>
            <span className="value purple">{formatLbs(lbs)}</span>
          </div>
        </div>
        
        <div className="summary-popup-body two-column">
          {/* Left Column - Product & Brand */}
          <div className="summary-popup-column">
            <div className="summary-popup-section">
              <h3 className="section-header product">PRODUCT</h3>
              <table className="detail-table product-table">
                <thead className="thead-purple"><tr><th>Product</th><th>CTNS</th><th>LBS</th></tr></thead>
                <tbody>
                  {products.length > 0 ? products.map((p, i) => (
                    <tr key={i}><td>{p.name || p.product}</td><td className="num">{formatNumber(p.cartons)}</td><td className="num">{formatLbs(p.lbs)}</td></tr>
                  )) : <tr><td colSpan="3" className="no-data">No data</td></tr>}
                </tbody>
              </table>
            </div>
            
            <div className="summary-popup-section">
              <h3 className="section-header brand">BRAND</h3>
              <table className="detail-table brand-table">
                <thead className="thead-orange"><tr><th>Brand</th><th>CTNS</th><th>LBS</th></tr></thead>
                <tbody>
                  {brands.length > 0 ? brands.map((b, i) => (
                    <tr key={i}><td>{b.name || b.brand}</td><td className="num">{formatNumber(b.cartons)}</td><td className="num">{formatLbs(b.lbs)}</td></tr>
                  )) : <tr><td colSpan="3" className="no-data">No data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Right Column - Grade / Pack Size */}
          <div className="summary-popup-column">
            <div className="summary-popup-section">
              <h3 className="section-header grade">GRADE / PACK SIZE</h3>
              <table className="detail-table grade-table">
                <thead className="thead-cyan"><tr><th>Grade / Pack Size</th><th>CTNS</th><th>LBS</th></tr></thead>
                <tbody>
                  {pack_sizes.length > 0 ? pack_sizes.map((ps, i) => (
                    <tr key={i}><td>{ps.name || ps.pack_size}</td><td className="num">{formatNumber(ps.cartons)}</td><td className="num">{formatLbs(ps.lbs)}</td></tr>
                  )) : <tr><td colSpan="3" className="no-data">No data</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ColdStoreTab = ({ data }) => {
  const [selectedModal, setSelectedModal] = useState(null);
  const [shipmentModal, setShipmentModal] = useState(null);
  const [modalTab, setModalTab] = useState('all');
  const [showMoreBreakdowns, setShowMoreBreakdowns] = useState(false);
  
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setSelectedModal(null);
        setShipmentModal(null);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);
  
  if (!data) return null;
  
  const { 
    current_stock, 
    today_inward, 
    yesterday_inward,
    today_shipment, 
    yesterday_shipment, 
    mtd_shipment,
    last_month_shipment,
    utilization, 
    weekly_trend, 
    product_distribution,
    product_grade_breakdown,
    grade_packsize_breakdown,
    brand_grade_breakdown,
    full_hierarchy_current_stock,
    dates
  } = data;

  const openDetailModal = (title, cartons, lbs, color, breakdownData = null) => {
    setSelectedModal({ 
      title, 
      cartons, 
      lbs: lbs || Math.round(cartons * 2.2), 
      color,
      products: breakdownData?.products || [],
      pack_sizes: breakdownData?.pack_sizes || [],
      brands: breakdownData?.brands || [],
      grades: breakdownData?.grades || []
    });
  };

  const openShipmentModal = (period, shipData) => {
    setShipmentModal({ period, data: shipData });
    setModalTab('all');
  };

  const closeDetailModal = () => setSelectedModal(null);
  const closeShipmentModal = () => setShipmentModal(null);

  return (
    <div className="coldstore-tab-v2">
      <div className="section-title" style={{ marginBottom: '16px' }}>
        <div className="section-icon blue">🏭</div>
        <span>COLD STORE INVENTORY</span>
        <ExportButton 
          onClick={() => exportToExcel({ 
            type: 'coldstore', 
            cs1: current_stock?.cs1,
            cs2: current_stock?.cs2,
            current_stock,
            today_inward,
            yesterday_inward
          }, 'ColdStore_Inventory')}
          label="Export"
        />
      </div>
      <div className="cs-summary-row-6">
        <div className="cs-summary-card inward" onClick={() => openDetailModal('TODAY INWARD', today_inward?.total || 0, today_inward?.lbs, 'green', today_inward)}>
          <div className="cs-summary-icon">📥</div>
          <div className="cs-summary-info">
            <div className="cs-summary-label">Today Inward</div>
            <div className="cs-summary-value green">{formatNumber(today_inward?.total || 0)}</div>
            <div className="cs-summary-lbs">{formatLbs(today_inward?.lbs || 0)} LBS</div>
          </div>
        </div>
        <div className="cs-summary-card stock" onClick={() => openDetailModal('CURRENT STOCK', current_stock?.cartons || 0, current_stock?.lbs, 'blue', current_stock)}>
          <div className="cs-summary-icon">📦</div>
          <div className="cs-summary-info">
            <div className="cs-summary-label">Current Stock</div>
            <div className="cs-summary-value blue">{formatNumber(current_stock?.cartons || 0)}</div>
            <div className="cs-summary-lbs">{formatLbs(current_stock?.lbs || 0)} LBS</div>
          </div>
        </div>
        <div className="cs-summary-card today-ship" onClick={() => openShipmentModal('TODAY', today_shipment)}>
          <div className="cs-summary-icon">🚚</div>
          <div className="cs-summary-info">
            <div className="cs-summary-label">Today Shipment</div>
            <div className="cs-summary-value cyan">{formatNumber(today_shipment?.total_cartons || 0)}</div>
            <div className="cs-summary-lbs">{formatLbs(today_shipment?.total_lbs || 0)} LBS</div>
          </div>
        </div>
        <div className="cs-summary-card yesterday-ship" onClick={() => openShipmentModal('YESTERDAY', yesterday_shipment)}>
          <div className="cs-summary-icon">📅</div>
          <div className="cs-summary-info">
            <div className="cs-summary-label">Yesterday Shipment</div>
            <div className="cs-summary-value purple">{formatNumber(yesterday_shipment?.total_cartons || 0)}</div>
            <div className="cs-summary-lbs">{formatLbs(yesterday_shipment?.total_lbs || 0)} LBS</div>
          </div>
        </div>
        <div className="cs-summary-card mtd-ship" onClick={() => openShipmentModal('MTD', mtd_shipment)}>
          <div className="cs-summary-icon">📊</div>
          <div className="cs-summary-info">
            <div className="cs-summary-label">MTD Shipment</div>
            <div className="cs-summary-value amber">{formatNumber(mtd_shipment?.cartons || 0)}</div>
            <div className="cs-summary-lbs">{formatLbs(mtd_shipment?.lbs || 0)} LBS</div>
          </div>
        </div>
        <div className="cs-summary-card last-month-ship" onClick={() => openShipmentModal('LAST MONTH', last_month_shipment)}>
          <div className="cs-summary-icon">📆</div>
          <div className="cs-summary-info">
            <div className="cs-summary-label">{last_month_shipment?.month_name || 'Last Month'} Shipped</div>
            <div className="cs-summary-value red">{formatNumber(last_month_shipment?.cartons || 0)}</div>
            <div className="cs-summary-lbs">{formatLbs(last_month_shipment?.lbs || 0)} LBS</div>
          </div>
        </div>
      </div>

      <div className="cs-section-header">
        <div className="cs-section-icon">🏭</div>
        <span className="cs-section-title">Cold Store Utilization</span>
        <span className="cs-live-badge">🔴 LIVE</span>
      </div>
      
      <div className="cs-util-grid">
        <UtilizationCardClickable 
          title="❄️ Cold Store 1 (CS1)"
          percent={utilization?.cs1?.percent || 0}
          occupied={utilization?.cs1?.bins_occupied || 0}
          total={utilization?.cs1?.bins_total || 744}
          cartons={utilization?.cs1?.cartons || 0}
          color="#22c55e"
          onClick={() => openDetailModal('COLD STORE 1 (CS1)', utilization?.cs1?.cartons || 0, utilization?.cs1?.lbs, 'green', utilization?.cs1)}
        />
        <UtilizationCardClickable 
          title="❄️ Cold Store 2 (CS2)"
          percent={utilization?.cs2?.percent || 0}
          occupied={utilization?.cs2?.bins_occupied || 0}
          total={utilization?.cs2?.bins_total || 744}
          cartons={utilization?.cs2?.cartons || 0}
          color="#3b82f6"
          onClick={() => openDetailModal('COLD STORE 2 (CS2)', utilization?.cs2?.cartons || 0, utilization?.cs2?.lbs, 'blue', utilization?.cs2)}
        />
        <UtilizationCardClickable 
          title="📊 Total Utilization"
          percent={utilization?.total?.percent || 0}
          occupied={utilization?.total?.bins_occupied || 0}
          total={utilization?.total?.bins_total || 1488}
          cartons={utilization?.total?.cartons || 0}
          isTotal={true}
          onClick={() => openDetailModal('TOTAL COLD STORE', utilization?.total?.cartons || 0, utilization?.total?.lbs, 'cyan', current_stock)}
        />
      </div>

      {/* COMPREHENSIVE BREAKUP - PRODUCT → BRAND → GRADE → PACK SIZE */}
      <div className="section-title">
        <div className="section-icon teal">📊</div>
        <span>PRODUCT → BRAND → GRADE → PACK SIZE BREAKUP</span>
      </div>
      <div className="chart-card-full">
        <ColdStoreComprehensiveBreakdown data={full_hierarchy_current_stock} />
      </div>

      {/* ADDITIONAL BREAKUPS - Collapsible */}
      <div className="collapsible-section">
        <button 
          className="collapsible-toggle"
          onClick={() => setShowMoreBreakdowns(!showMoreBreakdowns)}
        >
          {showMoreBreakdowns ? '▼ Hide' : '▶ Show'} Additional Breakups (Product→Grade, Grade→PackSize, Brand→Grade)
        </button>
        
        {showMoreBreakdowns && (
          <div className="collapsible-content">
            {/* PRODUCT → GRADE BREAKUP */}
            <div className="section-title compact">
              <div className="section-icon green">🏷️</div>
              <span>PRODUCT → GRADE BREAKUP</span>
            </div>
            <div className="chart-card-full">
              <ColdStoreProductGradeChart data={product_grade_breakdown} />
            </div>

            {/* GRADE → PACK SIZE BREAKUP */}
            <div className="section-title compact">
              <div className="section-icon purple">📏</div>
              <span>GRADE → PACK SIZE BREAKUP</span>
            </div>
            <div className="chart-card-full">
              <ColdStoreGradePackSizeChart data={grade_packsize_breakdown} />
            </div>

            {/* BRAND → GRADE BREAKUP */}
            <div className="section-title compact">
              <div className="section-icon amber">🏷️</div>
              <span>BRAND → GRADE BREAKUP</span>
            </div>
            <div className="chart-card-full">
              <ColdStoreBrandGradeChart data={brand_grade_breakdown} />
            </div>
          </div>
        )}
      </div>

      <div className="cs-section-header">
        <div className="cs-section-icon">📈</div>
        <span className="cs-section-title">Weekly Movement Trend</span>
      </div>
      <div className="cs-chart-card">
        <h3>📊 Stock Movement — Last 7 Days</h3>
        <div className="cs-chart-container">
          <WeeklyMovementChart data={weekly_trend} />
        </div>
      </div>

      {selectedModal && (
        <ColdStoreDetailModal 
          modal={selectedModal} 
          onClose={closeDetailModal}
        />
      )}

      {shipmentModal && (
        <ShipmentModalV2 
          modal={shipmentModal}
          activeTab={modalTab}
          setActiveTab={setModalTab}
          onClose={closeShipmentModal}
        />
      )}
    </div>
  );
};

// ===================== UTILIZATION CARD CLICKABLE =====================
const UtilizationCardClickable = ({ title, percent, occupied, total, cartons, color, isTotal, onClick }) => {
  return (
    <div className={`cs-util-card ${isTotal ? 'total' : ''}`} onClick={onClick}>
      <div className="cs-util-title">{title}</div>
      <div className="cs-util-chart">
        <Doughnut 
          data={{
            datasets: [{
              data: [percent, 100 - percent],
              backgroundColor: isTotal ? ['#ffffff', 'rgba(255,255,255,0.3)'] : [color, '#e2e8f0'],
              borderWidth: 0
            }]
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: '75%',
            plugins: { legend: { display: false }, datalabels: { display: false }, tooltip: { enabled: false } }
          }}
        />
        <div className={`cs-util-percent ${isTotal ? 'white' : ''}`}>{percent}%</div>
      </div>
      <div className="cs-util-info">
        <div className="cs-util-bins">{formatNumber(occupied)} / {formatNumber(total)} Bins</div>
        <div className="cs-util-cartons">{formatNumber(cartons)} Cartons</div>
      </div>
    </div>
  );
};

// ===================== WEEKLY MOVEMENT CHART =====================
const WeeklyMovementChart = ({ data }) => {
  const [selectedDay, setSelectedDay] = React.useState(null);
  
  if (!data || data.length === 0) return <div className="no-chart-data">No trend data</div>;

  const chartData = {
    labels: data.map(d => `${d.day}\n${d.date?.slice(5) || ''}`),
    datasets: [
      { 
        label: 'Produced', 
        data: data.map(d => d.produced || d.inward || 0), 
        borderColor: '#22c55e', 
        backgroundColor: 'rgba(34,197,94,0.15)', 
        fill: false, 
        tension: 0.4, 
        borderWidth: 3, 
        pointRadius: 6, 
        pointBackgroundColor: '#22c55e', 
        pointBorderColor: '#fff', 
        pointBorderWidth: 2 
      },
      { 
        label: 'Repack-In', 
        data: data.map(d => d.repack_in || 0), 
        borderColor: '#3b82f6', 
        backgroundColor: 'rgba(59,130,246,0.15)', 
        fill: false, 
        tension: 0.4, 
        borderWidth: 3, 
        pointRadius: 6, 
        pointBackgroundColor: '#3b82f6', 
        pointBorderColor: '#fff', 
        pointBorderWidth: 2 
      },
      { 
        label: 'Shipped', 
        data: data.map(d => d.shipped || 0), 
        borderColor: '#ef4444', 
        backgroundColor: 'rgba(239,68,68,0.15)', 
        fill: false, 
        tension: 0.4, 
        borderWidth: 3, 
        pointRadius: 6, 
        pointBackgroundColor: '#ef4444', 
        pointBorderColor: '#fff', 
        pointBorderWidth: 2 
      },
      { 
        label: 'Repack-Out', 
        data: data.map(d => d.repack_out || 0), 
        borderColor: '#f59e0b', 
        backgroundColor: 'rgba(245,158,11,0.15)', 
        fill: false, 
        tension: 0.4, 
        borderWidth: 3, 
        pointRadius: 6, 
        pointBackgroundColor: '#f59e0b', 
        pointBorderColor: '#fff', 
        pointBorderWidth: 2 
      }
    ]
  };

  const options = {
    responsive: true, 
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    onClick: (event, elements) => {
      if (elements.length > 0 && data) {
        const index = elements[0].index;
        setSelectedDay(selectedDay === index ? null : index);
      }
    },
    plugins: { 
      legend: { 
        position: 'top', 
        labels: { 
          usePointStyle: true, 
          pointStyle: 'circle', 
          padding: 15, 
          font: { size: 10, weight: '600' } 
        } 
      }, 
      datalabels: { display: false },
      tooltip: {
        backgroundColor: 'rgba(30, 41, 59, 0.95)',
        titleFont: { size: 12, weight: '700' },
        bodyFont: { size: 11, weight: '500' },
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          afterBody: function(context) {
            const dayData = data?.[context[0]?.dataIndex];
            if (!dayData) return '';
            return [
              '',
              `📦 Produced: ${formatNumber(dayData.produced || dayData.inward || 0)} ctns`,
              `🔄 Repack-In: ${formatNumber(dayData.repack_in || 0)} ctns`,
              `🚚 Shipped: ${formatNumber(dayData.shipped || 0)} ctns`,
              `📤 Repack-Out: ${formatNumber(dayData.repack_out || 0)} ctns`,
              '',
              '👆 Click for details'
            ];
          }
        }
      }
    },
    scales: { 
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 10 } } }, 
      x: { grid: { display: false }, ticks: { font: { size: 9 }, maxRotation: 0 } } 
    }
  };

  const selectedDayData = selectedDay !== null && data ? data[selectedDay] : null;

  return (
    <div className="weekly-trend-container">
      <div style={{ height: '280px' }}>
        <Line data={chartData} options={options} />
      </div>
      
      {selectedDayData && (
        <div className="weekly-trend-details">
          <div className="trend-details-header">
            <span>📅 {selectedDayData.day} ({selectedDayData.date}) - Movement Details</span>
            <button className="close-btn" onClick={() => setSelectedDay(null)}>✕</button>
          </div>
          <div className="trend-details-grid four-col">
            <div className="trend-detail-card green">
              <div className="trend-detail-title">📦 PRODUCED</div>
              <div className="trend-detail-main">{formatNumber(selectedDayData.produced || selectedDayData.inward || 0)}</div>
              <div className="trend-detail-sub">{formatLbs(selectedDayData.produced_lbs || selectedDayData.inward_lbs || 0)} LBS</div>
            </div>
            <div className="trend-detail-card blue">
              <div className="trend-detail-title">🔄 REPACK-IN</div>
              <div className="trend-detail-main">{formatNumber(selectedDayData.repack_in || 0)}</div>
              <div className="trend-detail-sub">{formatLbs(selectedDayData.repack_in_lbs || 0)} LBS</div>
            </div>
            <div className="trend-detail-card red">
              <div className="trend-detail-title">🚚 SHIPPED</div>
              <div className="trend-detail-main">{formatNumber(selectedDayData.shipped || 0)}</div>
              <div className="trend-detail-sub">{formatLbs(selectedDayData.shipped_lbs || 0)} LBS</div>
            </div>
            <div className="trend-detail-card orange">
              <div className="trend-detail-title">📤 REPACK-OUT</div>
              <div className="trend-detail-main">{formatNumber(selectedDayData.repack_out || 0)}</div>
              <div className="trend-detail-sub">{formatLbs(selectedDayData.repack_out_lbs || 0)} LBS</div>
            </div>
          </div>
          {selectedDayData.products && selectedDayData.products.length > 0 && (
            <div className="trend-products-section">
              <div className="trend-products-title">📋 Top Products</div>
              <div className="trend-products-list">
                {selectedDayData.products.slice(0, 5).map((p, i) => (
                  <div key={i} className="trend-product-item">
                    <span className="rank">#{i + 1}</span>
                    <span className="name">{p.name}</span>
                    <span className="cartons">{formatNumber(p.cartons)} CTNS</span>
                    <span className="lbs">{formatLbs(p.lbs)} LBS</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ===================== PRODUCT STOCK CHART V2 =====================
const ProductStockChartV2 = ({ data, onBarClick }) => {
  if (!data || data.length === 0) return <div className="no-chart-data">No product data</div>;

  const colors = ['#1565c0', '#22c55e', '#f9a825', '#6a1b9a', '#0097a7', '#c62828', '#ad1457', '#00838f', '#e65100', '#283593'];
  
  const chartData = {
    labels: data.map(d => d.product),
    datasets: [{ 
      label: 'Cartons', 
      data: data.map(d => d.cartons), 
      backgroundColor: colors.slice(0, data.length), 
      borderRadius: 8 
    }]
  };

  const options = {
    indexAxis: 'y', 
    responsive: true, 
    maintainAspectRatio: false,
    plugins: { 
      legend: { display: false }, 
      datalabels: { 
        color: '#fff', 
        font: { size: 11, weight: '800' }, 
        anchor: 'center', 
        align: 'center', 
        formatter: v => v.toLocaleString() 
      } 
    },
    scales: { 
      x: { grid: { color: 'rgba(0,0,0,0.05)' }, title: { display: true, text: 'Cartons', font: { size: 11, weight: '600' } } }, 
      y: { grid: { display: false }, ticks: { font: { size: 10, weight: '600' } } } 
    }
  };

  return <div style={{ height: '280px' }}><Bar data={chartData} options={options} /></div>;
};

// ===================== DETAIL MODAL =====================
const DetailModal = ({ modal, onClose }) => {
  const { title, cartons, lbs, color, products, pack_sizes, brands } = modal;

  return (
    <div className="cs-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cs-modal">
        <div className={`cs-modal-header ${color}`}>
          <div>
            <h2>{title}</h2>
            <span>Product / Pack Size / Brand Breakdown</span>
          </div>
          <button className="cs-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="cs-modal-summary">
          <div className="cs-modal-summary-item">
            <div className="cs-modal-summary-label">Total Cartons</div>
            <div className="cs-modal-summary-value green">{formatNumber(cartons)}</div>
          </div>
          <div className="cs-modal-summary-item">
            <div className="cs-modal-summary-label">Total LBS</div>
            <div className="cs-modal-summary-value purple">{formatLbs(lbs)}</div>
          </div>
        </div>
        <div className="cs-modal-body">
          <div className="cs-modal-section">
            <div className="cs-modal-section-title">📦 Product Wise Breakup</div>
            <table className="cs-modal-table">
              <thead><tr><th>Product</th><th style={{textAlign:'right'}}>Cartons</th><th style={{textAlign:'right'}}>LBS</th></tr></thead>
              <tbody>
                {products.length > 0 ? products.map((p, i) => (
                  <tr key={i}>
                    <td>{p.name || p.product}</td>
                    <td className="cartons">{formatNumber(p.cartons)}</td>
                    <td className="lbs">{formatLbs(p.lbs)}</td>
                  </tr>
                )) : <tr><td colSpan="3" style={{textAlign:'center',color:'#999'}}>No data available</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="cs-modal-section">
            <div className="cs-modal-section-title">📏 Pack Size Wise Breakup</div>
            <table className="cs-modal-table">
              <thead><tr><th>Pack Size</th><th style={{textAlign:'right'}}>Cartons</th><th style={{textAlign:'right'}}>LBS</th></tr></thead>
              <tbody>
                {pack_sizes.length > 0 ? pack_sizes.map((p, i) => (
                  <tr key={i}>
                    <td>{p.name || p.pack_size}</td>
                    <td className="cartons">{formatNumber(p.cartons)}</td>
                    <td className="lbs">{formatLbs(p.lbs)}</td>
                  </tr>
                )) : <tr><td colSpan="3" style={{textAlign:'center',color:'#999'}}>No data available</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="cs-modal-section">
            <div className="cs-modal-section-title">🏷️ Brand Wise Breakup</div>
            <table className="cs-modal-table">
              <thead><tr><th>Brand</th><th style={{textAlign:'right'}}>Cartons</th><th style={{textAlign:'right'}}>LBS</th></tr></thead>
              <tbody>
                {brands.length > 0 ? brands.map((b, i) => (
                  <tr key={i}>
                    <td>{b.name || b.brand}</td>
                    <td className="cartons">{formatNumber(b.cartons)}</td>
                    <td className="lbs">{formatLbs(b.lbs)}</td>
                  </tr>
                )) : <tr><td colSpan="3" style={{textAlign:'center',color:'#999'}}>No data available</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===================== SHIPMENT MODAL V2 =====================
const ShipmentModalV2 = ({ modal, activeTab, setActiveTab, onClose }) => {
  const { period, data } = modal;
  const total = data?.total_cartons || data?.cartons || 0;
  const totalLbs = data?.total_lbs || data?.lbs || Math.round(total * 2.2);
  const dayCartons = data?.day?.cartons || 0;
  const nightCartons = data?.night?.cartons || 0;
  
  const products = data?.products || [];
  const pack_sizes = data?.pack_sizes || [];
  const brands = data?.brands || [];

  const colorMap = { 'YESTERDAY': 'indigo', 'TODAY': 'green', 'MTD': 'amber', 'LAST MONTH': 'red' };
  const color = colorMap[period] || 'blue';

  return (
    <div className="cs-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="cs-modal">
        <div className={`cs-modal-header ${color}`}>
          <div>
            <h2>{period} Shipment - Details</h2>
            <span>Product / Pack Size / Brand + Shift Wise</span>
          </div>
          <button className="cs-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="cs-modal-summary">
          <div className="cs-modal-summary-item">
            <div className="cs-modal-summary-label">Total Cartons</div>
            <div className="cs-modal-summary-value green">{formatNumber(total)}</div>
          </div>
          <div className="cs-modal-summary-item">
            <div className="cs-modal-summary-label">Total LBS</div>
            <div className="cs-modal-summary-value purple">{formatLbs(totalLbs)}</div>
          </div>
          <div className="cs-modal-summary-item">
            <div className="cs-modal-summary-label">Products</div>
            <div className="cs-modal-summary-value">{products.length}</div>
          </div>
          <div className="cs-modal-summary-item">
            <div className="cs-modal-summary-label">☀️ Day Shift</div>
            <div className="cs-modal-summary-value">{formatNumber(dayCartons)}</div>
          </div>
          <div className="cs-modal-summary-item">
            <div className="cs-modal-summary-label">🌙 Night Shift</div>
            <div className="cs-modal-summary-value">{nightCartons > 0 ? formatNumber(nightCartons) : '—'}</div>
          </div>
        </div>
        <div className="cs-modal-body">
          <div className="cs-modal-tabs">
            <button className={`cs-modal-tab ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>📊 All</button>
            <button className={`cs-modal-tab ${activeTab === 'day' ? 'active' : ''}`} onClick={() => setActiveTab('day')}>☀️ Day</button>
            <button className={`cs-modal-tab ${activeTab === 'night' ? 'active' : ''}`} onClick={() => setActiveTab('night')}>🌙 Night</button>
          </div>
          
          {activeTab === 'all' && (
            <>
              <ModalBreakupSectionReal title="📦 Product Wise" data={products} />
              <ModalBreakupSectionReal title="📏 Pack Size Wise" data={pack_sizes} />
              <ModalBreakupSectionReal title="🏷️ Brand Wise" data={brands} />
            </>
          )}
          {activeTab === 'day' && (
            <div style={{textAlign:'center', padding:'40px', color:'#666'}}>
              <p>☀️ Day Shift Total: <strong>{formatNumber(dayCartons)}</strong> cartons</p>
            </div>
          )}
          {activeTab === 'night' && (
            <div style={{textAlign:'center', padding:'40px', color:'#666'}}>
              <p>🌙 Night Shift Total: <strong>{nightCartons > 0 ? formatNumber(nightCartons) : '—'}</strong> cartons</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ModalBreakupSectionReal = ({ title, data }) => (
  <div className="cs-modal-section">
    <div className="cs-modal-section-title">{title}</div>
    <table className="cs-modal-table">
      <thead>
        <tr>
          <th>Name</th>
          <th style={{textAlign:'right'}}>Cartons</th>
          <th style={{textAlign:'right'}}>LBS</th>
        </tr>
      </thead>
      <tbody>
        {data && data.length > 0 ? data.map((item, i) => (
          <tr key={i}>
            <td>{item.name || 'Unknown'}</td>
            <td className="cartons">{formatNumber(item.cartons)}</td>
            <td className="lbs">{formatLbs(item.lbs)}</td>
          </tr>
        )) : <tr><td colSpan="3" style={{textAlign:'center',color:'#999'}}>No data available</td></tr>}
      </tbody>
    </table>
  </div>
);

export default ExecutiveDashboard;
